import { mountWorkspaceHelp, openWorkspaceHelp } from "../../../shared/help/workspace-help";
import { HELP_CONTENT } from "../data/helpContent";
import gatewayStyles from "../styles/gateway.css?raw";
import { GatewayLanding } from "../components/GatewayLanding";
import redesignStyles from "../styles/redesign.css?raw";
import { AcademyImage } from "../components/AcademyImage";
import { ApplicationStepPages } from "../components/ApplicationStepPages";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "../styles/app.css";
import { Icon } from "../components/Icon";
import {
  ACADEMY_LOGO_SRC,
  Button,
  Callout,
  CheckCard,
  Modal,
  PageHead,
  SelectField,
  StatusChip,
  TextAreaField,
  TextField,
  ToastStack,
  ToggleRow,
} from "../components/ui";
import {
  ACCOMMODATIONS,
  APPLICATION_ROUTE_CARDS,
  APPLICATION_VERSION,
  AVAILABILITY,
  COHORTS,
  COUNTRIES,
  CURRENCIES,
  DEMO_EMAIL_CODE,
  DEMO_INVITE_CODE,
  DEMO_MFA_CODE,
  EVERYDAY_TRACKS,
  LANGUAGES,
  ONBOARDING_DEFS,
  PROGRAMS,
  ROLES,
  ROLE_GROUPS,
  STEP_DEFINITIONS,
  STORAGE_KEY,
  TIMEZONES,
  initialApplication,
  initialState,
  safeLoadState,
} from "../data/mockData";
import {
  ApplicantProfile,
  ApplicationDraft,
  ApplicationRoute,
  ApplicationStepId,
  EntityProfile,
  FundingRoute,
  GuardianProfile,
  IconName,
  LearnerProfile,
  OnboardingDraft,
  OnboardingStepId,
  ProfessionalProfile,
  RoleCategory,
  RoleRecord,
  RootState,
  Screen,
  ToastMessage,
  ToastTone,
} from "../types/domain";
import {
  ageTrackEligibility,
  applicationStepsFor,
  calculateAge,
  classNames,
  cohortById,
  defaultRoleForRoute,
  downloadFile,
  formatDate,
  learnerAgeForDraft,
  makeReference,
  onboardingDestination,
  onboardingStepsFor,
  percentage,
  programById,
  programEligibleForRoute,
  requiresGuardian,
  roleById,
  routeCategory,
  statusLabel,
  statusTone,
  trackById,
  workspaceRecordsFor,
} from "../utils/auth";
import {
  AUTHORIZATION_CLIENTS,
  authorizationContextFor,
  readAuthorizationClientId,
  readAuthorizationRequest,
  type AuthorizationClientId,
  type AuthorizationClientContext,
  type AuthorizationRequest,
} from "../services/authorization";
import { appUrl } from "../config/environment";
import {
  accountJourneyScreen,
  accessEntryFromPath,
  updateAccessPath,
  stateForAccessEntry,
  visibleAccessScreen,
} from "../services/accessRoutes";
import { completeAuthorization } from "../services/identityProvider";

const APPLICATION_STATUS_LABELS: Record<ApplicationDraft["status"], string> = {
  draft: "in progress",
  submitted: "submitted",
  review: "under review",
  interview: "at interview",
  offered: "offer ready",
  accepted: "accepted",
  enrolled: "enrolled",
};

const APPLICATION_PHASES: Array<{
  id: string;
  label: string;
  description: string;
  icon: IconName;
  steps: ApplicationStepId[];
}> = [
  {
    id: "story",
    label: "Your story",
    description: "Route, identity and context",
    icon: "user",
    steps: ["route", "profile", "learner", "entity", "professional"],
  },
  {
    id: "pathway",
    label: "Your pathway",
    description: "Program and cohort",
    icon: "book",
    steps: ["program", "cohort"],
  },
  {
    id: "preparation",
    label: "Your plan",
    description: "Readiness, access and support",
    icon: "sparkles",
    steps: ["readiness", "availability", "funding", "guardian", "checks"],
  },
  {
    id: "confirmation",
    label: "Final check",
    description: "Consent, review and submit",
    icon: "shield",
    steps: ["consent", "review"],
  },
];

const PROGRAM_JOURNEY: Array<{ label: string; detail: string; icon: IconName }> = [
  { label: "Learn live", detail: "Guided Academy teaching", icon: "video" },
  { label: "Practice deeply", detail: "Eligible practical Labs", icon: "flask" },
  { label: "Build proof", detail: "A real reviewable project", icon: "laptop" },
  { label: "Earn evidence", detail: "Human-reviewed credential", icon: "graduation" },
];

function stateForAuthorizationContext(
  state: RootState,
  context: AuthorizationClientContext | null,
): RootState {
  if (!context) return state;
  const requiresAuthEntry =
    accessEntryFromPath() === "apply" &&
    context.roleCategory !== "learner" &&
    context.roleCategory !== "family";
  const hasOwnedJourney = Boolean(
    state.authEmail ||
    state.emailVerified ||
    state.application.status !== "draft" ||
    state.application.completedSteps.length ||
    state.onboarding.completedSteps.length ||
    state.onboarding.completedAt,
  );
  if (hasOwnedJourney)
    return { ...state, selectedWorkspace: "", ...(requiresAuthEntry ? { screen: "signin" as const } : {}) };
  return {
    ...state,
    ...(requiresAuthEntry ? { screen: "signin" as const } : {}),
    selectedWorkspace: "",
    application: {
      ...state.application,
      route: context.route,
      roleId: context.role,
    },
    onboarding: {
      ...state.onboarding,
      roleId: context.role,
    },
  };
}

function workspaceClientForRole(role: RoleRecord): AuthorizationClientId | null {
  if (role.category === "learner") return "learners";
  if (role.category === "family") return "parents";
  if (role.category === "teaching") return "instructors";
  if (role.category === "assessment") return "proctors";
  if (role.category === "organization") return "organizations";
  if (role.category === "institution") return "institutions";
  return null;
}

function hasSignedOutFlag() {
  if (typeof window === "undefined") return false;
  return new URL(window.location.href).searchParams.get("signed_out") === "1";
}

function consumeSignedOutFlag() {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get("signed_out") !== "1") return false;
  url.searchParams.delete("signed_out");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  return true;
}

function AccessHeader({
  context,
  onHome,
  onHelp,
  showSignIn,
  onSignIn,
  authenticated,
  onSignOut,
  minimal,
}: {
  context: AuthorizationClientContext | null;
  onHome: () => void;
  onHelp: () => void;
  showSignIn: boolean;
  onSignIn: () => void;
  authenticated: boolean;
  onSignOut: () => void;
  minimal?: boolean;
}) {
  return (
    <header className="access-header">
      <button type="button" className="access-brand" onClick={onHome} aria-label="Chatoner AI Academy access">
        <img
          src={ACADEMY_LOGO_SRC}
          alt="Chatoner AI Academy"
          width="960"
          height="225"
          decoding="sync"
          fetchPriority="high"
        />
      </button>
      <div className="access-header-purpose" aria-label="Current access destination">
        <span className="access-purpose-icon">
          <Icon name={context?.icon || "shield"} size={17} />
        </span>
        <span>
          <small>{context ? "Securely continuing to" : "Academy account gateway"}</small>
          <strong>{context?.label || "Access · Applications · Onboarding"}</strong>
        </span>
      </div>
      <div className="access-header-actions">
        {authenticated ? (
          <button type="button" className="access-header-link" onClick={onSignOut}>
            <Icon name="logout" size={14} />
            <span>Sign out</span>
          </button>
        ) : showSignIn && !minimal ? (
          <button type="button" className="access-header-link academy-header-signin" onClick={onSignIn}>
            Sign in <Icon name="arrow-right" size={16} />
          </button>
        ) : null}
        <button type="button" className="access-header-link" onClick={onHelp}>
          <Icon name="info" size={14} />
          <span>Help</span>
        </button>
      </div>
    </header>
  );
}

function AuthFlowVisual({
  image,
  icon,
  eyebrow,
  title,
  description,
}: {
  image: string;
  icon: IconName;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <section className="auth-flow-visual" aria-label={title}>
      <img src={image} alt="" width="960" height="720" />
      <span className="auth-flow-visual-wash" aria-hidden="true" />
      <div className="auth-flow-visual-copy">
        <span className="auth-flow-visual-kicker">
          <Icon name={icon} size={16} />
          {eyebrow}
        </span>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </section>
  );
}

function MicrosoftBrandMark() {
  return (
    <svg className="provider-brand-mark" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#f25022" d="M2 2h9v9H2z" />
      <path fill="#7fba00" d="M13 2h9v9h-9z" />
      <path fill="#00a4ef" d="M2 13h9v9H2z" />
      <path fill="#ffb900" d="M13 13h9v9h-9z" />
    </svg>
  );
}

function GoogleBrandMark() {
  return (
    <svg className="provider-brand-mark" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285f4"
        d="M21.6 12.23c0-.71-.06-1.4-.18-2.05H12v3.87h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.35Z"
      />
      <path
        fill="#34a853"
        d="M12 22c2.7 0 4.98-.9 6.63-2.42l-3.24-2.51c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.77-5.61-4.15H3.04v2.59A10 10 0 0 0 12 22Z"
      />
      <path
        fill="#fbbc05"
        d="M6.39 13.88A6.01 6.01 0 0 1 6.08 12c0-.65.11-1.29.31-1.88V7.53H3.04A10 10 0 0 0 2 12c0 1.61.39 3.14 1.04 4.47l3.35-2.59Z"
      />
      <path
        fill="#ea4335"
        d="M12 5.97c1.47 0 2.79.5 3.83 1.5l2.87-2.88A9.64 9.64 0 0 0 12 2a10 10 0 0 0-8.96 5.53l3.35 2.59C7.18 7.74 9.39 5.97 12 5.97Z"
      />
    </svg>
  );
}

export default function ChatonerAIAcademyCompleteAuthApplicationOnboarding() {
  const [initialEntry] = useState(() => accessEntryFromPath());
  const [requestingClientId] = useState(() => readAuthorizationClientId());
  const [authorizationRequest] = useState<AuthorizationRequest | null>(() => readAuthorizationRequest());
  const authorizationContext = useMemo(
    () => (requestingClientId ? authorizationContextFor(requestingClientId) : null),
    [requestingClientId],
  );
  const [state, setState] = useState<RootState>(() => {
    const persisted = stateForAccessEntry(safeLoadState());
    if (hasSignedOutFlag()) {
      return stateForAuthorizationContext(
        { ...persisted, authenticated: false, mfaVerified: false, selectedWorkspace: "", screen: "signin" },
        authorizationContext,
      );
    }
    return stateForAuthorizationContext(persisted, authorizationContext);
  });
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [modal, setModal] = useState<"resume" | "role-guide" | "application-export" | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [accountPanel, setAccountPanel] = useState<"purpose" | "details">(
    authorizationContext ? "details" : "purpose",
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteRole, setInviteRole] = useState(authorizationContext?.invitationRoleIds[0] || "team-learner");
  const [pendingEntry, setPendingEntry] = useState<"invite" | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [roleFilter, setRoleFilter] = useState<RoleCategory>(authorizationContext?.roleCategory || "learner");
  const [cohortTimezoneFilter, setCohortTimezoneFilter] = useState("All time zones");
  const [cohortLanguageFilter, setCohortLanguageFilter] = useState("All languages");
  const [mobileStepsOpen, setMobileStepsOpen] = useState(false);
  const [checkoutMethod, setCheckoutMethod] = useState("");
  const [checkoutPlan, setCheckoutPlan] = useState("");
  const [checkoutAgreement, setCheckoutAgreement] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const toastSequence = useRef(0);
  const notify = useCallback((title: string, detail: string, tone: ToastTone = "info") => {
    const id = ++toastSequence.current;
    setToasts(current => [...current, { id, title, detail, tone }].slice(-4));
    window.setTimeout(() => setToasts(current => current.filter(message => message.id !== id)), 4200);
  }, []);

  useEffect(() => {
    consumeSignedOutFlag();
  }, []);

  const applicationSteps = useMemo(() => applicationStepsFor(state.application), [state.application]);
  const activeApplicationStep =
    applicationSteps[Math.min(state.applicationStep, applicationSteps.length - 1)] || STEP_DEFINITIONS.route;
  const accessHelp = useMemo(
    () => ({
      ...HELP_CONTENT,
      title: authorizationContext ? `${authorizationContext.label} access help` : HELP_CONTENT.title,
      guides: HELP_CONTENT.guides
        .filter(
          guide =>
            guide.id !== "family" || !authorizationContext || authorizationContext.route === "parent-child",
        )
        .map(guide => ({
          ...guide,
          actions: guide.actions.filter(
            action =>
              action.area !== "application" ||
              !authorizationContext ||
              action.page === authorizationContext.route,
          ),
        })),
    }),
    [authorizationContext],
  );
  useEffect(
    () =>
      mountWorkspaceHelp({
        content: accessHelp,
        context: {
          area: state.screen,
          page: state.screen === "application" ? activeApplicationStep.id : state.screen,
          title:
            state.screen === "application" ? activeApplicationStep.label : state.screen.replace(/-/g, " "),
        },
        navigate: (area, page) => {
          if (area === "modal" && page === "role-guide") {
            setModal("role-guide");
            return;
          }
          if (area === "application") {
            const route = page as ApplicationRoute;
            if (
              ![
                "learner-self",
                "parent-child",
                "teaching",
                "proctor",
                "organization",
                "institution",
              ].includes(route)
            )
              return;
            if (authorizationContext && authorizationContext.route !== route) return;
            const roleId = authorizationContext?.role || defaultRoleForRoute(route);
            setRoleFilter(routeCategory(route));
            setErrors({});
            setMobileStepsOpen(false);
            setModal(null);
            setState(current => {
              const updated: RootState = {
                ...current,
                applicationStep: 0,
                application: {
                  ...current.application,
                  route,
                  roleId,
                  programId: "",
                  trackId: "",
                  cohortId: "",
                  guardian: { ...current.application.guardian, required: route === "parent-child" },
                  lastSavedAt: new Date().toISOString(),
                },
                onboarding: { ...current.onboarding, roleId },
              };
              return {
                ...updated,
                screen: !updated.authenticated
                  ? updated.emailVerified
                    ? "signin"
                    : "signup"
                  : accountJourneyScreen(updated),
              };
            });
            return;
          }
          // Only public entry actions are offered by help; protected steps retain their existing guards.
          if (!["signin", "invite", "forgot-password"].includes(page)) return;
          setModal(null);
          setErrors({});
          setState(current => ({ ...current, screen: page as Screen }));
        },
      }),
    [state.screen, activeApplicationStep.id, activeApplicationStep.label, accessHelp, authorizationContext],
  );
  const selectedRole = roleById(state.application.roleId || "learner");
  const selectedProgram = programById(state.application.programId);
  const selectedCohort = cohortById(state.application.cohortId);
  const selectedTrack = trackById(state.application.programId, state.application.trackId);
  const onboardingSteps = useMemo(
    () => onboardingStepsFor(state.onboarding.roleId || state.application.roleId || "learner"),
    [state.onboarding.roleId, state.application.roleId],
  );
  const activeOnboardingStep =
    onboardingSteps[Math.min(state.onboardingStep, onboardingSteps.length - 1)] || ONBOARDING_DEFS.welcome;
  const workspaceRecords = useMemo(
    () => workspaceRecordsFor(state.application.roleId || "learner", state.application),
    [state.application],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* local persistence can be unavailable */
    }
  }, [state]);

  useEffect(() => {
    const required = requiresGuardian(state.application);
    if (state.application.guardian.required !== required) {
      setState(current => ({
        ...current,
        application: {
          ...current.application,
          guardian: { ...current.application.guardian, required },
          lastSavedAt: new Date().toISOString(),
        },
      }));
    }
  }, [state.application]);

  useEffect(() => {
    if (state.applicationStep >= applicationSteps.length)
      setState(current => ({ ...current, applicationStep: Math.max(0, applicationSteps.length - 1) }));
  }, [applicationSteps.length, state.applicationStep]);

  useEffect(() => {
    if (state.onboardingStep >= onboardingSteps.length)
      setState(current => ({ ...current, onboardingStep: Math.max(0, onboardingSteps.length - 1) }));
  }, [onboardingSteps.length, state.onboardingStep]);

  useLayoutEffect(() => {
    const mainScroll = scrollRef.current;
    if (mainScroll) mainScroll.scrollTop = 0;
    if (typeof document !== "undefined" && document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [state.screen, state.applicationStep, state.onboardingStep]);

  useEffect(() => {
    setErrors({});
  }, [state.screen, state.applicationStep, state.onboardingStep]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setModal(null);
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        notify(
          "Draft saved",
          "Your current application and onboarding progress are stored on this device.",
          "success",
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [notify]);

  // Auto-continue existing completed accounts on direct landing (/apply or /auth)
  const autoContinueRan = useRef(false);
  useEffect(() => {
    if (autoContinueRan.current) return;
    autoContinueRan.current = true;
    if (!["apply", "auth", "signin"].includes(initialEntry)) return;
    if (!state.authenticated) return;
    const destination = accountJourneyScreen(state);
    if (destination !== "complete") return;
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    if (role.mfa === "required" && !state.mfaVerified) {
      setScreen("mfa");
      notify(
        "Additional verification required",
        `${role.label} access requires multi-factor authentication.`,
        "info",
      );
      return;
    }
    openAssignedWorkspace(role, { name: state.authName || "", email: state.authEmail || "" });
    // This guard intentionally runs the direct-entry continuation only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const root = document.documentElement;
    const viewport = window.visualViewport;
    const pendingTimers = new Set<number>();
    let largestViewportHeight = Math.max(window.innerHeight, viewport?.height || 0);

    const isEditableControl = (value: EventTarget | Element | null): value is HTMLElement =>
      value instanceof HTMLElement &&
      value.matches(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]',
      );

    const activeEditableControl = () =>
      isEditableControl(document.activeElement) ? document.activeElement : null;

    const updateViewport = () => {
      const visibleHeight = viewport?.height || window.innerHeight;
      const viewportOffset = viewport?.offsetTop || 0;
      const focusedControl = activeEditableControl();

      if (!focusedControl)
        largestViewportHeight = Math.max(largestViewportHeight, visibleHeight, window.innerHeight);

      const viewportGap = Math.max(0, window.innerHeight - visibleHeight - viewportOffset);
      const baselineGap = Math.max(0, largestViewportHeight - visibleHeight);
      const keyboardOpen = Boolean(
        focusedControl && Math.max(viewportGap, baselineGap) > 120 && (!viewport || viewport.scale <= 1.05),
      );

      root.style.setProperty("--auth-visual-height", `${Math.round(visibleHeight)}px`);
      root.style.setProperty("--auth-keyboard-inset", `${Math.round(Math.max(viewportGap, baselineGap))}px`);
      root.dataset.authKeyboardOpen = String(keyboardOpen);
    };

    const revealControl = (control: HTMLElement, block: ScrollLogicalPosition = "center") => {
      if (window.matchMedia("(min-width: 821px)").matches) return;
      control.scrollIntoView({
        block,
        inline: "nearest",
        behavior:
          root.dataset.authKeyboardOpen === "true" ||
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
      });
    };

    const scheduleReveal = (control: HTMLElement, delay: number, block: ScrollLogicalPosition = "center") => {
      const timer = window.setTimeout(() => {
        pendingTimers.delete(timer);
        updateViewport();
        revealControl(control, block);
      }, delay);
      pendingTimers.add(timer);
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!isEditableControl(event.target)) return;
      updateViewport();
      scheduleReveal(event.target, 80);
      scheduleReveal(event.target, 280, "nearest");
    };

    const onFocusOut = () => {
      const timer = window.setTimeout(() => {
        pendingTimers.delete(timer);
        updateViewport();
      }, 160);
      pendingTimers.add(timer);
    };

    const onViewportChange = () => {
      updateViewport();
      const control = activeEditableControl();
      if (control) {
        revealControl(control, "nearest");
        scheduleReveal(control, 40, "nearest");
      }
    };

    const onOrientationChange = () => {
      const timer = window.setTimeout(() => {
        pendingTimers.delete(timer);
        largestViewportHeight = Math.max(window.innerHeight, viewport?.height || 0);
        updateViewport();
      }, 260);
      pendingTimers.add(timer);
    };

    updateViewport();
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("orientationchange", onOrientationChange);
    viewport?.addEventListener("resize", onViewportChange);
    viewport?.addEventListener("scroll", onViewportChange);

    return () => {
      pendingTimers.forEach(timer => window.clearTimeout(timer));
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("orientationchange", onOrientationChange);
      viewport?.removeEventListener("resize", onViewportChange);
      viewport?.removeEventListener("scroll", onViewportChange);
      root.style.removeProperty("--auth-visual-height");
      root.style.removeProperty("--auth-keyboard-inset");
      delete root.dataset.authKeyboardOpen;
    };
  }, []);

  function dismissToast(id: number) {
    setToasts(current => current.filter(message => message.id !== id));
  }

  function setScreen(screen: Screen) {
    setState(current => ({ ...current, screen }));
  }

  const openAssignedWorkspace = useCallback(
    (role: RoleRecord, identity: { name: string; email: string }) => {
      if (authorizationRequest) {
        if (authorizationContext && role.category !== authorizationContext.roleCategory) {
          notify(
            "This account is not assigned to the requesting platform",
            `Sign in with an account that has a verified ${authorizationContext.shortLabel.toLowerCase()} role.`,
            "warning",
          );
          return;
        }
        completeAuthorization(authorizationRequest, identity);
        return;
      }

      const clientId = authorizationContext?.clientId || workspaceClientForRole(role);
      if (!clientId) {
        notify(
          "Workspace assignment required",
          "Academy Operations access must be opened from an authorized staff invitation.",
          "warning",
        );
        return;
      }

      completeAuthorization(
        {
          clientId,
          returnTo: appUrl(clientId),
          state: crypto.randomUUID(),
          role: AUTHORIZATION_CLIENTS[clientId].role,
        },
        identity,
      );
    },
    [authorizationContext, authorizationRequest, notify],
  );

  function continueAfterAuthentication(
    role: RoleRecord,
    identity: { name: string; email: string },
    mfaJustCompleted = false,
  ) {
    if (pendingEntry === "invite") {
      setPendingEntry(null);
      setScreen("invite");
      notify("Account confirmed", "Validate the invitation to add its assigned role and scope.", "success");
      return;
    }
    if (authorizationContext && role.category !== authorizationContext.roleCategory) {
      setState(current => ({ ...current, authenticated: false }));
      notify(
        "This account has a different assigned role",
        `Use an account with a verified ${authorizationContext.shortLabel.toLowerCase()} journey.`,
        "warning",
      );
      return;
    }
    const destination = accountJourneyScreen(state);
    if (destination === "complete") {
      if (role.mfa === "required" && !state.mfaVerified && !mfaJustCompleted) {
        setScreen("mfa");
        notify(
          "Additional verification required",
          `${role.label} access requires multi-factor authentication.`,
          "info",
        );
        return;
      }
      openAssignedWorkspace(role, identity);
      return;
    }

    setScreen(destination);
    if (destination === "application") {
      notify("Application ready", "Continue your saved Academy application.", "success");
      return;
    }
    if (destination === "status" || destination === "offer") {
      notify("Application status restored", "Your latest decision and next action are ready.", "success");
      return;
    }
    if (destination === "onboarding" || destination === "checkout") {
      notify("Onboarding restored", "Continue from your saved account progress.", "success");
      return;
    }

    if (role.mfa === "required") {
      setScreen("mfa");
      notify(
        "Additional verification required",
        `${role.label} access requires multi-factor authentication.`,
        "info",
      );
      return;
    }
    setScreen("workspaces");
    notify("Signed in", "Your available Academy workspaces are ready.", "success");
  }

  function beginAccountJourney() {
    if (!state.authenticated) {
      setState(current => ({
        ...current,
        screen: current.emailVerified ? "signin" : "signup",
      }));
      return;
    }
    setScreen(accountJourneyScreen(state));
  }

  function beginAccountCreation() {
    setAccountPanel(authorizationContext ? "details" : "purpose");
    setState(current => ({ ...current, screen: "signup" }));
  }

  function showAccountPanel(panel: "purpose" | "details") {
    setAccountPanel(panel);
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(
          panel === "details" ? ".account-details-heading" : ".account-route-panel .route-card.selected",
        )
        ?.focus();
    });
  }

  function updateApplication<K extends keyof ApplicationDraft>(key: K, value: ApplicationDraft[K]) {
    setState(current => ({
      ...current,
      application: { ...current.application, [key]: value, lastSavedAt: new Date().toISOString() },
    }));
  }

  function updateApplicant<K extends keyof ApplicantProfile>(key: K, value: ApplicantProfile[K]) {
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        applicant: { ...current.application.applicant, [key]: value },
        lastSavedAt: new Date().toISOString(),
      },
    }));
  }

  function updateLearner<K extends keyof LearnerProfile>(key: K, value: LearnerProfile[K]) {
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        learner: { ...current.application.learner, [key]: value },
        lastSavedAt: new Date().toISOString(),
      },
    }));
  }

  function updateGuardian<K extends keyof GuardianProfile>(key: K, value: GuardianProfile[K]) {
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        guardian: { ...current.application.guardian, [key]: value },
        lastSavedAt: new Date().toISOString(),
      },
    }));
  }

  function updateEntity<K extends keyof EntityProfile>(key: K, value: EntityProfile[K]) {
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        entity: { ...current.application.entity, [key]: value },
        lastSavedAt: new Date().toISOString(),
      },
    }));
  }

  function updateProfessional<K extends keyof ProfessionalProfile>(key: K, value: ProfessionalProfile[K]) {
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        professional: { ...current.application.professional, [key]: value },
        lastSavedAt: new Date().toISOString(),
      },
    }));
  }

  function updateOnboarding<K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) {
    setState(current => ({ ...current, onboarding: { ...current.onboarding, [key]: value } }));
  }

  function toggleArray<T extends string>(items: T[], item: T, setter: (next: T[]) => void) {
    setter(items.includes(item) ? items.filter(value => value !== item) : [...items, item]);
  }

  function chooseRole(roleId: string) {
    const role = roleById(roleId);
    if (
      role.access === "invitation" &&
      state.application.route !== "invited-member" &&
      state.application.route !== "academy-staff"
    ) {
      setInviteRole(role.id);
      setScreen("invite");
      notify(
        "Invitation required",
        `${role.label} access is assigned by an authorized organization, institution or Academy administrator.`,
        "warning",
      );
      return;
    }
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        roleId: role.id,
        route: current.application.route || role.route,
        lastSavedAt: new Date().toISOString(),
      },
      onboarding: { ...current.onboarding, roleId: role.id },
    }));
  }

  function chooseProgram(programId: string) {
    const program = programById(programId);
    if (!program) return;
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        programId,
        trackId: program.tracks ? "" : current.application.trackId,
        cohortId: "",
        lastSavedAt: new Date().toISOString(),
      },
    }));
  }

  function markApplicationStepComplete(stepId: string) {
    setState(current => ({
      ...current,
      application: {
        ...current.application,
        completedSteps: current.application.completedSteps.includes(stepId)
          ? current.application.completedSteps
          : [...current.application.completedSteps, stepId],
        lastSavedAt: new Date().toISOString(),
      },
    }));
  }

  function markOnboardingStepComplete(stepId: string) {
    setState(current => ({
      ...current,
      onboarding: {
        ...current.onboarding,
        completedSteps: current.onboarding.completedSteps.includes(stepId)
          ? current.onboarding.completedSteps
          : [...current.onboarding.completedSteps, stepId],
      },
    }));
  }

  function validateApplicationStep(step: ApplicationStepId): Record<string, string> {
    const draft = state.application;
    const nextErrors: Record<string, string> = {};
    if (step === "route") {
      if (!draft.route) nextErrors.route = "Choose the route that describes this application.";
      if (!draft.roleId) nextErrors.roleId = "Choose an account role.";
    }
    if (step === "profile") {
      if (!draft.applicant.firstName.trim()) nextErrors.firstName = "Enter the applicant’s first name.";
      if (!draft.applicant.lastName.trim()) nextErrors.lastName = "Enter the applicant’s last name.";
      if (!/^\S+@\S+\.\S+$/.test(draft.applicant.email)) nextErrors.email = "Enter a valid email address.";
      if (!draft.applicant.country) nextErrors.country = "Choose a country.";
      if (!draft.applicant.language) nextErrors.language = "Choose a primary language.";
      if (!draft.applicant.timezone) nextErrors.timezone = "Choose a time zone.";
      if (["learner-self", "teaching", "proctor"].includes(draft.route) && !draft.applicant.dateOfBirth)
        nextErrors.dateOfBirth = "Enter the applicant’s date of birth.";
    }
    if (step === "learner") {
      const dob = draft.route === "parent-child" ? draft.learner.dateOfBirth : draft.applicant.dateOfBirth;
      if (draft.route === "parent-child") {
        if (!draft.learner.firstName.trim()) nextErrors.learnerFirstName = "Enter the learner’s first name.";
        if (!draft.learner.lastName.trim()) nextErrors.learnerLastName = "Enter the learner’s last name.";
      }
      if (!dob) nextErrors.learnerDob = "Enter the learner’s date of birth.";
      const age = calculateAge(dob);
      if (age !== null && age < 5)
        nextErrors.learnerDob =
          "The current age-based Programs begin at 5 years. Admissions can discuss other family options.";
    }
    if (step === "entity") {
      if (!draft.entity.legalName.trim()) nextErrors.entityName = "Enter the legal entity name.";
      if (!draft.entity.type) nextErrors.entityType = "Choose the organization or institution type.";
      if (!draft.entity.country) nextErrors.entityCountry = "Choose the entity’s country.";
      if (!draft.entity.learnerCount)
        nextErrors.learnerCount = "Estimate the number of learners or participants.";
      if (!draft.entity.representativeTitle.trim())
        nextErrors.representativeTitle = "Enter the representative’s role or title.";
    }
    if (step === "professional") {
      if (!draft.professional.currentTitle.trim())
        nextErrors.currentTitle = "Enter your current title or professional role.";
      if (!draft.professional.qualifications.trim())
        nextErrors.qualifications = "Summarize relevant qualifications or equivalent experience.";
      if (!draft.professional.teachingOrProctoringExperience.trim())
        nextErrors.experience = "Describe relevant teaching, mentoring, assessment or proctoring experience.";
      if (!draft.professional.weeklyAvailability)
        nextErrors.professionalAvailability = "Enter your expected weekly availability.";
      if (!draft.professional.referenceOne.trim())
        nextErrors.referenceOne = "Provide at least one professional reference.";
      if (!draft.professional.languages.length)
        nextErrors.professionalLanguages = "Choose at least one working language.";
    }
    if (step === "program") {
      if (!draft.programId) nextErrors.programId = "Choose a Program.";
      const program = programById(draft.programId);
      if (program?.tracks && !draft.trackId) nextErrors.trackId = "Choose the learner’s age track.";
      const track = program?.tracks?.find(item => item.id === draft.trackId);
      if (track) {
        const eligibility = ageTrackEligibility(draft, track);
        if (!eligibility.eligible) nextErrors.trackId = eligibility.reason;
      }
    }
    if (step === "cohort") {
      if (!draft.cohortId) nextErrors.cohortId = "Choose a specific cohort or configured intake.";
      const cohort = cohortById(draft.cohortId);
      if (cohort?.status === "closed") nextErrors.cohortId = "This cohort is closed. Choose another intake.";
      const track = programById(draft.programId)?.tracks?.find(item => item.id === draft.trackId);
      if (track) {
        const eligibility = ageTrackEligibility(draft, track);
        if (!eligibility.eligible) nextErrors.cohortId = eligibility.reason;
      }
    }
    if (step === "readiness") {
      if (!draft.goal.trim()) nextErrors.goal = "Describe the capability or outcome you want.";
      if (!draft.currentAIUse) nextErrors.currentAIUse = "Choose the current AI-use level.";
      if (["learner-self", "parent-child"].includes(draft.route) && !draft.projectIdea.trim())
        nextErrors.projectIdea = "Describe one real task, problem or project to improve.";
      if (["organization", "institution"].includes(draft.route) && !draft.entity.goals.trim())
        nextErrors.entityGoals = "Describe what must improve for the organization or institution.";
    }
    if (step === "availability") {
      if (!draft.availability.length) nextErrors.availability = "Choose at least one available time.";
      if (!draft.weeklyHours) nextErrors.weeklyHours = "Choose the weekly time commitment available.";
    }
    if (step === "funding") {
      if (!draft.funding) nextErrors.funding = "Choose a funding or billing route.";
      if (
        ["employer", "organization", "institution", "invoice"].includes(draft.funding) &&
        !draft.sponsorName.trim()
      )
        nextErrors.sponsorName = "Enter the sponsor or billing entity.";
      if (
        ["employer", "organization", "institution", "invoice"].includes(draft.funding) &&
        !/^\S+@\S+\.\S+$/.test(draft.sponsorEmail)
      )
        nextErrors.sponsorEmail = "Enter a valid sponsor or finance email.";
      if (draft.funding === "scholarship" && draft.scholarshipReason.trim().length < 40)
        nextErrors.scholarshipReason = "Explain the scholarship request in at least 40 characters.";
    }
    if (step === "guardian") {
      if (draft.guardian.required) {
        if (!draft.guardian.firstName.trim())
          nextErrors.guardianFirstName = "Enter the guardian’s first name.";
        if (!draft.guardian.lastName.trim()) nextErrors.guardianLastName = "Enter the guardian’s last name.";
        if (!/^\S+@\S+\.\S+$/.test(draft.guardian.email))
          nextErrors.guardianEmail = "Enter a valid guardian email address.";
        if (!draft.guardian.relationship)
          nextErrors.guardianRelationship = "Choose the relationship to the learner.";
        if (
          !draft.guardian.consentIdentity ||
          !draft.guardian.consentProgram ||
          !draft.guardian.consentData ||
          !draft.guardian.consentSafeguarding
        )
          nextErrors.guardianConsent = "Complete all required guardian acknowledgements.";
        if (!draft.guardian.signature.trim())
          nextErrors.guardianSignature = "Enter the guardian’s digital signature.";
      }
    }
    if (step === "checks") {
      if (["teaching", "proctor"].includes(draft.route)) {
        if (!draft.professional.safeguardingTraining)
          nextErrors.safeguarding = "Acknowledge the safeguarding preparation requirement.";
        if (!draft.professional.conflictDeclaration)
          nextErrors.conflict = "Complete the conflict-of-interest declaration.";
        if (!draft.professional.authorityDeclaration)
          nextErrors.authority = "Acknowledge the role-authority boundary.";
      }
      if (["organization", "institution"].includes(draft.route) && !draft.professional.authorityDeclaration)
        nextErrors.authority =
          "Acknowledge that sponsorship does not create unrestricted academic or learner access.";
    }
    if (step === "consent") {
      ["privacy", "terms", "conduct", "aiUse", "dataAccuracy"].forEach(key => {
        if (!draft.consent[key]) nextErrors[key] = "Required";
      });
      if (!draft.signature.trim())
        nextErrors.signature = "Enter the applicant or authorized representative’s digital signature.";
    }
    if (step === "review") {
      const allRequired = ["privacy", "terms", "conduct", "aiUse", "dataAccuracy"].every(
        key => draft.consent[key],
      );
      if (!allRequired) nextErrors.reviewConsent = "Complete the required consent step before submitting.";
      if (draft.guardian.required && (!draft.guardian.signature || !draft.guardian.consentProgram))
        nextErrors.reviewGuardian = "Complete the required guardian section before submitting.";
      if (!draft.declaration) nextErrors.declaration = "Confirm the final declaration.";
    }
    return nextErrors;
  }

  function nextApplicationStep() {
    const validation = validateApplicationStep(activeApplicationStep.id);
    setErrors(validation);
    if (Object.keys(validation).length) {
      notify(
        "Complete the highlighted items",
        "Your draft remains saved. Resolve the required fields before continuing.",
        "warning",
      );
      return;
    }
    markApplicationStepComplete(activeApplicationStep.id);
    if (state.applicationStep < applicationSteps.length - 1) {
      setState(current => ({ ...current, applicationStep: current.applicationStep + 1 }));
      notify("Section saved", `${activeApplicationStep.label} is complete.`, "success");
    }
  }

  function previousApplicationStep() {
    setState(current => ({ ...current, applicationStep: Math.max(0, current.applicationStep - 1) }));
  }

  function submitApplication() {
    for (let index = 0; index < applicationSteps.length; index += 1) {
      const step = applicationSteps[index];
      const validation = validateApplicationStep(step.id);
      if (Object.keys(validation).length) {
        setErrors(validation);
        setState(current => ({ ...current, applicationStep: index }));
        notify(
          "Application is not ready",
          `Complete ${step.label.toLowerCase()} before submitting.`,
          "warning",
        );
        return;
      }
    }
    setErrors({});
    setState(current => ({
      ...current,
      screen: "status",
      application: {
        ...current.application,
        status: "submitted",
        completedSteps: Array.from(
          new Set([...current.application.completedSteps, ...applicationSteps.map(step => step.id)]),
        ),
        lastSavedAt: new Date().toISOString(),
      },
    }));
    notify(
      "Application submitted",
      `${state.application.reference} is now in the human-reviewed admissions queue.`,
      "success",
    );
  }

  function validateOnboardingStep(id: OnboardingStepId): Record<string, string> {
    const onboarding = state.onboarding;
    const role = roleById(onboarding.roleId || state.application.roleId);
    const nextErrors: Record<string, string> = {};
    if (
      id === "security" &&
      role.mfa === "required" &&
      (!onboarding.mfaConfirmed || !onboarding.recoveryCodesSaved)
    )
      nextErrors.security = "MFA and recovery-code confirmation are required for this role.";
    if (
      id === "profile" &&
      (!onboarding.profileConfirmed || !onboarding.languageConfirmed || !onboarding.accessibilityConfirmed)
    )
      nextErrors.profile = "Confirm profile, language and accessibility preferences.";
    if (id === "policies") {
      const requiredPolicies = policySetForRole(role).map(item => item.key);
      if (!requiredPolicies.every(key => onboarding.policyChecks[key]))
        nextErrors.policies = "Acknowledge every required role boundary.";
    }
    if (id === "readiness" && !onboarding.deviceChecked)
      nextErrors.readiness = "Complete the device and connection check.";
    return nextErrors;
  }

  function nextOnboardingStep() {
    const id = activeOnboardingStep.id;
    const nextErrors = validateOnboardingStep(id);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      notify("Onboarding step needs attention", Object.values(nextErrors)[0], "warning");
      return;
    }
    markOnboardingStepComplete(id);
    if (state.onboardingStep < onboardingSteps.length - 1)
      setState(current => ({ ...current, onboardingStep: current.onboardingStep + 1 }));
  }

  function previousOnboardingStep() {
    setState(current => ({ ...current, onboardingStep: Math.max(0, current.onboardingStep - 1) }));
  }

  function completeOnboarding() {
    for (let index = 0; index < onboardingSteps.length; index += 1) {
      const step = onboardingSteps[index];
      const validation = validateOnboardingStep(step.id);
      if (Object.keys(validation).length) {
        setErrors(validation);
        setState(current => ({ ...current, onboardingStep: index }));
        notify("Onboarding is not complete", Object.values(validation)[0], "warning");
        return;
      }
    }
    const destination = onboardingDestination(state.onboarding.roleId || state.application.roleId);
    setErrors({});
    setState(current => ({
      ...current,
      screen: "complete",
      application: { ...current.application, status: "enrolled" },
      onboarding: {
        ...current.onboarding,
        completedAt: new Date().toISOString(),
        completedSteps: Array.from(
          new Set([...current.onboarding.completedSteps, ...onboardingSteps.map(step => step.id)]),
        ),
      },
    }));
    notify("Workspace activated", `${destination.label} is ready.`, "success");
  }

  function resetDemo() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    const resetState: RootState = {
      ...initialState,
      application: {
        ...initialApplication,
        reference: makeReference("APP"),
        lastSavedAt: new Date().toISOString(),
      },
    };
    setState(stateForAuthorizationContext(stateForAccessEntry(resetState), authorizationContext));
    setPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setMfaCode("");
    setErrors({});
    setModal(null);
    notify("Demo reset", "A clean access, application and onboarding journey is ready.", "success");
  }

  function signOut() {
    setPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setMfaCode("");
    setPasswordVisible(false);
    setErrors({});
    setModal(null);
    setState(current => ({
      ...current,
      authenticated: false,
      mfaVerified: false,
      selectedWorkspace: "",
      screen: "signin",
    }));
    notify(
      "Signed out",
      "Your Academy session has ended. Sign in again to continue as this account.",
      "info",
    );
  }

  function exportApplication() {
    const safeApplication = {
      ...state.application,
      professional: {
        ...state.application.professional,
        identityDocument: state.application.professional.identityDocument ? "[selected file metadata]" : "",
      },
    };
    downloadFile(
      `Chatoner-Academy-Application-${state.application.reference}.json`,
      JSON.stringify(
        { version: APPLICATION_VERSION, exportedAt: new Date().toISOString(), application: safeApplication },
        null,
        2,
      ),
    );
    notify(
      "Application exported",
      "A portable JSON copy of this prototype draft has been downloaded.",
      "success",
    );
  }

  function renderWelcome() {
    const applicationHasEnteredData = Boolean(
      state.application.applicant.firstName.trim() ||
      state.application.applicant.lastName.trim() ||
      state.application.applicant.email.trim() ||
      state.application.programId ||
      state.application.cohortId ||
      state.application.entity.legalName.trim() ||
      state.application.professional.currentTitle.trim(),
    );
    const hasDraft =
      state.application.completedSteps.length > 0 ||
      state.applicationStep > 0 ||
      applicationHasEnteredData ||
      state.application.status !== "draft";
    return (
      <GatewayLanding
        onSignIn={() => setScreen("signin")}
        onSignup={() => setScreen("signup")}
        onApply={beginAccountJourney}
        onInvite={() => setScreen("invite")}
        savedProgress={
          hasDraft ? (
            <section className="welcome-saved" aria-label="Saved application progress">
              <span className="welcome-saved-icon">
                <Icon name="save" size={16} />
              </span>
              <div className="welcome-saved-copy">
                <div className="welcome-saved-meta">
                  <span>Saved application</span>
                  <code>{state.application.reference}</code>
                </div>
                <strong>
                  {state.application.status === "draft"
                    ? "Continue where you left off"
                    : `Application ${APPLICATION_STATUS_LABELS[state.application.status]}`}
                </strong>
                <p>
                  {state.application.status === "draft"
                    ? `${state.application.completedSteps.length} of ${applicationSteps.length} sections completed.`
                    : "Your latest application stage and next action are ready."}
                </p>
              </div>
              <div className="welcome-saved-actions">
                <Button tone="primary" onClick={beginAccountJourney}>
                  Continue
                  <Icon name="arrow-right" size={15} />
                </Button>
                <Button onClick={exportApplication}>
                  <Icon name="download" size={15} />
                  Export
                </Button>
              </div>
            </section>
          ) : null
        }
        accessNotes={
          <>
            <h2>One secure entry. The right workspace.</h2>
            <p>
              Sign in or choose a route. Your verified role determines the workspace and permissions that open
              next.
            </p>
            <div className="welcome-authority" aria-label="Academy access safeguards">
              <div className="welcome-assurance-item">
                <span>
                  <Icon name="shield" size={15} />
                </span>
                <div>
                  <strong>Verified identity</strong>
                  <small>One secure account across Academy roles.</small>
                </div>
              </div>
              <div className="welcome-assurance-item">
                <span>
                  <Icon name="key" size={15} />
                </span>
                <div>
                  <strong>Role-scoped access</strong>
                  <small>Only assigned workspaces and permissions open.</small>
                </div>
              </div>
              <div className="welcome-assurance-item">
                <span>
                  <Icon name="users" size={15} />
                </span>
                <div>
                  <strong>Human-reviewed decisions</strong>
                  <small>Admission, safeguarding and outcomes remain human-owned.</small>
                </div>
              </div>
            </div>
          </>
        }
      />
    );
  }

  function renderSignIn() {
    const canSignIn = /^\S+@\S+\.\S+$/.test(state.authEmail) && password.length >= 8;
    const submit = () => {
      const nextErrors: Record<string, string> = {};
      if (!/^\S+@\S+\.\S+$/.test(state.authEmail)) nextErrors.authEmail = "Enter a valid email address.";
      if (password.length < 8) nextErrors.password = "Enter a password with at least 8 characters.";
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length) return;
      const role = roleById(state.application.roleId || "learner");
      const applicantEmail = state.application.applicant.email.trim().toLowerCase();
      const applicantName =
        `${state.application.applicant.firstName} ${state.application.applicant.lastName}`.trim();
      const emailLocalName = state.authEmail
        .split("@")[0]
        .split(/[._-]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
      const name =
        state.authEmail.trim().toLowerCase() === applicantEmail
          ? state.authName || applicantName || emailLocalName || "Academy User"
          : emailLocalName || "Academy User";
      setState(current => ({
        ...current,
        authenticated: true,
        emailVerified: true,
        authName: name,
      }));
      continueAfterAuthentication(role, { name, email: state.authEmail });
    };
    return (
      <div className="auth-page signin-page auth-magic-page" data-design="academy-library-signin-v3">
        <div className="auth-magic-scene" aria-hidden="true">
          <img
            src="/images/academy/auth-magic-library-v3.webp"
            alt=""
            fetchPriority="high"
            decoding="async"
          />
          <div className="auth-magic-atmosphere" data-academy-effect="true" />
        </div>
        <section className="signin-intro" aria-label="Secure account access">
          <PageHead
            icon={authorizationContext?.icon || "lock"}
            eyebrow={
              authorizationContext ? `Secure return to ${authorizationContext.label}` : "Secure member access"
            }
            title={
              authorizationContext
                ? `Continue to ${authorizationContext.shortLabel.toLowerCase()}.`
                : "Welcome back."
            }
            description={
              authorizationContext
                ? `Sign in with the verified account assigned to this ${authorizationContext.shortLabel.toLowerCase()} role. We will return you to the requesting app automatically.`
                : "Use one verified account to reach the workspace, role and permissions already assigned to you."
            }
          />
          <div className="signin-benefits">
            <div className="signin-benefit">
              <span>
                <Icon name="user" size={16} />
              </span>
              <div>
                <strong>One account, the right workspace</strong>
                <p>Learner, family, teaching and operations access stay clearly separated.</p>
              </div>
            </div>
            <div className="signin-benefit">
              <span>
                <Icon name="key" size={16} />
              </span>
              <div>
                <strong>Permissions remain role-scoped</strong>
                <p>You only enter the tools and records assigned to your verified role.</p>
              </div>
            </div>
            <div className="signin-benefit">
              <span>
                <Icon name="shield" size={16} />
              </span>
              <div>
                <strong>Stronger checks when needed</strong>
                <p>MFA is requested automatically before privileged workspaces open.</p>
              </div>
            </div>
          </div>
        </section>
        <form
          className="auth-card signin-card auth-magic-form"
          aria-labelledby="auth-magic-form-title"
          noValidate
          onSubmit={event => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="auth-magic-form-heading">
            <span className="auth-magic-form-icon" aria-hidden="true">
              <Icon name="shield" size={25} />
            </span>
            <div>
              <span className="auth-magic-form-kicker">Your Academy awaits</span>
              <h2 id="auth-magic-form-title">Sign in to your account</h2>
            </div>
          </div>
          <div className="form-grid">
            <TextField
              className="full"
              label="Email address"
              value={state.authEmail}
              onChange={value => setState(current => ({ ...current, authEmail: value }))}
              placeholder="name@example.com"
              type="email"
              icon="mail"
              required
              error={errors.authEmail}
              autoComplete="email"
            />
            <TextField
              className="full"
              label="Password"
              value={password}
              onChange={setPassword}
              placeholder="Enter your password"
              type={passwordVisible ? "text" : "password"}
              icon="key"
              required
              error={errors.password}
              autoComplete="current-password"
              endAction={
                <button
                  type="button"
                  className="end-action"
                  onClick={() => setPasswordVisible(current => !current)}
                  aria-label={passwordVisible ? "Hide password" : "Show password"}
                >
                  <Icon name={passwordVisible ? "eye-off" : "eye"} size={16} />
                </button>
              }
            />
          </div>
          <div className="button-row between auth-magic-options">
            <label className="auth-magic-remember">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={event => setRememberDevice(event.target.checked)}
              />
              Remember this device
            </label>
            <Button tone="link" onClick={() => setScreen("forgot-password")}>
              Forgot password?
            </Button>
          </div>
          <Button tone="primary" className="auth-magic-submit" type="submit" disabled={!canSignIn}>
            Sign in securely
            <Icon name="arrow-right" size={18} />
          </Button>
          <div className="divider">or continue with</div>
          <div className="provider-grid">
            <button
              type="button"
              className="provider-btn microsoft-provider"
              onClick={() => {
                const role = roleById(state.application.roleId || "learner");
                const identity = { name: "Microsoft User", email: "microsoft.user@example.com" };
                setState(current => ({
                  ...current,
                  authenticated: true,
                  emailVerified: true,
                  authName: identity.name,
                  authEmail: identity.email,
                }));
                continueAfterAuthentication(role, identity);
                notify(
                  "Microsoft sign-in completed",
                  role.mfa === "required"
                    ? "Complete MFA for this privileged role."
                    : "Production uses the approved OIDC identity provider.",
                  "success",
                );
              }}
            >
              <MicrosoftBrandMark />
              <span>Continue with Microsoft</span>
            </button>
            <button
              type="button"
              className="provider-btn google-provider"
              onClick={() => {
                const role = roleById(state.application.roleId || "learner");
                const identity = { name: "Google User", email: "google.user@example.com" };
                setState(current => ({
                  ...current,
                  authenticated: true,
                  emailVerified: true,
                  authName: identity.name,
                  authEmail: identity.email,
                }));
                continueAfterAuthentication(role, identity);
                notify(
                  "Google sign-in completed",
                  role.mfa === "required"
                    ? "Complete MFA for this privileged role."
                    : "Production uses the approved OAuth identity provider.",
                  "success",
                );
              }}
            >
              <GoogleBrandMark />
              <span>Continue with Google</span>
            </button>
          </div>
          <div className="auth-entry-links">
            <span>
              New to the Academy?
              <Button tone="link" onClick={beginAccountCreation}>
                Create an account
              </Button>
            </span>
            <span>
              Joining an assigned role?
              <Button tone="link" onClick={() => setScreen("invite")}>
                Accept an invitation
              </Button>
            </span>
          </div>
        </form>
      </div>
    );
  }

  function renderSignup() {
    const route = state.application.route;
    const publicRoles = ROLES.filter(
      role =>
        role.route === route &&
        role.access !== "invitation" &&
        (!authorizationContext || authorizationContext.applicationRoleIds.includes(role.id)),
    );
    const availableRouteCards = APPLICATION_ROUTE_CARDS.filter(
      card =>
        ["learner-self", "parent-child", "teaching", "proctor", "organization", "institution"].includes(
          card.id,
        ) &&
        (!authorizationContext || card.id === authorizationContext.route),
    );
    const strength = Math.min(
      4,
      [
        password.length >= 8,
        /[A-Z]/.test(password),
        /[0-9]/.test(password),
        /[^A-Za-z0-9]/.test(password),
      ].filter(Boolean).length,
    );
    const canCreateAccount =
      Boolean(state.application.route) &&
      Boolean(state.application.applicant.firstName.trim()) &&
      Boolean(state.application.applicant.lastName.trim()) &&
      /^\S+@\S+\.\S+$/.test(state.application.applicant.email) &&
      password.length >= 8 &&
      password === confirmPassword;
    const submit = () => {
      const nextErrors: Record<string, string> = {};
      if (!state.application.route) nextErrors.route = "Choose what you are creating this account for.";
      if (!state.application.applicant.firstName.trim()) nextErrors.firstName = "Enter your first name.";
      if (!state.application.applicant.lastName.trim()) nextErrors.lastName = "Enter your last name.";
      if (!/^\S+@\S+\.\S+$/.test(state.application.applicant.email))
        nextErrors.email = "Enter a valid email address.";
      if (password.length < 8) nextErrors.password = "Use at least 8 characters.";
      if (password !== confirmPassword) nextErrors.confirmPassword = "Passwords do not match.";
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length) return;
      const name = `${state.application.applicant.firstName} ${state.application.applicant.lastName}`.trim();
      setState(current => ({
        ...current,
        authName: name,
        authEmail: current.application.applicant.email,
        screen: "verify-email",
        application: {
          ...current.application,
          roleId:
            current.application.roleId || defaultRoleForRoute(current.application.route as ApplicationRoute),
        },
        onboarding: {
          ...current.onboarding,
          roleId:
            current.application.roleId || defaultRoleForRoute(current.application.route as ApplicationRoute),
        },
      }));
      notify("Account created", "Verify the email address to continue.", "success");
    };
    return (
      <div
        className="auth-page account-purpose-page account-compact-page auth-flow-page auth-flow-signup"
        data-account-panel={accountPanel}
      >
        <section className="account-purpose-intro" aria-labelledby="account-purpose-title">
          <div className="account-purpose-kicker">
            <span className="page-head-icon">
              <Icon name={authorizationContext?.icon || "user"} size={28} />
            </span>
            <span className="eyebrow">
              {authorizationContext
                ? `Create access for ${authorizationContext.label}`
                : "Create an Academy account"}
            </span>
          </div>
          <h2 id="account-purpose-title">
            {authorizationContext ? (
              `Create your ${authorizationContext.shortLabel.toLowerCase()} account.`
            ) : (
              <>
                Create the account that owns this <span>journey.</span>
              </>
            )}
          </h2>
          <p>
            {authorizationContext
              ? `${authorizationContext.description} The requesting app has already selected the correct account route.`
              : "Account ownership is different from role authority. Privileged roles still require assignment, approval and MFA before their workspace opens."}
          </p>
          <div className="account-purpose-assurances">
            <div>
              <Icon name="users" size={22} />
              <span>
                One account.
                <br />A world of learning.
              </span>
            </div>
            <div>
              <Icon name="shield" size={22} />
              <span>Secure, approved access.</span>
            </div>
            <div>
              <Icon name="chart" size={22} />
              <span>
                Built for learners, educators
                <br />
                and organizations.
              </span>
            </div>
          </div>
          <span className="account-purpose-motto">Learn · Belong · Build · Further together</span>
        </section>
        {accountPanel === "purpose" ? (
          <div className="auth-card account-route-panel">
            <section className="section-block">
              <h3 className="section-title">
                <Icon name="arrow-right" size={15} />
                {authorizationContext ? "Account route" : "What are you creating this account for?"}
                {authorizationContext ? <span>Selected by {authorizationContext.label}</span> : null}
              </h3>
              <p className="account-route-intro">
                Choose the option that matches your journey. You can request additional roles later.
              </p>
              <div className="route-grid">
                {availableRouteCards.map(card => (
                  <button
                    type="button"
                    className={classNames("route-card", route === card.id && "selected")}
                    key={card.id}
                    aria-pressed={route === card.id}
                    onClick={() => {
                      const roleId = defaultRoleForRoute(card.id);
                      showAccountPanel("details");
                      setState(current => ({
                        ...current,
                        application: { ...current.application, route: card.id, roleId },
                        onboarding: { ...current.onboarding, roleId },
                      }));
                    }}
                  >
                    <AcademyImage subject={card.title} />
                    <span className="route-icon">
                      <Icon name={card.icon} size={19} />
                    </span>
                    <span>
                      <strong>{card.title}</strong>
                      <p>{card.description}</p>
                      <span className="route-card-cta">
                        {
                          {
                            "learner-self": "Apply for myself",
                            "parent-child": "Apply for a child",
                            teaching: "Apply as an educator",
                            proctor: "Apply as a proctor",
                            organization: "Create an organization",
                            institution: "Create an institution",
                            "invited-member": "Accept invitation",
                            "academy-staff": "Continue with staff access",
                          }[card.id]
                        }
                        <Icon name="arrow-right" size={15} />
                      </span>
                    </span>
                    {route === card.id ? (
                      <span className="selected-check">
                        <Icon name="check" size={13} />
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
              {errors.route ? (
                <span className="field-error" style={{ marginTop: 8 }}>
                  <Icon name="alert" size={12} />
                  {errors.route}
                </span>
              ) : null}
              <div className="auth-entry-links signup-entry-links">
                <span>
                  Already have an account?
                  <Button tone="link" onClick={() => setScreen("signin")}>
                    Sign in
                  </Button>
                </span>
              </div>
            </section>
          </div>
        ) : (
          <div className="auth-card account-details-panel">
            <div className="account-details-heading" tabIndex={-1}>
              <span className="eyebrow">Step 2 of 2 · Your account</span>
              <h3>{APPLICATION_ROUTE_CARDS.find(card => card.id === route)?.title || "Account details"}</h3>
              <p>Your selected journey. Complete your details to verify your email and continue.</p>
            </div>
            {publicRoles.length > 1 ? (
              <section className="section-block">
                <h3 className="section-title">
                  <Icon name="badge" size={15} />
                  Account role
                </h3>
                <div className="checkbox-grid">
                  {publicRoles.map(role => (
                    <CheckCard
                      key={role.id}
                      selected={state.application.roleId === role.id}
                      onClick={() => chooseRole(role.id)}
                      title={role.label}
                      detail={role.description}
                    />
                  ))}
                </div>
              </section>
            ) : null}
            <section className="section-block">
              <h3 className="section-title">
                <Icon name="id" size={15} />
                Account details
              </h3>
              <div className="form-grid">
                <TextField
                  label="First name"
                  value={state.application.applicant.firstName}
                  onChange={value => updateApplicant("firstName", value)}
                  required
                  error={errors.firstName}
                  autoComplete="given-name"
                />
                <TextField
                  label="Last name"
                  value={state.application.applicant.lastName}
                  onChange={value => updateApplicant("lastName", value)}
                  required
                  error={errors.lastName}
                  autoComplete="family-name"
                />
                <TextField
                  className="full"
                  label="Email address"
                  value={state.application.applicant.email}
                  onChange={value => updateApplicant("email", value)}
                  placeholder="name@example.com"
                  type="email"
                  icon="mail"
                  required
                  error={errors.email}
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  placeholder="Create a secure password"
                  type={passwordVisible ? "text" : "password"}
                  icon="key"
                  required
                  error={errors.password}
                  endAction={
                    <button
                      type="button"
                      className="end-action"
                      aria-label={passwordVisible ? "Hide passwords" : "Show passwords"}
                      onClick={() => setPasswordVisible(current => !current)}
                    >
                      <Icon name={passwordVisible ? "eye-off" : "eye"} size={16} />
                    </button>
                  }
                />
                <TextField
                  label="Confirm password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="Repeat the password"
                  type={passwordVisible ? "text" : "password"}
                  icon="key"
                  required
                  error={errors.confirmPassword}
                />
                <div className="form-field full">
                  <div
                    className="password-meter"
                    role="meter"
                    aria-label="Password strength"
                    aria-valuemin={0}
                    aria-valuemax={4}
                    aria-valuenow={strength}
                    aria-valuetext={`${strength} of 4`}
                  >
                    {[1, 2, 3, 4].map(value => (
                      <i className={value <= strength ? "on" : ""} key={value} />
                    ))}
                  </div>
                  <span className="field-hint">
                    Use 8+ characters with uppercase, number and symbol. Production must apply
                    breached-password and rate-limit checks.
                  </span>
                </div>
              </div>
            </section>
            <div className="button-row signup-submit-only">
              <Button tone="primary" disabled={!canCreateAccount} onClick={submit}>
                Create account
                <Icon name="arrow-right" size={15} />
              </Button>
            </div>
            <div className="auth-entry-links signup-entry-links">
              <span>
                Already have an account?
                <Button tone="link" onClick={() => setScreen("signin")}>
                  Sign in
                </Button>
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderVerifyEmail() {
    const submit = () => {
      if (verificationCode !== DEMO_EMAIL_CODE) {
        setErrors({ verificationCode: `Enter the six-digit prototype code ${DEMO_EMAIL_CODE}.` });
        return;
      }
      const destination = pendingEntry === "invite" ? "invite" : accountJourneyScreen(state);
      setState(current => ({
        ...current,
        emailVerified: true,
        authenticated: true,
        screen: destination,
      }));
      if (pendingEntry === "invite") setPendingEntry(null);
      notify(
        "Email verified",
        destination === "invite"
          ? "Your account is ready. Validate the invitation to add its assigned role."
          : destination === "application"
            ? "Your account is ready. Start or continue your application."
            : destination === "onboarding"
              ? "Your account is ready. Continue with role onboarding."
              : "Your saved account journey is ready.",
        "success",
      );
    };
    return (
      <div
        className="auth-page verify-email-page auth-flow-page auth-flow-verify"
        data-design="academy-email-verification-v2"
      >
        <section className="verify-email-intro" aria-labelledby="verify-email-title">
          <img
            className="verify-email-portrait"
            src="/images/academy/learner-studio.webp"
            alt=""
            width="960"
            height="540"
          />
          <div className="verify-email-intro-copy">
            <div className="eyebrow">Verify email</div>
            <h2 id="verify-email-title">
              Confirm that this email <span>belongs to you.</span>
            </h2>
            <p>
              A six-digit code was sent to{" "}
              <strong>{state.authEmail || state.application.applicant.email || "your email address"}</strong>.
            </p>
          </div>
          <div className="verify-email-emblem" aria-hidden="true">
            <span className="verify-email-orbit" />
            <span className="verify-email-envelope">
              <Icon name="mail" size={56} />
            </span>
          </div>
          <ul className="verify-email-benefits">
            <li>
              <Icon name="shield" size={21} />
              Keep your account secure
            </li>
            <li>
              <Icon name="users" size={21} />
              Protect your applications
            </li>
            <li>
              <Icon name="sparkles" size={21} />
              Join a trusted learning community
            </li>
          </ul>
          <div className="verify-email-motto">Learn · Build · Grow together</div>
        </section>
        <form
          className="auth-card verify-email-form"
          onSubmit={event => {
            event.preventDefault();
            submit();
          }}
        >
          <Callout icon="shield" title="Verification protects applications and invitations">
            The same verified email can later hold more than one workspace, but each role and relationship
            remains separately authorized.
          </Callout>
          <TextField
            label="Verification code"
            value={verificationCode}
            onChange={value => setVerificationCode(value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            icon="key"
            autoComplete="one-time-code"
            required
            error={errors.verificationCode}
          />
          <div className="button-row between">
            <Button
              onClick={() => {
                notify("New code sent", "A replacement prototype code is ready.", "success");
                setVerificationCode("");
              }}
            >
              <Icon name="refresh" size={18} />
              Resend code
            </Button>
            <Button tone="primary" type="submit" disabled={verificationCode.length !== 6}>
              Verify email
              <Icon name="arrow-right" size={18} />
            </Button>
          </div>
          <div className="demo-box">
            <Icon name="info" size={23} />
            <span>
              Prototype verification code: <code>{DEMO_EMAIL_CODE}</code>
            </span>
          </div>
        </form>
      </div>
    );
  }

  function renderMfa() {
    const role = roleById(state.application.roleId || "learner");
    const submit = () => {
      if (mfaCode !== DEMO_MFA_CODE) {
        setErrors({ mfaCode: `Enter the prototype code ${DEMO_MFA_CODE}.` });
        return;
      }
      setState(current => ({
        ...current,
        mfaVerified: true,
        authenticated: true,
        onboarding: { ...current.onboarding, mfaConfirmed: true },
      }));
      continueAfterAuthentication(
        role,
        {
          name: state.authName || "Academy User",
          email: state.authEmail || state.application.applicant.email,
        },
        true,
      );
      notify("Multi-factor verification complete", `${role.workspace} access can continue.`, "success");
    };
    return (
      <div className="auth-page auth-flow-page auth-flow-mfa">
        <AuthFlowVisual
          image="/images/academy/governance-safety.webp"
          icon="shield"
          eyebrow="Protect your workspace"
          title="Verify the person behind the account."
          description="Stronger identity checks keep sensitive Academy workspaces scoped to the right person and role."
        />
        <PageHead
          icon="shield"
          eyebrow="Multi-factor authentication"
          title={`Protect ${role.label} access.`}
          description={`${role.workspace} includes actions that require stronger identity assurance. Use an authenticator app, security key or approved organization identity provider in production.`}
        />
        <div className="auth-card">
          <div className="form-grid">
            <div className="form-field full">
              <SelectField
                label="Verification method"
                value="Authenticator app"
                onChange={() => undefined}
                options={["Authenticator app", "Security key / passkey", "Approved organization SSO"]}
                icon="key"
              />
            </div>
            <div className="form-field full">
              <TextField
                label="Six-digit code"
                value={mfaCode}
                onChange={value => setMfaCode(value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                icon="lock"
                required
                error={errors.mfaCode}
              />
            </div>
          </div>
          <div className="callout blue" style={{ marginTop: 13 }}>
            <span>
              <Icon name="key" size={16} />
            </span>
            <div>
              <strong>Recovery codes</strong>
              <p>
                Production shows one-time recovery codes only after verified enrollment. Store them offline;
                never place them in learner evidence or shared workspaces.
              </p>
            </div>
          </div>
          <div className="button-row between">
            <Button onClick={() => setScreen("signin")}>
              <Icon name="arrow-left" size={15} />
              Back
            </Button>
            <Button tone="primary" disabled={mfaCode.length !== 6} onClick={submit}>
              Verify and continue
              <Icon name="arrow-right" size={15} />
            </Button>
          </div>
          <div className="demo-box">
            Prototype MFA code: <code>{DEMO_MFA_CODE}</code>
          </div>
        </div>
      </div>
    );
  }

  function renderForgotPassword() {
    return (
      <div className="auth-page auth-flow-page auth-flow-forgot">
        <AuthFlowVisual
          image="/images/academy/data-knowledge-systems.webp"
          icon="key"
          eyebrow="Account recovery"
          title="Get back to your Academy journey."
          description="Request a secure reset link without revealing whether an account exists for an email address."
        />
        <PageHead
          icon="key"
          eyebrow="Account recovery"
          title="Reset your password without exposing accounts."
          description="The confirmation is intentionally identical whether or not an account exists for the email address."
        />
        <div className="auth-card">
          <TextField
            label="Email address"
            value={resetEmail}
            onChange={setResetEmail}
            placeholder="name@example.com"
            type="email"
            icon="mail"
            required
          />
          <div className="button-row between">
            <Button onClick={() => setScreen("signin")}>
              <Icon name="arrow-left" size={15} />
              Back to sign in
            </Button>
            <Button
              tone="primary"
              disabled={!/^\S+@\S+\.\S+$/.test(resetEmail)}
              onClick={() => {
                if (!/^\S+@\S+\.\S+$/.test(resetEmail)) {
                  notify("Enter a valid email", "Check the address and try again.", "warning");
                  return;
                }
                notify(
                  "Reset instructions requested",
                  "If an eligible account exists, instructions have been sent.",
                  "success",
                );
                setScreen("reset-password");
              }}
            >
              Send reset instructions
              <Icon name="arrow-right" size={15} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderResetPassword() {
    const submit = () => {
      if (newPassword.length < 8) {
        setErrors({ newPassword: "Use at least 8 characters." });
        return;
      }
      if (newPassword !== confirmPassword) {
        setErrors({ confirmPassword: "Passwords do not match." });
        return;
      }
      setPassword(newPassword);
      setScreen("signin");
      notify(
        "Password reset",
        "Other sessions would be revoked according to your account policy.",
        "success",
      );
    };
    return (
      <div className="auth-page auth-flow-page auth-flow-reset">
        <AuthFlowVisual
          image="/images/academy/prompt-context-engineering.webp"
          icon="lock"
          eyebrow="Secure account recovery"
          title="Choose a password you can trust."
          description="A strong password protects your identity while workspace permissions remain role-scoped."
        />
        <PageHead
          icon="lock"
          eyebrow="Choose a new password"
          title="Create a new secure password."
          description="Production validates a short-lived reset token, password history, breached-password status and session revocation."
        />
        <div className="auth-card">
          <div className="form-grid">
            <div className="form-field full">
              <TextField
                label="New password"
                value={newPassword}
                onChange={setNewPassword}
                type={passwordVisible ? "text" : "password"}
                icon="key"
                required
                error={errors.newPassword}
                endAction={
                  <button
                    type="button"
                    className="end-action"
                    onClick={() => setPasswordVisible(current => !current)}
                  >
                    <Icon name={passwordVisible ? "eye-off" : "eye"} size={16} />
                  </button>
                }
              />
            </div>
            <div className="form-field full">
              <TextField
                label="Confirm new password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                type={passwordVisible ? "text" : "password"}
                required
                error={errors.confirmPassword}
              />
            </div>
          </div>
          <div className="button-row between">
            <Button onClick={() => setScreen("signin")}>
              <Icon name="arrow-left" size={15} />
              Back to sign in
            </Button>
            <Button
              tone="primary"
              disabled={newPassword.length < 8 || newPassword !== confirmPassword}
              onClick={submit}
            >
              Reset password
              <Icon name="check" size={15} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderInvite() {
    const inviteRoles = ROLES.filter(role =>
      authorizationContext
        ? authorizationContext.invitationRoleIds.includes(role.id)
        : role.access === "invitation" || ["team-learner", "parent", "guardian"].includes(role.id),
    );
    const accept = () => {
      const nextErrors: Record<string, string> = {};
      if (inviteCode !== DEMO_INVITE_CODE)
        nextErrors.inviteCode = `Enter the prototype invitation ${DEMO_INVITE_CODE}.`;
      if (!/^\S+@\S+\.\S+$/.test(state.application.applicant.email))
        nextErrors.email = "Enter the email address named by the invitation.";
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length) return;
      const role = roleById(inviteRole);
      setState(current => ({
        ...current,
        screen: "onboarding",
        application: {
          ...current.application,
          route: "invited-member",
          roleId: role.id,
          status: "accepted",
          lastSavedAt: new Date().toISOString(),
        },
        onboarding: { ...current.onboarding, roleId: role.id },
      }));
      notify("Invitation validated", `${role.label} onboarding is ready under this account.`, "success");
    };
    if (!state.authenticated) {
      return (
        <div className="auth-page auth-flow-page auth-flow-invite">
          <AuthFlowVisual
            image="/images/academy/family-learning.webp"
            icon={authorizationContext?.icon || "mail"}
            eyebrow="Verified invitation"
            title="Join the right Academy space."
            description="An invitation adds a scoped role or relationship without changing the account’s existing permissions."
          />
          <PageHead
            icon={authorizationContext?.icon || "mail"}
            eyebrow="Account required"
            title="Sign in before accepting an invitation."
            description="An invitation adds a verified role, relationship or organization scope to an Academy account. It never replaces account creation or sign-in."
          />
          <div className="auth-card">
            <Callout icon="shield" title="Keep the invitation with the right account">
              Create an account if you are new, or sign in with the email address named by the invitation. You
              will return here to validate the invitation token.
            </Callout>
            <div className="button-row between" style={{ marginTop: 16 }}>
              <Button onClick={() => setScreen(authorizationContext ? "signin" : "welcome")}>
                <Icon name="arrow-left" size={15} />
                Back
              </Button>
              <div className="button-row">
                <Button
                  onClick={() => {
                    setPendingEntry("invite");
                    setScreen("signin");
                  }}
                >
                  Sign in
                </Button>
                <Button
                  tone="primary"
                  onClick={() => {
                    setPendingEntry("invite");
                    beginAccountCreation();
                  }}
                >
                  Create account
                  <Icon name="arrow-right" size={15} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="auth-page auth-flow-page auth-flow-invite">
        <AuthFlowVisual
          image="/images/academy/family-learning.webp"
          icon={authorizationContext?.icon || "mail"}
          eyebrow="Invitation acceptance"
          title="Join the right Academy space."
          description="A signed invitation determines the role, relationship and scope that can be added to this account."
        />
        <PageHead
          icon={authorizationContext?.icon || "mail"}
          eyebrow={
            authorizationContext ? `Invitation for ${authorizationContext.label}` : "Invitation acceptance"
          }
          title="Accept a verified Academy invitation."
          description={
            authorizationContext
              ? `Only ${authorizationContext.shortLabel.toLowerCase()} roles relevant to the requesting app are shown. The signed invitation determines the final role and scope.`
              : "The invitation determines the organization, institution, family relationship, cohort, role, expiry and permission scope. The browser cannot elevate it."
          }
        />
        <div className="auth-card">
          <div className="form-grid">
            <div className="form-field full">
              <TextField
                label="Invitation code or token"
                value={inviteCode}
                onChange={setInviteCode}
                placeholder="ACADEMY-INVITE-…"
                icon="key"
                required
                error={errors.inviteCode}
              />
            </div>
            <div className="form-field full">
              <SelectField
                label="Prototype assigned role"
                value={inviteRole}
                onChange={setInviteRole}
                options={inviteRoles.map(role => ({
                  value: role.id,
                  label: `${role.label} — ${role.workspace}`,
                }))}
                icon="badge"
                hint="In production this field is read-only and comes from the signed invitation."
              />
            </div>
            <div className="form-field full">
              <TextField
                label="Email address"
                value={state.application.applicant.email}
                onChange={value => updateApplicant("email", value)}
                placeholder="invited.name@example.com"
                type="email"
                icon="mail"
                required
                error={errors.email}
              />
            </div>
          </div>
          <div style={{ marginTop: 13 }}>
            <Callout icon="shield" title="What acceptance does not do">
              It does not grant unrestricted access, inherit another person’s private data, or bypass MFA,
              safeguarding, academic-unit, project, cohort or expiry controls.
            </Callout>
          </div>
          <div className="button-row between">
            <Button onClick={() => setScreen(authorizationContext ? "signin" : "welcome")}>
              <Icon name="arrow-left" size={15} />
              Back
            </Button>
            <Button
              tone="primary"
              disabled={
                !inviteCode.trim() || !inviteRole || !/^\S+@\S+\.\S+$/.test(state.application.applicant.email)
              }
              onClick={accept}
            >
              Validate invitation
              <Icon name="arrow-right" size={15} />
            </Button>
          </div>
          <div className="demo-box">
            Prototype invitation: <code>{DEMO_INVITE_CODE}</code>
          </div>
        </div>
      </div>
    );
  }

  function renderWorkspaces() {
    const records = workspaceRecords;
    return (
      <div className="page">
        <PageHead
          icon="building"
          eyebrow="Verified workspaces"
          title="Choose where you are working now."
          description="One account may hold several roles, but each workspace opens with its own membership, scope, policies and audit context."
          action={
            <StatusChip tone="green">
              <Icon name="shield" size={12} />
              Identity verified
            </StatusChip>
          }
        />
        <div className="workspace-grid">
          {records.map(record => (
            <button
              type="button"
              key={record.id}
              className={classNames("workspace-card", state.selectedWorkspace === record.id && "selected")}
              onClick={() => setState(current => ({ ...current, selectedWorkspace: record.id }))}
            >
              <div className="workspace-card-top">
                <span className="workspace-card-icon">
                  <Icon name={record.icon} size={20} />
                </span>
                <div>
                  <h3>{record.name}</h3>
                  <p>{record.role}</p>
                </div>
                {state.selectedWorkspace === record.id ? (
                  <span className="selected-check">
                    <Icon name="check" size={13} />
                  </span>
                ) : null}
              </div>
              <p style={{ margin: "11px 0 0", color: "var(--muted)", fontSize: 14 }}>{record.description}</p>
              <div className="workspace-meta">
                <StatusChip tone="green">Active membership</StatusChip>
                <StatusChip>{selectedRole.mfa === "required" ? "MFA required" : "MFA available"}</StatusChip>
              </div>
            </button>
          ))}
        </div>
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="panel-head">
            <Icon name="arrow-right" size={16} />
            <strong>Next action</strong>
          </div>
          <div className="panel-body">
            <div className="button-row between">
              <Button
                onClick={() => {
                  signOut();
                }}
              >
                <Icon name="lock" size={15} />
                Sign out
              </Button>
              <Button
                tone="primary"
                disabled={!state.selectedWorkspace}
                onClick={() => {
                  if (authorizationRequest) {
                    completeAuthorization(authorizationRequest, {
                      name: state.authName,
                      email: state.authEmail,
                    });
                    return;
                  }
                  const status = state.application.status;
                  if (status === "draft") setScreen("application");
                  else if (["submitted", "review", "interview"].includes(status)) setScreen("status");
                  else if (status === "offered") setScreen("offer");
                  else setScreen("onboarding");
                }}
              >
                Continue
                <Icon name="arrow-right" size={15} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderApplicationRouteStep() {
    const currentRoute = state.application.route;
    const currentCategory = routeCategory(currentRoute);
    const rolesForSelectedRoute = ROLES.filter(
      role =>
        role.route === currentRoute &&
        role.access !== "invitation" &&
        (!authorizationContext || authorizationContext.applicationRoleIds.includes(role.id)),
    );
    const visibleRoleGroups = authorizationContext
      ? ROLE_GROUPS.filter(group => group.id === authorizationContext.roleCategory)
      : ROLE_GROUPS.filter(group => group.id === "learner" || group.id === "family");
    const visibleRoleIds = authorizationContext
      ? [...authorizationContext.applicationRoleIds, ...authorizationContext.invitationRoleIds]
      : null;
    const catalogueRoles = ROLES.filter(
      role =>
        (role.category === "learner" || role.category === "family") &&
        role.category === roleFilter &&
        (!visibleRoleIds || visibleRoleIds.includes(role.id)),
    );
    return (
      <>
        <section className="section-block application-route-section">
          <h3 className="section-title">
            <Icon name="arrow-right" size={15} />
            Who is this application for?
            <span>Choose one route to continue</span>
          </h3>
          <p className="section-intro">
            Choose whether the learner is applying for themselves or a parent or guardian is applying for a
            child. Staff, proctors, organizations and institutions use account onboarding instead.
          </p>
          <div className="route-grid application-route-grid">
            {APPLICATION_ROUTE_CARDS.filter(
              card =>
                (card.id === "learner-self" || card.id === "parent-child" || card.id === "invited-member") &&
                (!authorizationContext ||
                  card.id === authorizationContext.route ||
                  card.id === "invited-member"),
            ).map(card => (
              <button
                type="button"
                aria-pressed={currentRoute === card.id}
                className={classNames(
                  "route-card",
                  "application-route-card",
                  card.id === "invited-member" && "invitation-route",
                  currentRoute === card.id && "selected",
                )}
                key={card.id}
                onClick={() => {
                  if (card.id === "invited-member") {
                    setScreen("invite");
                    return;
                  }
                  const roleId = defaultRoleForRoute(card.id);
                  setState(current => ({
                    ...current,
                    application: {
                      ...current.application,
                      route: card.id,
                      roleId,
                      programId: "",
                      trackId: "",
                      cohortId: "",
                      guardian: { ...current.application.guardian, required: card.id === "parent-child" },
                      lastSavedAt: new Date().toISOString(),
                    },
                    onboarding: { ...current.onboarding, roleId },
                  }));
                  setRoleFilter(routeCategory(card.id));
                }}
              >
                {["", "learner-self", "parent-child"].includes(state.application.route) && (
                  <AcademyImage subject={card.title} />
                )}
                <span className="route-icon">
                  <Icon name={card.icon} size={20} />
                </span>
                <span>
                  <strong>{card.title}</strong>
                  <p>{card.description}</p>
                  <span className="card-badge">
                    <Icon name="badge" size={11} />
                    {card.badge}
                  </span>
                </span>
                {currentRoute === card.id ? (
                  <span className="selected-check">
                    <Icon name="check" size={13} />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          {errors.route ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.route}
            </span>
          ) : null}
        </section>

        {currentRoute && currentRoute !== "invited-member" && rolesForSelectedRoute.length > 1 ? (
          <section className="section-block application-role-section">
            <h3 className="section-title">
              <Icon name="badge" size={15} />
              Confirm the primary role<span>Authority remains subject to verification and approval</span>
            </h3>
            <p className="section-intro">
              This choice prepares the relevant questions and workspace setup. It does not grant role access.
            </p>
            <div className="role-list">
              {rolesForSelectedRoute.map(role => (
                <button
                  type="button"
                  aria-pressed={state.application.roleId === role.id}
                  className={classNames("role-card", state.application.roleId === role.id && "selected")}
                  key={role.id}
                  onClick={() => chooseRole(role.id)}
                >
                  {useAcademyDesign && <AcademyImage subject={role.label} />}
                  <span className="role-card-icon">
                    <Icon
                      name={
                        role.category === "family"
                          ? "heart"
                          : role.category === "teaching"
                            ? "badge"
                            : role.category === "assessment"
                              ? "eye"
                              : role.category === "organization"
                                ? "building"
                                : role.category === "institution"
                                  ? "school"
                                  : "graduation"
                      }
                      size={16}
                    />
                  </span>
                  <span>
                    <strong>{role.label}</strong>
                    <p>{role.description}</p>
                  </span>
                  <span className={classNames("access-tag", role.access)}>{role.access}</span>
                </button>
              ))}
            </div>
            {errors.roleId ? (
              <span className="field-error" style={{ marginTop: 9 }}>
                <Icon name="alert" size={12} />
                {errors.roleId}
              </span>
            ) : null}
            <div style={{ marginTop: 11 }}>
              <Callout
                icon="shield"
                title={`${ROLE_GROUPS.find(group => group.id === currentCategory)?.label || "Role"} boundaries`}
              >
                The selected role controls onboarding, workspace navigation and allowed actions. It never
                grants access to another learner’s private records, hidden assessment decisions or
                unrestricted organization data.
              </Callout>
            </div>
          </section>
        ) : null}

        <details className="role-catalogue">
          <summary>
            <span className="role-catalogue-icon">
              <Icon name="users" size={17} />
            </span>
            <span className="role-catalogue-copy">
              <strong>
                {authorizationContext
                  ? `Explore ${authorizationContext.shortLabel.toLowerCase()} roles`
                  : "Explore the complete Academy role catalogue"}
              </strong>
              <small>
                {authorizationContext
                  ? "Only roles valid for the requesting app are included"
                  : "Optional reference for public, application and invitation-only roles"}
              </small>
            </span>
            <span className="role-catalogue-action">
              Browse roles
              <Icon name="chevron" size={14} />
            </span>
          </summary>
          <div className="role-catalogue-body">
            <div className="role-tabs" aria-label="Role categories">
              {visibleRoleGroups.map(group => (
                <button
                  type="button"
                  aria-pressed={roleFilter === group.id}
                  className={classNames("role-tab", roleFilter === group.id && "active")}
                  key={group.id}
                  onClick={() => setRoleFilter(group.id)}
                >
                  {group.label}
                </button>
              ))}
            </div>
            <p className="section-copy">{ROLE_GROUPS.find(group => group.id === roleFilter)?.description}</p>
            <div className="role-list">
              {catalogueRoles.map(role => (
                <button
                  type="button"
                  aria-pressed={state.application.roleId === role.id}
                  className={classNames("role-card", state.application.roleId === role.id && "selected")}
                  key={role.id}
                  onClick={() => {
                    if (role.access === "invitation") {
                      setInviteRole(role.id);
                      setScreen("invite");
                      notify(
                        "Invitation required",
                        `${role.label} is assigned by an authorized administrator with explicit scope.`,
                        "warning",
                      );
                      return;
                    }
                    setState(current => ({
                      ...current,
                      application: {
                        ...current.application,
                        route: role.route,
                        roleId: role.id,
                        programId: "",
                        trackId: "",
                        cohortId: "",
                        lastSavedAt: new Date().toISOString(),
                      },
                      onboarding: { ...current.onboarding, roleId: role.id },
                    }));
                  }}
                >
                  {useAcademyDesign && <AcademyImage subject={role.label} />}
                  <span className="role-card-icon">
                    <Icon
                      name={
                        role.category === "family"
                          ? "heart"
                          : role.category === "teaching"
                            ? "badge"
                            : role.category === "assessment"
                              ? "eye"
                              : role.category === "organization"
                                ? "building"
                                : role.category === "institution"
                                  ? "school"
                                  : role.category === "academy"
                                    ? "shield"
                                    : "graduation"
                      }
                      size={16}
                    />
                  </span>
                  <span>
                    <strong>{role.label}</strong>
                    <p>{role.description}</p>
                  </span>
                  <span className={classNames("access-tag", role.access)}>{role.access}</span>
                </button>
              ))}
            </div>
          </div>
        </details>
      </>
    );
  }

  function renderApplicantProfileStep() {
    const route = state.application.route;
    const routeLabel =
      route === "parent-child"
        ? "parent or guardian"
        : route === "organization" || route === "institution"
          ? "authorized representative"
          : route === "teaching" || route === "proctor"
            ? "professional applicant"
            : "learner";
    return (
      <div className="profile-step-workspace">
        <div className="profile-step-intro">
          <Callout icon="id" title={`Profile of the ${routeLabel}`}>
            This is the person who owns and submits the application. A child learner, sponsored participant or
            invited staff member can have a separate linked identity.
          </Callout>
        </div>
        <div className="profile-sections-grid">
          <section className="section-block profile-section profile-identity-section">
            <h3 className="section-title">
              <Icon name="user" size={15} />
              Identity and contact
              <span>01 · Personal details</span>
            </h3>
            <div className="form-grid">
              <TextField
                label="First name"
                value={state.application.applicant.firstName}
                onChange={value => updateApplicant("firstName", value)}
                required
                error={errors.firstName}
                autoComplete="given-name"
              />
              <TextField
                label="Last name"
                value={state.application.applicant.lastName}
                onChange={value => updateApplicant("lastName", value)}
                required
                error={errors.lastName}
                autoComplete="family-name"
              />
              <TextField
                label="Email address"
                value={state.application.applicant.email}
                onChange={value => updateApplicant("email", value)}
                placeholder="name@example.com"
                type="email"
                icon="mail"
                required
                error={errors.email}
                autoComplete="email"
              />
              <TextField
                label="Mobile / WhatsApp number"
                value={state.application.applicant.phone}
                onChange={value => updateApplicant("phone", value)}
                placeholder="+256 …"
                type="tel"
                icon="phone"
                autoComplete="tel"
              />
              {!["parent-child", "organization", "institution"].includes(route) ? (
                <TextField
                  label="Date of birth"
                  value={state.application.applicant.dateOfBirth}
                  onChange={value => updateApplicant("dateOfBirth", value)}
                  type="date"
                  icon="calendar"
                  required
                  error={errors.dateOfBirth}
                />
              ) : null}
              <SelectField
                label="Gender (optional)"
                value={state.application.applicant.gender}
                onChange={value => updateApplicant("gender", value)}
                options={["Prefer not to say", "Female", "Male", "Non-binary", "Self-describe"]}
              />
            </div>
          </section>
          <section className="section-block profile-section profile-location-section">
            <h3 className="section-title">
              <Icon name="location" size={15} />
              Location, language and time
              <span>02 · Communication fit</span>
            </h3>
            <div className="form-grid profile-location-grid">
              <SelectField
                label="Country"
                value={state.application.applicant.country}
                onChange={value => updateApplicant("country", value)}
                options={COUNTRIES}
                required
                error={errors.country}
                icon="globe"
              />
              <TextField
                label="City / district"
                value={state.application.applicant.city}
                onChange={value => updateApplicant("city", value)}
                placeholder="Kampala"
                icon="location"
              />
              <SelectField
                label="Time zone"
                value={state.application.applicant.timezone}
                onChange={value => updateApplicant("timezone", value)}
                options={TIMEZONES}
                required
                error={errors.timezone}
                icon="clock"
              />
              <SelectField
                label="Primary language"
                value={state.application.applicant.language}
                onChange={value => updateApplicant("language", value)}
                options={LANGUAGES}
                required
                error={errors.language}
                icon="language"
              />
              <SelectField
                label="Secondary language"
                value={state.application.applicant.secondaryLanguage}
                onChange={value => updateApplicant("secondaryLanguage", value)}
                options={["None", ...LANGUAGES]}
                icon="language"
              />
              <SelectField
                label="How did you hear about us?"
                value={state.application.applicant.referral}
                onChange={value => updateApplicant("referral", value)}
                options={[
                  "Chatoner website",
                  "Parent / family",
                  "Employer",
                  "School / institution",
                  "Instructor / mentor",
                  "Social media",
                  "Event / webinar",
                  "Search engine",
                  "Other",
                ]}
              />
            </div>
          </section>
          <section className="section-block profile-section profile-context-section">
            <h3 className="section-title">
              <Icon name="work" size={15} />
              Current context
              <span>03 · Your starting point</span>
            </h3>
            <div className="form-grid">
              <TextField
                label="Occupation / current role"
                value={state.application.applicant.occupation}
                onChange={value => updateApplicant("occupation", value)}
                placeholder={
                  route === "parent-child"
                    ? "Parent, guardian or caregiver"
                    : "Student, founder, teacher, manager…"
                }
                icon="work"
              />
              <TextField
                label="Organization / school (optional)"
                value={state.application.applicant.organization}
                onChange={value => updateApplicant("organization", value)}
                placeholder="Organization or institution"
                icon="building"
              />
            </div>
          </section>
        </div>
        <div className="profile-step-footnote">
          <Callout icon="globe" title="Multilingual by design" tone="blue">
            Language selection controls communication and onboarding preferences. Production can present a
            much larger searchable catalogue and right-to-left interfaces without changing the application
            model.
          </Callout>
        </div>
      </div>
    );
  }

  function renderLearnerStep() {
    const isParent = state.application.route === "parent-child";
    const dob = isParent ? state.application.learner.dateOfBirth : state.application.applicant.dateOfBirth;
    const ageNow = calculateAge(dob);
    const candidateTracks = EVERYDAY_TRACKS.map(track => ({
      track,
      ...ageTrackEligibility(state.application, track),
    }));
    return (
      <>
        <Callout
          icon="child"
          title={isParent ? "Create a separate learner profile" : "Confirm age-aware eligibility"}
        >
          {isParent
            ? "The adult account owns consent, communication and payment. The child receives only the age-appropriate participation access configured for the selected track."
            : "Age is evaluated again at the selected cohort start date. Turning 18 can change guardian requirements without erasing prior safeguarding records."}
        </Callout>
        {isParent ? (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="child" size={15} />
              Learner identity
            </h3>
            <div className="form-grid">
              <TextField
                label="Learner first name"
                value={state.application.learner.firstName}
                onChange={value => updateLearner("firstName", value)}
                required
                error={errors.learnerFirstName}
              />
              <TextField
                label="Learner last name"
                value={state.application.learner.lastName}
                onChange={value => updateLearner("lastName", value)}
                required
                error={errors.learnerLastName}
              />
              <TextField
                label="Preferred name"
                value={state.application.learner.preferredName}
                onChange={value => updateLearner("preferredName", value)}
                placeholder="Name used in class"
              />
              <TextField
                label="Date of birth"
                value={state.application.learner.dateOfBirth}
                onChange={value => updateLearner("dateOfBirth", value)}
                type="date"
                icon="calendar"
                required
                error={errors.learnerDob}
              />
              <TextField
                label="School / learning context"
                value={state.application.learner.schoolOrEmployer}
                onChange={value => updateLearner("schoolOrEmployer", value)}
                placeholder="School, home learning or other"
                icon="school"
              />
              <TextField
                label="Class / grade"
                value={state.application.learner.gradeOrRole}
                onChange={value => updateLearner("gradeOrRole", value)}
                placeholder="Primary 5, Grade 8…"
              />
              <TextField
                label="Learner email (optional)"
                value={state.application.learner.learnerEmail}
                onChange={value => updateLearner("learnerEmail", value)}
                placeholder="Used only if age and policy permit"
                type="email"
                icon="mail"
              />
              <div className="form-field">
                <label>Current calculated age</label>
                <div className="input-wrap">
                  <input readOnly value={ageNow === null ? "Add date of birth" : `${ageNow} years`} />
                </div>
                <span className="field-hint">Final eligibility is calculated at cohort start.</span>
              </div>
            </div>
          </section>
        ) : (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="calendar" size={15} />
              Learner age
            </h3>
            <div className="form-grid">
              <TextField
                label="Date of birth"
                value={state.application.applicant.dateOfBirth}
                onChange={value => updateApplicant("dateOfBirth", value)}
                type="date"
                icon="calendar"
                required
                error={errors.learnerDob || errors.dateOfBirth}
              />
              <div className="form-field">
                <label>Current calculated age</label>
                <div className="input-wrap">
                  <input readOnly value={ageNow === null ? "Add date of birth" : `${ageNow} years`} />
                </div>
                <span className="field-hint">
                  Cohort-start age controls the final track and guardian rule.
                </span>
              </div>
            </div>
          </section>
        )}

        <section className="section-block">
          <h3 className="section-title">
            <Icon name="sparkles" size={15} />
            Age-track preview<span>The exact track is selected on the Program step.</span>
          </h3>
          <div className="track-grid">
            {candidateTracks.map(({ track, eligible, reason }, index) => (
              <div className={classNames("track-card", !eligible && "ineligible")} key={track.id}>
                <AcademyImage subject={track.label} index={index} />
                <span className="ages">{track.ages}</span>
                <h4>{track.label}</h4>
                <p>{track.description}</p>
                <div className="track-access">{track.learnerAccess}</div>
                <div className={classNames("track-eligibility", !eligible && "no")}>
                  <Icon name={eligible ? "check" : "alert"} size={12} />
                  {reason}
                </div>
              </div>
            ))}
          </div>
        </section>

        {isParent ? (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="lock" size={15} />
              Learner account model
            </h3>
            <div className="checkbox-grid">
              <ToggleRow
                label="Create age-appropriate learner sign-in"
                detail="Enabled only after admission and guardian verification; parent-primary tracks remain family managed."
                value={state.application.learner.createLearnerLogin}
                onChange={value => updateLearner("createLearnerLogin", value)}
              />
              <ToggleRow
                label="Learner has given age-appropriate assent"
                detail="Assent supports participation but does not replace legally required adult consent."
                value={state.application.learner.assent}
                onChange={value => updateLearner("assent", value)}
              />
            </div>
            <div className="form-grid" style={{ marginTop: 11 }}>
              <div className="form-field full">
                <TextAreaField
                  label="Learning support notes (optional)"
                  value={state.application.learner.supportNotes}
                  onChange={value => updateLearner("supportNotes", value)}
                  placeholder="Share only information needed to support safe, accessible participation. Do not include unnecessary medical details."
                  rows={3}
                />
              </div>
            </div>
          </section>
        ) : null}
        {errors.learnerDob ? (
          <span className="field-error" style={{ marginTop: 10 }}>
            <Icon name="alert" size={12} />
            {errors.learnerDob}
          </span>
        ) : null}
        <Callout icon="heart" title="Age tracks have clear boundaries" tone="orange">
          Learners aged 12–17 use the youth experience. A learner who is 18 or older at cohort start uses the
          18+ adult experience, so the two tracks never overlap. Legal and sponsor policy still controls
          whether supporter involvement is required.
        </Callout>
      </>
    );
  }

  function renderEntityStep() {
    const institution = state.application.route === "institution";
    return (
      <>
        <Callout
          icon={institution ? "school" : "building"}
          title={institution ? "Institution applicant" : "Organization applicant"}
        >
          This establishes the entity-level relationship. Additional owners, registrars, finance, governance,
          IT, quality and program roles are invited only after verification.
        </Callout>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="building" size={15} />
            Legal and operational identity
          </h3>
          <div className="form-grid">
            <TextField
              label="Legal name"
              value={state.application.entity.legalName}
              onChange={value => updateEntity("legalName", value)}
              required
              error={errors.entityName}
              icon="building"
            />
            <TextField
              label="Trading / public name"
              value={state.application.entity.tradingName}
              onChange={value => updateEntity("tradingName", value)}
              placeholder="Optional"
            />
            <SelectField
              label={institution ? "Institution type" : "Organization type"}
              value={state.application.entity.type}
              onChange={value => updateEntity("type", value)}
              options={
                institution
                  ? [
                      "",
                      "Primary school",
                      "Secondary school",
                      "University",
                      "College / TVET",
                      "Training provider",
                      "Research institution",
                      "Government education body",
                      "Other",
                    ]
                  : [
                      "",
                      "Startup",
                      "SME",
                      "Corporation",
                      "Public institution",
                      "Non-profit / NGO",
                      "Professional association",
                      "Government body",
                      "Other",
                    ]
              }
              required
              error={errors.entityType}
            />
            <TextField
              label="Registration / institution number"
              value={state.application.entity.registrationNumber}
              onChange={value => updateEntity("registrationNumber", value)}
              placeholder="Where applicable"
              icon="id"
            />
            <TextField
              label="Website"
              value={state.application.entity.website}
              onChange={value => updateEntity("website", value)}
              placeholder="https://…"
              icon="globe"
            />
            <SelectField
              label="Country"
              value={state.application.entity.country}
              onChange={value => updateEntity("country", value)}
              options={COUNTRIES}
              required
              error={errors.entityCountry}
            />
            <TextField
              label="Headquarters / campus"
              value={state.application.entity.headquarters}
              onChange={value => updateEntity("headquarters", value)}
              placeholder="City and address"
              icon="location"
            />
            <TextField
              label="Your representative title"
              value={state.application.entity.representativeTitle}
              onChange={value => updateEntity("representativeTitle", value)}
              placeholder={institution ? "Principal, registrar, dean…" : "Director, HR lead, program owner…"}
              required
              error={errors.representativeTitle}
              icon="badge"
            />
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="users" size={15} />
            Scale and delivery
          </h3>
          <div className="form-grid">
            <SelectField
              label="Entity size"
              value={state.application.entity.teamSize}
              onChange={value => updateEntity("teamSize", value)}
              options={["", "1–20", "21–50", "51–200", "201–500", "501–2,000", "2,001+"]}
            />
            <SelectField
              label="Estimated learners / participants"
              value={state.application.entity.learnerCount}
              onChange={value => updateEntity("learnerCount", value)}
              options={["", "1–10", "11–25", "26–50", "51–100", "101–250", "251–500", "501+"]}
              required
              error={errors.learnerCount}
            />
            <TextField
              label="Finance / billing email"
              value={state.application.entity.financeEmail}
              onChange={value => updateEntity("financeEmail", value)}
              placeholder="finance@example.com"
              type="email"
              icon="mail"
            />
            <SelectField
              label="Preferred data region"
              value={state.application.entity.dataRegion}
              onChange={value => updateEntity("dataRegion", value)}
              options={[
                "To be agreed",
                "Africa",
                "European Union",
                "United Kingdom",
                "United States",
                "Country-specific requirement",
              ]}
            />
          </div>
          <div className="checkbox-grid" style={{ marginTop: 11 }}>
            <ToggleRow
              label="Formal procurement process"
              detail="Requires vendor onboarding, security or due-diligence steps."
              value={state.application.entity.procurementRequired}
              onChange={value => updateEntity("procurementRequired", value)}
            />
            <ToggleRow
              label="Purchase order required"
              detail="Invoice cannot be processed without an approved PO."
              value={state.application.entity.purchaseOrderRequired}
              onChange={value => updateEntity("purchaseOrderRequired", value)}
            />
            <ToggleRow
              label="Single sign-on required"
              detail="Configure approved SAML/OIDC identity after contracting."
              value={state.application.entity.ssoRequired}
              onChange={value => updateEntity("ssoRequired", value)}
            />
            <ToggleRow
              label="SCIM provisioning required"
              detail="Provision and deprovision members from an approved directory."
              value={state.application.entity.scimRequired}
              onChange={value => updateEntity("scimRequired", value)}
            />
          </div>
        </section>
        <Callout icon="shield" title="No shared unrestricted CRM">
          Production must integrate only with the entity-approved systems and data scopes. Sponsorship never
          grants unrestricted access to private learner messages, raw Lab activity, safeguarding records or
          individual assessment evidence.
        </Callout>
      </>
    );
  }

  function renderProfessionalStep() {
    const isProctor = state.application.route === "proctor";
    const roleOptions = ROLES.filter(
      role =>
        role.route === state.application.route &&
        role.access !== "invitation" &&
        (!authorizationContext || authorizationContext.applicationRoleIds.includes(role.id)),
    );
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="badge" size={15} />
            Role being requested
          </h3>
          <div
            className={classNames("role-list professional-role-list", roleOptions.length === 3 && "three-up")}
          >
            {roleOptions.map(role => (
              <button
                type="button"
                className={classNames("role-card", state.application.roleId === role.id && "selected")}
                key={role.id}
                onClick={() => chooseRole(role.id)}
              >
                <span className="role-card-icon">
                  <Icon name={isProctor ? "eye" : "badge"} size={16} />
                </span>
                <span>
                  <strong>{role.label}</strong>
                  <p>{role.description}</p>
                </span>
                <span className={classNames("access-tag", role.access)}>{role.access}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="work" size={15} />
            Experience and preparation
          </h3>
          <div className="form-grid">
            <TextField
              label="Current title / role"
              value={state.application.professional.currentTitle}
              onChange={value => updateProfessional("currentTitle", value)}
              placeholder={
                isProctor ? "Exam officer, invigilator, operations…" : "Instructor, lecturer, mentor…"
              }
              required
              error={errors.currentTitle}
              icon="work"
            />
            <TextField
              label="Employer / affiliation"
              value={state.application.professional.employer}
              onChange={value => updateProfessional("employer", value)}
              placeholder="Optional"
              icon="building"
            />
            <SelectField
              label="Years of relevant experience"
              value={state.application.professional.yearsExperience}
              onChange={value => updateProfessional("yearsExperience", value)}
              options={["", "Less than 1", "1–2", "3–5", "6–10", "11+"]}
            />
            <TextField
              label="Weekly availability"
              value={state.application.professional.weeklyAvailability}
              onChange={value => updateProfessional("weeklyAvailability", value)}
              placeholder="For example: 8 hours, evenings EAT"
              required
              error={errors.professionalAvailability}
              icon="clock"
            />
            <div className="form-field full">
              <TextAreaField
                label="Qualifications or equivalent experience"
                value={state.application.professional.qualifications}
                onChange={value => updateProfessional("qualifications", value)}
                placeholder="Education, credentials, relevant practice and subject expertise."
                required
                error={errors.qualifications}
              />
            </div>
            <div className="form-field full">
              <TextAreaField
                label={
                  isProctor
                    ? "Assessment operations / proctoring experience"
                    : "Teaching, mentoring and assessment experience"
                }
                value={state.application.professional.teachingOrProctoringExperience}
                onChange={value => updateProfessional("teachingOrProctoringExperience", value)}
                placeholder="Describe responsibilities, learner groups, delivery modes and how you handled difficult situations."
                required
                error={errors.experience}
              />
            </div>
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="language" size={15} />
            Languages
          </h3>
          <div className="checkbox-grid">
            {LANGUAGES.slice(0, 12).map(language => (
              <CheckCard
                key={language}
                selected={state.application.professional.languages.includes(language)}
                onClick={() =>
                  toggleArray(state.application.professional.languages, language, next =>
                    updateProfessional("languages", next),
                  )
                }
                title={language}
              />
            ))}
          </div>
          {errors.professionalLanguages ? (
            <span className="field-error" style={{ marginTop: 8 }}>
              <Icon name="alert" size={12} />
              {errors.professionalLanguages}
            </span>
          ) : null}
        </section>
        {!isProctor ? (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="book" size={15} />
              Programs you can support
            </h3>
            <div className="checkbox-grid">
              {PROGRAMS.map(program => (
                <CheckCard
                  key={program.id}
                  selected={state.application.professional.programs.includes(program.id)}
                  onClick={() =>
                    toggleArray(state.application.professional.programs, program.id, next =>
                      updateProfessional("programs", next),
                    )
                  }
                  title={program.name}
                  detail={program.level}
                />
              ))}
            </div>
          </section>
        ) : null}
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="users" size={15} />
            References and verification
          </h3>
          <div className="form-grid">
            <TextField
              label="Professional reference 1"
              value={state.application.professional.referenceOne}
              onChange={value => updateProfessional("referenceOne", value)}
              placeholder="Name, role, email and relationship"
              required
              error={errors.referenceOne}
            />
            <TextField
              label="Professional reference 2"
              value={state.application.professional.referenceTwo}
              onChange={value => updateProfessional("referenceTwo", value)}
              placeholder="Optional at initial application"
            />
            <div className="form-field">
              <label>Identity document metadata</label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={event =>
                  updateProfessional("identityDocument", event.target.files?.[0]?.name || "")
                }
              />
              <span className="field-hint">
                Prototype stores only the selected filename. Production uses encrypted upload, malware
                scanning and retention policy.
              </span>
            </div>
            <SelectField
              label="Background / suitability check"
              value={state.application.professional.backgroundCheck}
              onChange={value => updateProfessional("backgroundCheck", value)}
              options={[
                "Not yet provided",
                "Available on request",
                "Completed within 12 months",
                "Employer / institution verified",
                "Not applicable in my jurisdiction",
              ]}
            />
          </div>
        </section>
        <Callout
          icon="shield"
          title={isProctor ? "Proctor authority boundary" : "Educator authority boundary"}
          tone="orange"
        >
          {isProctor
            ? "Proctors operate assigned sessions, support readiness, record observable facts and escalate. They do not teach during the exam, grade responses, declare misconduct or treat automated signals as proof."
            : "Instructors and mentors guide learning; assessors grade only within assigned scope. AI can assist with preparation and evidence organization but cannot make the final academic or credential decision."}
        </Callout>
      </>
    );
  }

  function renderProgramStep() {
    const route = state.application.route;
    const programs = PROGRAMS.filter(program =>
      route === "teaching" ? true : programEligibleForRoute(program, route),
    );
    const program = selectedProgram;
    const learnerAge = learnerAgeForDraft(state.application);
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="book" size={15} />
            {route === "teaching" ? "Primary Program teaching pathway" : "Choose an Academy Program"}
            <span>{programs.length} current Program options</span>
          </h3>
          <p className="section-copy">
            Programs are selected by outcome, not only topic. Admissions confirms prerequisites, age fit,
            cohort capacity and the best route before enrolment.
          </p>
          <div className="program-journey" aria-label="Academy learning journey">
            {PROGRAM_JOURNEY.map((stage, index) => (
              <div className="program-journey-stage" key={stage.label}>
                <span className="program-journey-number">{String(index + 1).padStart(2, "0")}</span>
                <span className="program-journey-icon">
                  <Icon name={stage.icon} size={15} />
                </span>
                <span>
                  <strong>{stage.label}</strong>
                  <small>{stage.detail}</small>
                </span>
              </div>
            ))}
          </div>
          <div className="program-grid">
            {programs.map((item, index) => (
              <button
                type="button"
                className={classNames(
                  "program-card",
                  `program-card-accent-${(index % 4) + 1}`,
                  state.application.programId === item.id && "selected",
                )}
                key={item.id}
                aria-pressed={state.application.programId === item.id}
                onClick={() => chooseProgram(item.id)}
              >
                <div className="program-card-visual">
                  {useAcademyDesign && <AcademyImage index={index} />}
                  <div className="program-card-top">
                    <span className="card-icon">
                      <Icon name={item.icon} size={19} />
                    </span>
                    <span className="program-category">{item.category}</span>
                    <span className="level">{item.level}</span>
                  </div>
                  {!useAcademyDesign && (
                    <div className="program-card-heading">
                      <span>Academy Program</span>
                      <h4>{item.name}</h4>
                    </div>
                  )}
                </div>
                <div className="program-card-content">
                  {useAcademyDesign && (
                    <div className="program-card-heading">
                      <span>Academy Program</span>
                      <h4>{item.name}</h4>
                    </div>
                  )}
                  <p>{item.outcome}</p>
                  <div className="program-card-project">
                    <span className="program-card-project-icon">
                      <Icon name="sparkles" size={14} />
                    </span>
                    <span>
                      <small>What you will build</small>
                      <strong>{item.project}</strong>
                    </span>
                  </div>
                  <div className="program-meta">
                    <span className="meta-chip">
                      <Icon name="calendar" size={11} />
                      {item.duration}
                    </span>
                    <span className="meta-chip">
                      <Icon name="clock" size={11} />
                      {item.commitment}
                    </span>
                    <span className="meta-chip">
                      <Icon name="user" size={11} />
                      Age {item.minAge}+
                    </span>
                  </div>
                </div>
                <div className="program-footer">
                  <span className="program-credential-icon">
                    <Icon name="graduation" size={14} />
                  </span>
                  <span className="program-credential">
                    <small>Credential pathway</small>
                    <strong>{item.credential}</strong>
                  </span>
                  <span
                    className={classNames(
                      "program-choice",
                      state.application.programId === item.id && "selected",
                    )}
                  >
                    <Icon
                      name={state.application.programId === item.id ? "check" : "arrow-right"}
                      size={14}
                    />
                    {state.application.programId === item.id ? "Selected program" : "Choose program"}
                  </span>
                </div>
              </button>
            ))}
          </div>
          {errors.programId ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.programId}
            </span>
          ) : null}
        </section>

        {program?.tracks ? (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="child" size={15} />
              Choose the age-based sub-program
              <span>Age at cohort start: {learnerAge === null ? "not yet confirmed" : learnerAge}</span>
            </h3>
            <div className="track-grid">
              {program.tracks.map(track => {
                const eligibility = ageTrackEligibility(state.application, track);
                return (
                  <button
                    type="button"
                    className={classNames(
                      "track-card",
                      state.application.trackId === track.id && "selected",
                      !eligibility.eligible && "ineligible",
                    )}
                    key={track.id}
                    disabled={!eligibility.eligible}
                    onClick={() =>
                      setState(current => ({
                        ...current,
                        application: {
                          ...current.application,
                          trackId: track.id,
                          cohortId: "",
                          lastSavedAt: new Date().toISOString(),
                        },
                      }))
                    }
                  >
                    {useAcademyDesign && (
                      <AcademyImage
                        subject={
                          track.min >= 18
                            ? "adult learner"
                            : track.min >= 12
                              ? "collaborative project"
                              : "family learning"
                        }
                      />
                    )}
                    <span className="ages">{track.ages}</span>
                    <h4>{track.label}</h4>
                    <p>{track.description}</p>
                    <div className="track-access">{track.learnerAccess}</div>
                    <div className={classNames("track-eligibility", !eligibility.eligible && "no")}>
                      <Icon name={eligibility.eligible ? "check" : "alert"} size={12} />
                      {eligibility.reason}
                    </div>
                    {state.application.trackId === track.id ? (
                      <span className="selected-check">
                        <Icon name="check" size={13} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {errors.trackId ? (
              <span className="field-error" style={{ marginTop: 9 }}>
                <Icon name="alert" size={12} />
                {errors.trackId}
              </span>
            ) : null}
            <div style={{ marginTop: 10 }}>
              <Callout icon="heart" title="Family participation changes by track">
                Ages 5–7 and 8–11 are parent-primary. Ages 12–17 combine learner participation with guardian
                involvement where legally or institutionally required. Adults apply independently unless they
                invite a supporter.
              </Callout>
            </div>
          </section>
        ) : null}

        {program ? (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="star" size={15} />
              What this pathway produces
            </h3>
            <div className="review-grid">
              <div className="review-section">
                <div className="review-head">
                  <Icon name="chart" size={14} />
                  <strong>Outcome</strong>
                </div>
                <p className="section-copy" style={{ margin: 0 }}>
                  {program.outcome}
                </p>
              </div>
              <div className="review-section">
                <div className="review-head">
                  <Icon name="laptop" size={14} />
                  <strong>Project</strong>
                </div>
                <p className="section-copy" style={{ margin: 0 }}>
                  {program.project}
                </p>
              </div>
              <div className="review-section full">
                <div className="review-head">
                  <Icon name="sparkles" size={14} />
                  <strong>Skills</strong>
                </div>
                <div className="program-meta">
                  {program.skills.map(skill => (
                    <span className="meta-chip" key={skill}>
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : null}
        {route === "teaching" ? (
          <Callout icon="badge" title="Teaching assignment comes later" tone="blue">
            This selection records the primary Program interest. Final teaching, mentoring or assessing scope
            is assigned only after qualification checks, safeguarding preparation, orientation and an
            authorized Academy appointment.
          </Callout>
        ) : null}
      </>
    );
  }

  function renderCohortStep() {
    const available = COHORTS.filter(
      cohort =>
        cohort.programId === state.application.programId &&
        (!cohort.trackId || cohort.trackId === state.application.trackId),
    );
    const timezones = Array.from(new Set(available.map(cohort => cohort.timezone)));
    const languages = Array.from(new Set(available.map(cohort => cohort.language)));
    const filtered = available.filter(
      cohort =>
        (cohortTimezoneFilter === "All time zones" || cohort.timezone === cohortTimezoneFilter) &&
        (cohortLanguageFilter === "All languages" || cohort.language === cohortLanguageFilter),
    );
    return (
      <>
        <Callout icon="calendar" title="Choose one specific cohort">
          A Program can have several intakes. Cohort selection fixes the start date, schedule, time zone,
          language, delivery format, capacity and application deadline used for eligibility and onboarding.
        </Callout>
        {!selectedProgram ? (
          <div className="empty-state" style={{ marginTop: 13 }}>
            <span>
              <Icon name="book" size={23} />
            </span>
            <h3>Choose a Program first</h3>
            <p>Return to the Program step so the correct cohort catalogue can be loaded.</p>
            <Button
              tone="primary"
              onClick={() =>
                setState(current => ({
                  ...current,
                  applicationStep: Math.max(
                    0,
                    applicationSteps.findIndex(step => step.id === "program"),
                  ),
                }))
              }
            >
              Choose Program
            </Button>
          </div>
        ) : (
          <>
            <section className="section-block">
              <h3 className="section-title">
                <Icon name="search" size={15} />
                Cohort filters<span>{filtered.length} matching intakes</span>
              </h3>
              <div className="cohort-filters">
                <SelectField
                  label="Time zone"
                  value={cohortTimezoneFilter}
                  onChange={setCohortTimezoneFilter}
                  options={["All time zones", ...timezones]}
                  icon="clock"
                />
                <SelectField
                  label="Language"
                  value={cohortLanguageFilter}
                  onChange={setCohortLanguageFilter}
                  options={["All languages", ...languages]}
                  icon="language"
                />
              </div>
            </section>
            <div className="cohort-list">
              {filtered.map(cohort => {
                const full = cohort.enrolled >= cohort.capacity;
                const disabled = cohort.status === "closed" || full;
                const age = calculateAge(
                  state.application.route === "parent-child"
                    ? state.application.learner.dateOfBirth
                    : state.application.applicant.dateOfBirth,
                  cohort.startDate,
                );
                return (
                  <button
                    type="button"
                    className={classNames(
                      "cohort-card",
                      state.application.cohortId === cohort.id && "selected",
                      disabled && "disabled",
                    )}
                    key={cohort.id}
                    disabled={disabled}
                    onClick={() => updateApplication("cohortId", cohort.id)}
                  >
                    {useAcademyDesign && <AcademyImage subject={selectedProgram.name} />}
                    <div className="cohort-card-copy">
                      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                        <h4>{cohort.name}</h4>
                        <StatusChip tone={statusTone(cohort.status)}>{statusLabel(cohort.status)}</StatusChip>
                        {cohort.format === "private" ? (
                          <StatusChip tone="blue">Private cohort</StatusChip>
                        ) : null}
                      </div>
                      <p>{cohort.delivery}</p>
                      <div className="cohort-details">
                        <span className="cohort-detail">
                          <Icon name="calendar" size={11} />
                          {formatDate(cohort.startDate)} – {formatDate(cohort.endDate)}
                        </span>
                        <span className="cohort-detail">
                          <Icon name="clock" size={11} />
                          {cohort.schedule}
                        </span>
                        <span className="cohort-detail">
                          <Icon name="globe" size={11} />
                          {cohort.timezone}
                        </span>
                        <span className="cohort-detail">
                          <Icon name="language" size={11} />
                          {cohort.language}
                        </span>
                        <span className="cohort-detail">
                          <Icon name="clipboard" size={11} />
                          Apply by {formatDate(cohort.applicationDeadline)}
                        </span>
                        {age !== null ? (
                          <span className="cohort-detail">
                            <Icon name="child" size={11} />
                            Learner age at start: {age}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="capacity">
                      <strong>{Math.max(0, cohort.capacity - cohort.enrolled)} spaces shown</strong>
                      <div className="capacity-bar">
                        <i style={{ width: `${percentage(cohort.enrolled, cohort.capacity)}%` }} />
                      </div>
                      <span>
                        {cohort.enrolled}/{cohort.capacity} configured
                      </span>
                    </div>
                    {state.application.cohortId === cohort.id ? (
                      <span className="selected-check">
                        <Icon name="check" size={13} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {!filtered.length ? (
              <div className="empty-state">
                <span>
                  <Icon name="calendar" size={23} />
                </span>
                <h3>No cohorts match these filters</h3>
                <p>Reset the filters or choose another Program or age track.</p>
                <Button
                  onClick={() => {
                    setCohortTimezoneFilter("All time zones");
                    setCohortLanguageFilter("All languages");
                  }}
                >
                  Reset filters
                </Button>
              </div>
            ) : null}
            {errors.cohortId ? (
              <span className="field-error" style={{ marginTop: 9 }}>
                <Icon name="alert" size={12} />
                {errors.cohortId}
              </span>
            ) : null}
            <div style={{ marginTop: 11 }}>
              <Callout icon="info" title="Seeded cohort records for this build" tone="blue">
                These example October 2026 and 2027 intakes demonstrate the complete multi-cohort
                architecture. Production must replace them with server-managed cohorts and publish only dates
                confirmed by Academy operations.
              </Callout>
            </div>
          </>
        )}
      </>
    );
  }

  function renderReadinessStep() {
    const readinessItems: Array<{ key: string; label: string; detail: string }> = [
      {
        key: "literacy",
        label: "Everyday AI literacy",
        detail: "I understand what AI can and cannot reliably do.",
      },
      {
        key: "prompting",
        label: "Prompting and context",
        detail: "I can give clear instructions and useful background.",
      },
      {
        key: "verification",
        label: "Verification habits",
        detail: "I check sources, outputs and uncertainty before relying on AI.",
      },
      {
        key: "technical",
        label: "Technical confidence",
        detail: "I can work with digital tools, data or code appropriate to the Program.",
      },
      {
        key: "governance",
        label: "Privacy and responsible use",
        detail: "I understand that permissions, disclosure and human review matter.",
      },
    ];
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="star" size={15} />
            Goals and real-world outcome
          </h3>
          <div className="form-grid">
            <div className="form-field full">
              <TextAreaField
                label={
                  state.application.route === "parent-child"
                    ? "What should the learner be able to do by the end?"
                    : state.application.route === "organization" || state.application.route === "institution"
                      ? "What organizational capability should improve?"
                      : "What capability or outcome do you want?"
                }
                value={
                  state.application.route === "organization" || state.application.route === "institution"
                    ? state.application.entity.goals
                    : state.application.goal
                }
                onChange={value =>
                  state.application.route === "organization" || state.application.route === "institution"
                    ? updateEntity("goals", value)
                    : updateApplication("goal", value)
                }
                placeholder="Describe the current situation, desired outcome and why it matters."
                required
                error={errors.goal || errors.entityGoals}
              />
            </div>
            {["", "learner-self", "parent-child"].includes(state.application.route) ? (
              <div className="form-field full">
                <TextAreaField
                  label="One real task, problem or project to improve"
                  value={state.application.projectIdea}
                  onChange={value => updateApplication("projectIdea", value)}
                  placeholder="For example: organize school research safely, improve customer follow-up, create a useful app, or verify information before decisions."
                  required
                  error={errors.projectIdea}
                />
              </div>
            ) : null}
            <div className="form-field full">
              <SelectField
                label="Current use of AI"
                value={state.application.currentAIUse}
                onChange={value => updateApplication("currentAIUse", value)}
                options={[
                  "",
                  "I have not used AI tools",
                  "I have tried a few tools",
                  "I use AI occasionally",
                  "I use AI most weeks",
                  "I build or manage AI-enabled workflows",
                ]}
                required
                error={errors.currentAIUse}
                icon="sparkles"
              />
            </div>
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="chart" size={15} />
            Self-assessment<span>1 = new to this · 5 = confident</span>
          </h3>
          {readinessItems.map(item => (
            <div className="range-row" key={item.key}>
              <label>
                {item.label}
                <span>{item.detail}</span>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={state.application.readiness[item.key] || 1}
                  onChange={event =>
                    updateApplication("readiness", {
                      ...state.application.readiness,
                      [item.key]: Number(event.target.value),
                    })
                  }
                />
              </label>
              <output>{state.application.readiness[item.key] || 1}/5</output>
            </div>
          ))}
        </section>
        <Callout icon="shield" title="Readiness is not an automated admission score">
          Admissions uses this information to recommend preparation, support or a better cohort. The system
          must not infer protected characteristics, disability, intelligence or academic integrity from these
          answers.
        </Callout>
      </>
    );
  }

  function renderAvailabilityStep() {
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="clock" size={15} />
            Availability
          </h3>
          <div className="checkbox-grid">
            {AVAILABILITY.map(item => (
              <CheckCard
                key={item}
                selected={state.application.availability.includes(item)}
                onClick={() =>
                  toggleArray(state.application.availability, item, next =>
                    updateApplication("availability", next),
                  )
                }
                title={item}
              />
            ))}
          </div>
          {errors.availability ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.availability}
            </span>
          ) : null}
          <div className="form-grid" style={{ marginTop: 11 }}>
            <SelectField
              label="Weekly time available"
              value={state.application.weeklyHours}
              onChange={value => updateApplication("weeklyHours", value)}
              options={["", "1–2 hours", "2–4 hours", "4–6 hours", "6–8 hours", "8–12 hours", "12+ hours"]}
              required
              error={errors.weeklyHours}
              icon="clock"
            />
            <SelectField
              label="Preferred delivery"
              value={state.application.deliveryPreference}
              onChange={value => updateApplication("deliveryPreference", value)}
              options={[
                "",
                "Live online cohort",
                "Guided online + live sessions",
                "In-person / blended",
                "Private organization delivery",
                "Private institution delivery",
                "Flexible where available",
              ]}
              icon="video"
            />
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="accessibility" size={15} />
            Accessibility and participation
          </h3>
          <div className="checkbox-grid">
            {ACCOMMODATIONS.map(item => (
              <CheckCard
                key={item}
                selected={state.application.accommodations.includes(item)}
                onClick={() =>
                  toggleArray(state.application.accommodations, item, next =>
                    updateApplication("accommodations", next),
                  )
                }
                title={item}
              />
            ))}
          </div>
          <div className="form-grid" style={{ marginTop: 11 }}>
            <div className="form-field full">
              <TextAreaField
                label="Accommodation or access details (optional)"
                value={state.application.accessibilityNeeds}
                onChange={value => updateApplication("accessibilityNeeds", value)}
                placeholder="Describe what would help you participate. Share only information necessary to arrange support."
                rows={3}
              />
            </div>
          </div>
          <div className="checkbox-grid">
            <ToggleRow
              label="Low-bandwidth participation needed"
              detail="Prioritize lightweight materials, audio-only options, downloads and resilient reconnection."
              value={state.application.lowBandwidth}
              onChange={value => updateApplication("lowBandwidth", value)}
            />
          </div>
        </section>
        <Callout icon="accessibility" title="Support does not require a diagnosis" tone="blue">
          Applicants can request practical accommodations without disclosing unnecessary medical information.
          Authorized accessibility staff receive only the minimum information needed to coordinate support.
        </Callout>
      </>
    );
  }

  function renderFundingStep() {
    const route = state.application.route;
    const options: Array<{ id: FundingRoute; title: string; detail: string }> = [
      { id: "self", title: "Self-funded", detail: "The applicant pays after accepting an offer." },
      {
        id: "parent",
        title: "Parent / guardian funded",
        detail: "The verified adult accepts the payment responsibility.",
      },
      {
        id: "employer",
        title: "Employer sponsored",
        detail: "An employer confirms sponsorship and receives approved billing information.",
      },
      {
        id: "organization",
        title: "Organization funded",
        detail: "The organization owns the commercial agreement and participant seats.",
      },
      {
        id: "institution",
        title: "Institution funded",
        detail: "The institution funds learners or a configured cohort.",
      },
      {
        id: "scholarship",
        title: "Scholarship request",
        detail: "A human reviewer considers need, fit and available funding.",
      },
      {
        id: "invoice",
        title: "Invoice / purchase order",
        detail: "Use a finance contact, contract and PO workflow where required.",
      },
    ];
    const allowed = options.filter(option =>
      route === "parent-child"
        ? ["parent", "scholarship", "invoice"].includes(option.id)
        : route === "organization"
          ? ["organization", "invoice"].includes(option.id)
          : route === "institution"
            ? ["institution", "invoice"].includes(option.id)
            : true,
    );
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="wallet" size={15} />
            Funding route
          </h3>
          <div className="checkbox-grid">
            {allowed.map(option => (
              <CheckCard
                key={option.id}
                selected={state.application.funding === option.id}
                onClick={() => updateApplication("funding", option.id)}
                title={option.title}
                detail={option.detail}
              />
            ))}
          </div>
          {errors.funding ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.funding}
            </span>
          ) : null}
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="credit-card" size={15} />
            Billing details
          </h3>
          <div className="form-grid">
            <SelectField
              label="Billing currency"
              value={state.application.currency}
              onChange={value => updateApplication("currency", value)}
              options={CURRENCIES}
              icon="wallet"
            />
            {state.application.funding &&
            ["employer", "organization", "institution", "invoice"].includes(state.application.funding) ? (
              <>
                <TextField
                  label="Sponsor / billing entity"
                  value={state.application.sponsorName}
                  onChange={value => updateApplication("sponsorName", value)}
                  required
                  error={errors.sponsorName}
                  icon="building"
                />
                <TextField
                  label="Sponsor / finance email"
                  value={state.application.sponsorEmail}
                  onChange={value => updateApplication("sponsorEmail", value)}
                  placeholder="finance@example.com"
                  type="email"
                  icon="mail"
                  required
                  error={errors.sponsorEmail}
                />
              </>
            ) : null}
            {state.application.funding === "scholarship" ? (
              <div className="form-field full">
                <TextAreaField
                  label="Scholarship request"
                  value={state.application.scholarshipReason}
                  onChange={value => updateApplication("scholarshipReason", value)}
                  placeholder="Explain the need, intended outcome and what support would make participation possible."
                  required
                  error={errors.scholarshipReason}
                  rows={5}
                />
              </div>
            ) : null}
          </div>
        </section>
        <Callout icon="wallet" title="No payment is taken during application">
          Fees, discounts, sponsorship, tax, payment schedule and refund terms must be shown in the offer and
          checkout before any charge. Scholarship requests are reviewed by people, not automatically accepted
          or rejected.
        </Callout>
      </>
    );
  }

  function renderGuardianStep() {
    const required = state.application.guardian.required;
    const learnerName =
      state.application.route === "parent-child"
        ? `${state.application.learner.firstName} ${state.application.learner.lastName}`.trim()
        : `${state.application.applicant.firstName} ${state.application.applicant.lastName}`.trim();
    return (
      <>
        <Callout
          icon="heart"
          title={required ? "Verified adult involvement is required" : "Guardian involvement is optional"}
          tone={required ? "orange" : "blue"}
        >
          {required
            ? `The selected age track, cohort-start age or policy requires a verified parent or guardian for ${learnerName || "this learner"}. Adult consent, learner assent and family access remain separate records.`
            : "An adult learner can invite a trusted supporter, but the relationship must be explicit, revocable and must not expose private learning, messages or evidence by default."}
        </Callout>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="user" size={15} />
            Parent / guardian identity
          </h3>
          <div className="form-grid">
            <TextField
              label="First name"
              value={state.application.guardian.firstName}
              onChange={value => updateGuardian("firstName", value)}
              required={required}
              error={errors.guardianFirstName}
            />
            <TextField
              label="Last name"
              value={state.application.guardian.lastName}
              onChange={value => updateGuardian("lastName", value)}
              required={required}
              error={errors.guardianLastName}
            />
            <TextField
              label="Email address"
              value={state.application.guardian.email}
              onChange={value => updateGuardian("email", value)}
              type="email"
              placeholder="guardian@example.com"
              icon="mail"
              required={required}
              error={errors.guardianEmail}
            />
            <TextField
              label="Mobile / WhatsApp number"
              value={state.application.guardian.phone}
              onChange={value => updateGuardian("phone", value)}
              type="tel"
              placeholder="+256 …"
              icon="phone"
            />
            <SelectField
              label="Relationship to learner"
              value={state.application.guardian.relationship}
              onChange={value => updateGuardian("relationship", value)}
              options={[
                "Parent",
                "Legal guardian",
                "Foster parent / carer",
                "Authorized relative",
                "Institution-appointed guardian",
                "Other authorized adult",
              ]}
              required={required}
              error={errors.guardianRelationship}
            />
            <SelectField
              label="Legal authority basis"
              value={state.application.guardian.legalAuthority}
              onChange={value => updateGuardian("legalAuthority", value)}
              options={[
                "Parent / legal guardian",
                "Court or statutory appointment",
                "School / institution authorization",
                "Written parental authorization",
                "Other documented authority",
              ]}
            />
            <SelectField
              label="Communication role"
              value={state.application.guardian.communicationRole}
              onChange={value => updateGuardian("communicationRole", value)}
              options={[
                "Primary family contact",
                "Secondary family contact",
                "Consent only",
                "Billing only",
                "Emergency contact",
              ]}
            />
            <div className="form-field">
              <label>Relationship verification</label>
              <div className="input-wrap">
                <input
                  readOnly
                  value={
                    state.application.guardian.invitationSent
                      ? "Invitation sent — verification pending"
                      : "Not yet invited"
                  }
                />
              </div>
              <Button
                size="small"
                onClick={() => {
                  if (!/^\S+@\S+\.\S+$/.test(state.application.guardian.email)) {
                    notify("Add a valid guardian email", "The invitation cannot be sent yet.", "warning");
                    return;
                  }
                  updateGuardian("invitationSent", true);
                  notify(
                    "Guardian invitation sent",
                    "Production sends a signed, expiring relationship-verification link.",
                    "success",
                  );
                }}
              >
                <Icon name="mail" size={13} />
                Send verification
              </Button>
            </div>
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="clipboard" size={15} />
            Required acknowledgements
          </h3>
          <div className="checkbox-grid">
            <ToggleRow
              label="Identity and relationship are accurate"
              detail="I am authorized to act for this learner and can provide evidence if requested."
              value={state.application.guardian.consentIdentity}
              required
              onChange={value => updateGuardian("consentIdentity", value)}
            />
            <ToggleRow
              label="Program and cohort participation"
              detail="I understand the selected track, schedule, expected adult involvement and learner responsibilities."
              value={state.application.guardian.consentProgram}
              required
              onChange={value => updateGuardian("consentProgram", value)}
            />
            <ToggleRow
              label="Minimum-necessary learner data"
              detail="I understand how account, learning, support and safety information is handled."
              value={state.application.guardian.consentData}
              required
              onChange={value => updateGuardian("consentData", value)}
            />
            <ToggleRow
              label="Safeguarding and communication"
              detail="I understand the reporting, emergency and protected-contact routes."
              value={state.application.guardian.consentSafeguarding}
              required
              onChange={value => updateGuardian("consentSafeguarding", value)}
            />
            <ToggleRow
              label="Class recording preference"
              detail="Where recording is optional, this stores the family preference; mandatory session rules remain visible."
              value={state.application.guardian.consentRecording}
              onChange={value => updateGuardian("consentRecording", value)}
            />
            <ToggleRow
              label="Payment responsibility"
              detail="I am responsible for payment unless a verified sponsor accepts it."
              value={state.application.guardian.paymentResponsibility}
              onChange={value => updateGuardian("paymentResponsibility", value)}
            />
          </div>
          {errors.guardianConsent ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.guardianConsent}
            </span>
          ) : null}
          <div className="form-grid" style={{ marginTop: 11 }}>
            <div className="form-field full">
              <TextField
                label="Guardian digital signature"
                value={state.application.guardian.signature}
                onChange={value => updateGuardian("signature", value)}
                placeholder="Type full legal name"
                required={required}
                error={errors.guardianSignature}
                icon="id"
              />
            </div>
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="shield" size={15} />
            Family access boundaries
          </h3>
          <div className="boundary-list">
            <div className="boundary-item">
              <span>
                <Icon name="check" size={15} />
              </span>
              <div>
                <strong>Family-safe progress and actions</strong>
                <p>
                  Guardians can see schedules, required consent, billing, attendance summaries, support
                  actions and age-appropriate progress.
                </p>
              </div>
            </div>
            <div className="boundary-item no">
              <span>
                <Icon name="x" size={15} />
              </span>
              <div>
                <strong>No learner impersonation</strong>
                <p>
                  A guardian cannot silently enter as the learner, submit assessed work, send private learner
                  messages or operate raw Labs activity on the learner’s behalf.
                </p>
              </div>
            </div>
            <div className="boundary-item no">
              <span>
                <Icon name="x" size={15} />
              </span>
              <div>
                <strong>No unrestricted private or academic data</strong>
                <p>
                  Private groups, raw prompts/files, grading rationale and protected safeguarding records
                  remain outside ordinary family visibility.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="refresh" size={15} />
              </span>
              <div>
                <strong>Age and control transition</strong>
                <p>
                  When the learner reaches legal adulthood, permissions are reviewed and transferred rather
                  than automatically continued forever.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderChecksStep() {
    const route = state.application.route;
    const professional = route === "teaching" || route === "proctor";
    return (
      <>
        <Callout icon="shield" title="Checks are role-specific">
          Only checks necessary for the requested authority are collected. A proctor, instructor, organization
          owner and institution owner receive different review, training and approval requirements.
        </Callout>
        {professional ? (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="badge" size={15} />
              Professional declarations
            </h3>
            <div className="checkbox-grid">
              <ToggleRow
                label="Safeguarding preparation"
                detail="I will complete required safeguarding orientation before interacting with minors or protected learners."
                value={state.application.professional.safeguardingTraining}
                required
                onChange={value => updateProfessional("safeguardingTraining", value)}
              />
              <ToggleRow
                label="Accessibility preparation"
                detail="I will use accessible delivery and approved accommodation processes."
                value={state.application.professional.accessibilityTraining}
                onChange={value => updateProfessional("accessibilityTraining", value)}
              />
              <ToggleRow
                label="Conflict-of-interest declaration"
                detail="I have disclosed any relationship or interest that could affect impartiality."
                value={state.application.professional.conflictDeclaration}
                required
                onChange={value => updateProfessional("conflictDeclaration", value)}
              />
              <ToggleRow
                label="Role-authority declaration"
                detail={
                  route === "proctor"
                    ? "I will record observable facts and escalate; I will not grade, teach during the exam or declare misconduct."
                    : "I will act only within assigned teaching, mentoring or assessment scope; AI will not replace final human judgment."
                }
                value={state.application.professional.authorityDeclaration}
                required
                onChange={value => updateProfessional("authorityDeclaration", value)}
              />
            </div>
            {[errors.safeguarding, errors.conflict, errors.authority].filter(Boolean).map((error, index) => (
              <span className="field-error" style={{ marginTop: 8 }} key={index}>
                <Icon name="alert" size={12} />
                {error}
              </span>
            ))}
          </section>
        ) : (
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="building" size={15} />
              Entity authority and learner privacy
            </h3>
            <div className="checkbox-grid">
              <ToggleRow
                label="Authorized representative"
                detail="I am authorized to start this organization or institution application and identify the contracting owner."
                value={state.application.professional.authorityDeclaration}
                required
                onChange={value => updateProfessional("authorityDeclaration", value)}
              />
              <ToggleRow
                label="Minimum-necessary sponsorship access"
                detail="I understand that paying for seats does not grant unrestricted access to learner messages, raw Labs work, grades or safeguarding records."
                value={state.application.professional.conflictDeclaration}
                onChange={value => updateProfessional("conflictDeclaration", value)}
              />
            </div>
            {errors.authority ? (
              <span className="field-error" style={{ marginTop: 8 }}>
                <Icon name="alert" size={12} />
                {errors.authority}
              </span>
            ) : null}
          </section>
        )}
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="id" size={15} />
            Review sequence
          </h3>
          <div className="status-timeline">
            <div className="timeline-step active">
              <span className="timeline-mark">
                <Icon name="file" size={15} />
              </span>
              <div>
                <strong>Application evidence review</strong>
                <p>Admissions checks completeness, relevance, conflicts and required documents.</p>
              </div>
            </div>
            <div className="timeline-step">
              <span className="timeline-mark">
                <Icon name="video" size={15} />
              </span>
              <div>
                <strong>Human interview or orientation</strong>
                <p>Role understanding, communication, scenario judgment and support needs.</p>
              </div>
            </div>
            <div className="timeline-step">
              <span className="timeline-mark">
                <Icon name="shield" size={15} />
              </span>
              <div>
                <strong>Safeguarding, identity and authority checks</strong>
                <p>Applied according to role, country, learner group and institutional policy.</p>
              </div>
            </div>
            <div className="timeline-step">
              <span className="timeline-mark">
                <Icon name="key" size={15} />
              </span>
              <div>
                <strong>Invitation and scoped access</strong>
                <p>Final membership, MFA, expiry, workspace and assignments come from the server.</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderConsentStep() {
    const consentItems: Array<{ key: string; title: string; detail: string; required?: boolean }> = [
      {
        key: "privacy",
        title: "Privacy notice",
        detail: "I understand how identity, application, learning, support, audit and evidence data is used.",
        required: true,
      },
      {
        key: "terms",
        title: "Academy terms",
        detail: "I accept the terms that apply to account access, Programs, payments and acceptable use.",
        required: true,
      },
      {
        key: "conduct",
        title: "Community and professional conduct",
        detail: "I will participate respectfully, protect others and use the correct reporting routes.",
        required: true,
      },
      {
        key: "aiUse",
        title: "Transparent AI use",
        detail:
          "I will disclose and review AI assistance according to the Program, role and assessment rules.",
        required: true,
      },
      {
        key: "dataAccuracy",
        title: "Information is accurate",
        detail:
          "I have not knowingly provided false identity, authority, eligibility or sponsorship information.",
        required: true,
      },
      {
        key: "recording",
        title: "Recording preference",
        detail:
          "I understand which sessions may be recorded, how notice is given and where alternatives exist.",
      },
      {
        key: "communications",
        title: "Program communications",
        detail:
          "Send reminders, learning updates and relevant Academy opportunities; I can change this later.",
      },
    ];
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="clipboard" size={15} />
            Consent and declarations
          </h3>
          <div className="checkbox-grid">
            {consentItems.map(item => (
              <ToggleRow
                key={item.key}
                label={item.title}
                detail={item.detail}
                value={Boolean(state.application.consent[item.key])}
                required={item.required}
                optional={!item.required}
                onChange={value =>
                  updateApplication("consent", { ...state.application.consent, [item.key]: value })
                }
              />
            ))}
          </div>
          {Object.entries(errors)
            .filter(([key]) => consentItems.some(item => item.key === key))
            .map(([key, error]) => (
              <span className="field-error" style={{ marginTop: 7 }} key={key}>
                <Icon name="alert" size={12} />
                {key}: {error}
              </span>
            ))}
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="id" size={15} />
            Digital signature
          </h3>
          <div className="form-grid">
            <div className="form-field full">
              <TextField
                label={
                  state.application.route === "organization" || state.application.route === "institution"
                    ? "Authorized representative signature"
                    : "Applicant signature"
                }
                value={state.application.signature}
                onChange={value => updateApplication("signature", value)}
                placeholder="Type your full legal name"
                icon="id"
                required
                error={errors.signature}
              />
            </div>
          </div>
        </section>
        <Callout icon="info" title="Granular consent remains revocable" tone="blue">
          Optional communications and recording preferences can be changed later. Required legal, academic,
          safeguarding and audit records follow the applicable retention policy and cannot be erased merely by
          switching off marketing preferences.
        </Callout>
      </>
    );
  }

  function reviewRow(label: string, value: React.ReactNode) {
    return (
      <div className="review-row">
        <span>{label}</span>
        <strong>{value || "Not provided"}</strong>
      </div>
    );
  }

  function renderReviewStep() {
    const routeCard = APPLICATION_ROUTE_CARDS.find(card => card.id === state.application.route);
    const learnerName =
      state.application.route === "parent-child"
        ? `${state.application.learner.firstName} ${state.application.learner.lastName}`.trim()
        : `${state.application.applicant.firstName} ${state.application.applicant.lastName}`.trim();
    const incomplete = applicationSteps.filter(
      step => step.id !== "review" && !state.application.completedSteps.includes(step.id),
    );
    return (
      <>
        {incomplete.length ? (
          <Callout
            icon="alert"
            title={`${incomplete.length} section${incomplete.length === 1 ? "" : "s"} not yet marked complete`}
            tone="orange"
          >
            Review the information below. You can jump back to any section before submitting; the final server
            submission will also validate every required field.
          </Callout>
        ) : (
          <Callout icon="check" title="All prior sections are marked complete">
            Review the summary, confirm the declaration and submit for human admissions review.
          </Callout>
        )}
        <div className="review-grid" style={{ marginTop: 12 }}>
          <section className="review-section">
            <div className="review-head">
              <Icon name="arrow-right" size={14} />
              <strong>Route and role</strong>
              <button
                type="button"
                onClick={() =>
                  setState(current => ({
                    ...current,
                    applicationStep: applicationSteps.findIndex(step => step.id === "route"),
                  }))
                }
              >
                Edit
              </button>
            </div>
            {reviewRow("Route", routeCard?.title)}
            {reviewRow("Role", selectedRole.label)}
            {reviewRow("Workspace", selectedRole.workspace)}
            {reviewRow("MFA", selectedRole.mfa)}
          </section>
          <section className="review-section">
            <div className="review-head">
              <Icon name="user" size={14} />
              <strong>Applicant</strong>
              <button
                type="button"
                onClick={() =>
                  setState(current => ({
                    ...current,
                    applicationStep: applicationSteps.findIndex(step => step.id === "profile"),
                  }))
                }
              >
                Edit
              </button>
            </div>
            {reviewRow(
              "Name",
              `${state.application.applicant.firstName} ${state.application.applicant.lastName}`,
            )}
            {reviewRow("Email", state.application.applicant.email)}
            {reviewRow("Country", state.application.applicant.country)}
            {reviewRow(
              "Language",
              `${state.application.applicant.language}${state.application.applicant.secondaryLanguage && state.application.applicant.secondaryLanguage !== "None" ? ` + ${state.application.applicant.secondaryLanguage}` : ""}`,
            )}
          </section>
          {state.application.route === "parent-child" ||
          applicationSteps.some(step => step.id === "learner") ? (
            <section className="review-section">
              <div className="review-head">
                <Icon name="child" size={14} />
                <strong>Learner and age</strong>
                <button
                  type="button"
                  onClick={() =>
                    setState(current => ({
                      ...current,
                      applicationStep: applicationSteps.findIndex(step => step.id === "learner"),
                    }))
                  }
                >
                  Edit
                </button>
              </div>
              {reviewRow("Learner", learnerName)}
              {reviewRow(
                "Date of birth",
                state.application.route === "parent-child"
                  ? state.application.learner.dateOfBirth
                  : state.application.applicant.dateOfBirth,
              )}
              {reviewRow(
                "Age at cohort start",
                learnerAgeForDraft(state.application) ?? "Cohort not selected",
              )}
              {reviewRow("Guardian required", state.application.guardian.required ? "Yes" : "No")}
            </section>
          ) : null}
          {selectedProgram ? (
            <section className="review-section">
              <div className="review-head">
                <Icon name="book" size={14} />
                <strong>Program and cohort</strong>
                <button
                  type="button"
                  onClick={() =>
                    setState(current => ({
                      ...current,
                      applicationStep: applicationSteps.findIndex(step => step.id === "program"),
                    }))
                  }
                >
                  Edit
                </button>
              </div>
              {reviewRow("Program", selectedProgram.name)}
              {reviewRow(
                "Track",
                selectedTrack ? `${selectedTrack.label} · ${selectedTrack.ages}` : "Standard Program track",
              )}
              {reviewRow("Cohort", selectedCohort?.name)}
              {reviewRow("Starts", selectedCohort ? formatDate(selectedCohort.startDate) : "Not selected")}
              {reviewRow("Schedule", selectedCohort?.schedule)}
            </section>
          ) : null}
          {state.application.route === "organization" || state.application.route === "institution" ? (
            <section className="review-section">
              <div className="review-head">
                <Icon name="building" size={14} />
                <strong>Entity</strong>
                <button
                  type="button"
                  onClick={() =>
                    setState(current => ({
                      ...current,
                      applicationStep: applicationSteps.findIndex(step => step.id === "entity"),
                    }))
                  }
                >
                  Edit
                </button>
              </div>
              {reviewRow("Legal name", state.application.entity.legalName)}
              {reviewRow("Type", state.application.entity.type)}
              {reviewRow("Participants", state.application.entity.learnerCount)}
              {reviewRow(
                "SSO / SCIM",
                `${state.application.entity.ssoRequired ? "SSO" : "No SSO"} · ${state.application.entity.scimRequired ? "SCIM" : "No SCIM"}`,
              )}
            </section>
          ) : null}
          {state.application.guardian.required ? (
            <section className="review-section">
              <div className="review-head">
                <Icon name="heart" size={14} />
                <strong>Parent / guardian</strong>
                <button
                  type="button"
                  onClick={() =>
                    setState(current => ({
                      ...current,
                      applicationStep: applicationSteps.findIndex(step => step.id === "guardian"),
                    }))
                  }
                >
                  Edit
                </button>
              </div>
              {reviewRow(
                "Name",
                `${state.application.guardian.firstName} ${state.application.guardian.lastName}`,
              )}
              {reviewRow("Relationship", state.application.guardian.relationship)}
              {reviewRow("Email", state.application.guardian.email)}
              {reviewRow(
                "Verification",
                state.application.guardian.invitationSent ? "Invitation sent" : "Pending",
              )}
            </section>
          ) : null}
          <section className="review-section">
            <div className="review-head">
              <Icon name="wallet" size={14} />
              <strong>Funding</strong>
              <button
                type="button"
                onClick={() =>
                  setState(current => ({
                    ...current,
                    applicationStep: applicationSteps.findIndex(step => step.id === "funding"),
                  }))
                }
              >
                Edit
              </button>
            </div>
            {reviewRow("Route", state.application.funding)}
            {reviewRow("Currency", state.application.currency)}
            {reviewRow("Sponsor", state.application.sponsorName)}
            {reviewRow("Payment during application", "None")}
          </section>
          <section className="review-section full">
            <div className="review-head">
              <Icon name="clipboard" size={14} />
              <strong>Final declaration</strong>
            </div>
            <ToggleRow
              label="I confirm this application is complete and accurate"
              detail="I understand that admission, professional approval, guardian verification, accommodations, sponsorship and role authority are subject to human review and may require supporting evidence."
              value={state.application.declaration}
              required
              onChange={value => updateApplication("declaration", value)}
            />
            {errors.declaration ? (
              <span className="field-error" style={{ marginTop: 8 }}>
                <Icon name="alert" size={12} />
                {errors.declaration}
              </span>
            ) : null}
            {errors.reviewConsent ? (
              <span className="field-error" style={{ marginTop: 8 }}>
                <Icon name="alert" size={12} />
                {errors.reviewConsent}
              </span>
            ) : null}
            {errors.reviewGuardian ? (
              <span className="field-error" style={{ marginTop: 8 }}>
                <Icon name="alert" size={12} />
                {errors.reviewGuardian}
              </span>
            ) : null}
          </section>
        </div>
        <div style={{ marginTop: 12 }}>
          <Callout icon="shield" title="Submission enters a human-reviewed queue">
            Automated validation can detect missing or inconsistent information. It cannot silently reject an
            applicant, approve a privileged role, waive safeguarding, decide scholarship need or award a
            credential.
          </Callout>
        </div>
      </>
    );
  }

  function renderApplicationStepBody(step: ApplicationStepId) {
    if (step === "route") return renderApplicationRouteStep();
    if (step === "profile") return renderApplicantProfileStep();
    if (step === "learner") return renderLearnerStep();
    if (step === "entity") return renderEntityStep();
    if (step === "professional") return renderProfessionalStep();
    if (step === "program") return renderProgramStep();
    if (step === "cohort") return renderCohortStep();
    if (step === "readiness") return renderReadinessStep();
    if (step === "availability") return renderAvailabilityStep();
    if (step === "funding") return renderFundingStep();
    if (step === "guardian") return renderGuardianStep();
    if (step === "checks") return renderChecksStep();
    if (step === "consent") return renderConsentStep();
    return renderReviewStep();
  }

  function renderApplication() {
    const progress = Math.round(((state.applicationStep + 1) / Math.max(1, applicationSteps.length)) * 100);
    const activeStepIsValid = Object.keys(validateApplicationStep(activeApplicationStep.id)).length === 0;
    const applicationIsReady = applicationSteps.every(
      step => Object.keys(validateApplicationStep(step.id)).length === 0,
    );
    const visiblePhases = APPLICATION_PHASES.map(phase => ({
      ...phase,
      steps: phase.steps.filter(stepId => applicationSteps.some(step => step.id === stepId)),
    })).filter(phase => phase.steps.length > 0);
    const currentPhaseIndex = Math.max(
      0,
      visiblePhases.findIndex(phase => phase.steps.includes(activeApplicationStep.id)),
    );
    const currentPhase = visiblePhases[currentPhaseIndex];
    const nextApplicationSection = applicationSteps[state.applicationStep + 1];
    const completedCount = state.application.completedSteps.filter(stepId =>
      applicationSteps.some(step => step.id === stepId),
    ).length;
    return (
      <div className="page application-page">
        <PageHead
          icon="clipboard"
          eyebrow={`Application ${state.application.reference}`}
          title="Complete your Academy application."
          description="The form adapts to the learner, family, professional, organization or institution route and saves every section locally in this prototype."
          action={
            <div className="application-head-status">
              <StatusChip tone="green">
                <Icon name="save" size={11} />
                Autosaved
              </StatusChip>
            </div>
          }
        />
        <div className="application-layout">
          <aside className={classNames("step-sidebar", mobileStepsOpen && "mobile-open")}>
            <div className="step-summary">
              <div className="step-summary-copy">
                <div className="step-progress-score">
                  <strong>{progress}%</strong>
                  <span>complete</span>
                </div>
                <div>
                  <span>Application journey</span>
                  <strong>
                    Step {state.applicationStep + 1} of {applicationSteps.length}
                  </strong>
                </div>
              </div>
              <button
                type="button"
                className="btn ghost small mobile-step-toggle mobile-only"
                onClick={() => setMobileStepsOpen(current => !current)}
              >
                <Icon name="menu" size={13} />
                {mobileStepsOpen ? "Hide" : "Show"} sections
              </button>
              <div className="step-progress">
                <i style={{ width: `${progress}%` }} />
              </div>
              <p className="step-summary-note">
                <Icon name={currentPhase?.icon || "sparkles"} size={13} />
                <span>
                  <strong>{currentPhase?.label || "Your application"}</strong>
                  {completedCount
                    ? `${completedCount} section${completedCount === 1 ? "" : "s"} safely saved`
                    : "A clear start, one section at a time"}
                </span>
              </p>
            </div>
            <div className="step-list">
              {applicationSteps.map((step, index) => {
                const done = state.application.completedSteps.includes(step.id);
                const unlocked =
                  index <= state.applicationStep ||
                  applicationSteps
                    .slice(0, index)
                    .every(previous => state.application.completedSteps.includes(previous.id));
                return (
                  <button
                    type="button"
                    disabled={!unlocked}
                    className={classNames(
                      "step-link",
                      index === state.applicationStep && "active",
                      done && "done",
                      !unlocked && "locked",
                    )}
                    key={step.id}
                    onClick={() => {
                      if (!unlocked) return;
                      setState(current => ({ ...current, applicationStep: index }));
                      setMobileStepsOpen(false);
                    }}
                  >
                    <span className="step-index">{done ? <Icon name="check" size={12} /> : index + 1}</span>
                    <span>
                      <strong>{step.short}</strong>
                    </span>
                    <Icon name="chevron" size={13} />
                  </button>
                );
              })}
            </div>
            <div style={{ padding: 10, borderTop: "1px solid var(--line)" }}>
              <Button
                size="small"
                onClick={() =>
                  notify(
                    "Draft saved",
                    `Last saved ${new Date(state.application.lastSavedAt).toLocaleTimeString()}.`,
                    "success",
                  )
                }
              >
                <Icon name="save" size={13} />
                Save draft
              </Button>
              <Button size="small" onClick={exportApplication}>
                <Icon name="download" size={13} />
                Export
              </Button>
            </div>
          </aside>
          <main className="application-main">
            <nav className="application-phase-track" aria-label="Application milestones" tabIndex={0}>
              {visiblePhases.map((phase, phaseIndex) => {
                const phaseDone = phase.steps.every(stepId =>
                  state.application.completedSteps.includes(stepId),
                );
                const phaseActive = phaseIndex === currentPhaseIndex;
                return (
                  <div
                    className={classNames("application-phase", phaseDone && "done", phaseActive && "active")}
                    key={phase.id}
                    aria-current={phaseActive ? "step" : undefined}
                  >
                    <span className="application-phase-icon">
                      <Icon name={phaseDone ? "check" : phase.icon} size={15} />
                    </span>
                    <span>
                      <small>Milestone {phaseIndex + 1}</small>
                      <strong>{phase.label}</strong>
                      <em>{phase.description}</em>
                    </span>
                  </div>
                );
              })}
            </nav>
            <section className="step-card">
              <div className="step-card-head">
                <span className="step-card-icon">
                  <Icon name={activeApplicationStep.icon} size={20} />
                </span>
                <div className="step-card-title">
                  <span className="application-step-kicker">
                    <Icon name="sparkles" size={12} />
                    {currentPhase?.label || "Application"} · Step {state.applicationStep + 1}
                  </span>
                  <h3>{activeApplicationStep.label}</h3>
                  <p>{activeApplicationStep.description}</p>
                </div>
                <span className="autosave">
                  <Icon name="save" size={12} />
                  Saved{" "}
                  {new Date(state.application.lastSavedAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              <div className="step-card-body step-enter" key={activeApplicationStep.id}>
                <ApplicationStepPages label={activeApplicationStep.label}>
                  {renderApplicationStepBody(activeApplicationStep.id)}
                </ApplicationStepPages>
              </div>
              <div className="step-card-actions">
                <Button disabled={state.applicationStep === 0} onClick={previousApplicationStep}>
                  <Icon name="arrow-left" size={15} />
                  Back
                </Button>
                <div className="application-action-next">
                  <span className="next-step-preview">
                    <small>{nextApplicationSection ? "Up next" : "Ready when you are"}</small>
                    <strong>{nextApplicationSection?.label || "Send for human review"}</strong>
                  </span>
                  {activeApplicationStep.id === "review" ? (
                    <>
                      <Button onClick={exportApplication}>
                        <Icon name="download" size={15} />
                        Export copy
                      </Button>
                      <Button tone="orange" disabled={!applicationIsReady} onClick={submitApplication}>
                        Submit application
                        <Icon name="arrow-right" size={15} />
                      </Button>
                    </>
                  ) : (
                    <Button tone="primary" disabled={!activeStepIsValid} onClick={nextApplicationStep}>
                      Save and continue
                      <Icon name="arrow-right" size={15} />
                    </Button>
                  )}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    );
  }

  function renderStatus() {
    const statuses: Array<{ id: ApplicationDraft["status"]; label: string; detail: string; icon: IconName }> =
      [
        {
          id: "submitted",
          label: "Application received",
          detail: "Reference created and submission locked for review.",
          icon: "check",
        },
        {
          id: "review",
          label: "Admissions review",
          detail: "A human reviewer checks fit, eligibility, evidence and support needs.",
          icon: "clipboard",
        },
        {
          id: "interview",
          label: "Conversation / additional information",
          detail:
            "A short interview, guardian check, sponsor confirmation or role scenario may be requested.",
          icon: "video",
        },
        {
          id: "offered",
          label: "Offer and conditions",
          detail: "Program, track, cohort, fees, support and any conditions are shown before acceptance.",
          icon: "mail",
        },
        {
          id: "accepted",
          label: "Accepted",
          detail: "The offer is accepted and payment, sponsorship or onboarding can continue.",
          icon: "star",
        },
      ];
    const order = statuses.map(item => item.id);
    const currentIndex = Math.max(0, order.indexOf(state.application.status));
    const advance = () => {
      const next =
        state.application.status === "submitted"
          ? "review"
          : state.application.status === "review"
            ? "interview"
            : state.application.status === "interview"
              ? "offered"
              : state.application.status;
      setState(current => ({
        ...current,
        screen: next === "offered" ? "offer" : "status",
        application: { ...current.application, status: next },
      }));
      notify(
        next === "offered" ? "Offer prepared" : "Application status updated",
        next === "review"
          ? "A human admissions review is now shown."
          : next === "interview"
            ? "The applicant conversation stage is now shown."
            : "The offer is ready to inspect.",
        "success",
      );
    };
    return (
      <div className="page">
        <PageHead
          icon="clipboard"
          eyebrow={state.application.reference}
          title="Track your application."
          description="The timeline separates automated completeness checks from human admissions, safeguarding, sponsorship and professional-role decisions."
          action={
            <StatusChip tone={state.application.status === "offered" ? "green" : "orange"}>
              {state.application.status}
            </StatusChip>
          }
        />
        <div className="checkout-layout">
          <div>
            <section className="panel">
              <div className="panel-head">
                <Icon name="chart" size={16} />
                <strong>Admissions timeline</strong>
                <span>Human-reviewed</span>
              </div>
              <div className="panel-body">
                <div className="status-timeline">
                  {statuses.map((item, index) => (
                    <div
                      className={classNames(
                        "timeline-step",
                        index < currentIndex && "done",
                        index === currentIndex && "active",
                      )}
                      key={item.id}
                    >
                      <span className="timeline-mark">
                        <Icon name={index < currentIndex ? "check" : item.icon} size={15} />
                      </span>
                      <div>
                        <strong>{item.label}</strong>
                        <p>{item.detail}</p>
                        <time>
                          {index < currentIndex
                            ? "Completed in prototype"
                            : index === currentIndex
                              ? "Current stage"
                              : "Not started"}
                        </time>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            <section className="panel" style={{ marginTop: 13 }}>
              <div className="panel-head">
                <Icon name="message" size={16} />
                <strong>Messages and requests</strong>
              </div>
              <div className="panel-body">
                <div className="boundary-list">
                  <div className="boundary-item">
                    <span>
                      <Icon name="check" size={15} />
                    </span>
                    <div>
                      <strong>Submission received</strong>
                      <p>
                        Your application is stored under {state.application.reference}. No payment was taken.
                      </p>
                    </div>
                  </div>
                  <div className="boundary-item">
                    <span>
                      <Icon name="shield" size={15} />
                    </span>
                    <div>
                      <strong>Human review remains final</strong>
                      <p>
                        Automated checks can flag a missing field or cohort mismatch but cannot make the final
                        admission, safeguarding or professional-role decision.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="button-row">
                  <Button onClick={() => setScreen("application")}>
                    <Icon name="eye" size={14} />
                    View application
                  </Button>
                  <Button onClick={() => setModal("application-export")}>
                    <Icon name="download" size={14} />
                    Export record
                  </Button>
                </div>
              </div>
            </section>
          </div>
          <aside className="panel order-summary">
            <div className="panel-head">
              <Icon name="calendar" size={16} />
              <strong>Selected pathway</strong>
            </div>
            <div className="panel-body">
              {reviewRow("Role", selectedRole.label)}
              {reviewRow("Program", selectedProgram?.name)}
              {reviewRow("Track", selectedTrack?.label)}
              {reviewRow("Cohort", selectedCohort?.name)}
              {reviewRow("Start date", selectedCohort ? formatDate(selectedCohort.startDate) : "Pending")}
              {reviewRow("Funding", state.application.funding)}
              <div className="button-row" style={{ marginTop: 14 }}>
                {state.application.status === "offered" ? (
                  <Button tone="primary" onClick={() => setScreen("offer")}>
                    Open offer
                    <Icon name="arrow-right" size={14} />
                  </Button>
                ) : (
                  <Button tone="orange" onClick={advance}>
                    Simulate next reviewed stage
                    <Icon name="arrow-right" size={14} />
                  </Button>
                )}
              </div>
              <div className="demo-box">
                Prototype controls are visible so the complete state machine can be tested. Production status
                changes require authorized server actions and an immutable decision record.
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  function prototypeFee() {
    if (state.application.route === "organization" || state.application.route === "institution")
      return { amount: 0, label: "Configured in proposal", deposit: 0 };
    const base: Record<string, number> = {
      "everyday-ai": 240000,
      "prompt-context": 420000,
      "governance-safety": 650000,
      "product-design": 850000,
      "data-knowledge": 900000,
      "automation-workflows": 900000,
      "app-builder": 1450000,
    };
    const amount = base[state.application.programId] || 0;
    return {
      amount,
      label: `${state.application.currency} ${amount.toLocaleString()}`,
      deposit: Math.round(amount * 0.35),
    };
  }

  function renderOffer() {
    const fee = prototypeFee();
    const acceptOffer = () => {
      setState(current => ({
        ...current,
        application: { ...current.application, status: "accepted" },
        screen: ["organization", "institution", "employer", "invoice"].includes(current.application.funding)
          ? "onboarding"
          : "checkout",
        onboarding: { ...current.onboarding, roleId: current.application.roleId },
      }));
      notify(
        "Offer accepted",
        ["organization", "institution", "employer", "invoice"].includes(state.application.funding)
          ? "Commercial confirmation can continue alongside role onboarding."
          : "Continue to the configured checkout.",
        "success",
      );
    };
    return (
      <div className="page">
        <section className="offer-hero">
          <span className="eyebrow">
            <Icon name="star" size={13} />
            Conditional Academy offer
          </span>
          <h2>You have an offer for {selectedProgram?.name || "the selected Academy pathway"}.</h2>
          <p>
            Review the Program, age track, exact cohort, support, funding and conditions before accepting.
            This prototype offer demonstrates the complete transition into checkout and role-specific
            onboarding.
          </p>
          <div className="offer-metrics">
            <div className="offer-metric">
              <span>Program</span>
              <strong>{selectedProgram?.name || "Configured pathway"}</strong>
            </div>
            <div className="offer-metric">
              <span>Cohort</span>
              <strong>{selectedCohort?.name || "To be configured"}</strong>
            </div>
            <div className="offer-metric">
              <span>Starts</span>
              <strong>{selectedCohort ? formatDate(selectedCohort.startDate) : "By agreement"}</strong>
            </div>
            <div className="offer-metric">
              <span>Prototype configured fee</span>
              <strong>{fee.label}</strong>
            </div>
          </div>
        </section>
        <div
          className="dashboard-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1.4fr) minmax(280px,.7fr)",
            gap: 14,
            marginTop: 14,
          }}
        >
          <div style={{ display: "grid", gap: 14 }}>
            <section className="panel">
              <div className="panel-head">
                <Icon name="clipboard" size={16} />
                <strong>Offer conditions</strong>
              </div>
              <div className="panel-body">
                <div className="boundary-list">
                  <div className="boundary-item">
                    <span>
                      <Icon name="check" size={15} />
                    </span>
                    <div>
                      <strong>Identity and eligibility</strong>
                      <p>
                        Complete any outstanding email, guardian, sponsor or professional-role verification
                        before workspace activation.
                      </p>
                    </div>
                  </div>
                  {state.application.guardian.required ? (
                    <div className="boundary-item">
                      <span>
                        <Icon name="heart" size={15} />
                      </span>
                      <div>
                        <strong>Guardian verification</strong>
                        <p>
                          The verified adult relationship and required consents must be active before the
                          learner enters age-restricted spaces.
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <div className="boundary-item">
                    <span>
                      <Icon name="clock" size={15} />
                    </span>
                    <div>
                      <strong>Participation</strong>
                      <p>
                        Attend the configured live sessions, complete practical work and follow the Program’s
                        AI-use and evidence rules.
                      </p>
                    </div>
                  </div>
                  <div className="boundary-item">
                    <span>
                      <Icon name="shield" size={15} />
                    </span>
                    <div>
                      <strong>Human-reviewed evidence</strong>
                      <p>Completion and credential decisions remain human-owned and evidence-based.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section className="panel">
              <div className="panel-head">
                <Icon name="accessibility" size={16} />
                <strong>Support and access</strong>
              </div>
              <div className="panel-body">
                <div className="program-meta">
                  {state.application.accommodations.length ? (
                    state.application.accommodations.map(item => (
                      <span className="meta-chip" key={item}>
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="meta-chip">No accommodation selected</span>
                  )}
                  {state.application.lowBandwidth ? (
                    <span className="meta-chip">Low-bandwidth route</span>
                  ) : null}
                  <span className="meta-chip">{state.application.applicant.language}</span>
                </div>
                <p className="section-copy" style={{ marginTop: 10 }}>
                  Support staff confirm practical arrangements separately. A request is not treated as a
                  diagnosis and does not lower academic or professional expectations.
                </p>
              </div>
            </section>
          </div>
          <aside className="panel order-summary">
            <div className="panel-head">
              <Icon name="wallet" size={16} />
              <strong>Commercial summary</strong>
            </div>
            <div className="panel-body">
              <div className="price-line">
                <span>Program / configured cohort</span>
                <strong>{fee.label}</strong>
              </div>
              <div className="price-line">
                <span>Application fee</span>
                <strong>{state.application.currency} 0</strong>
              </div>
              <div className="price-line">
                <span>Scholarship / sponsor</span>
                <strong>
                  {state.application.funding === "scholarship"
                    ? "Pending award"
                    : state.application.sponsorName || "None"}
                </strong>
              </div>
              <div className="price-line total">
                <span>Due at checkout</span>
                <strong>
                  {fee.amount
                    ? `${state.application.currency} ${fee.deposit.toLocaleString()}`
                    : "Per proposal / invoice"}
                </strong>
              </div>
              <p className="section-copy" style={{ marginTop: 10 }}>
                The values in this prototype are configuration examples, not published Academy prices.
                Production loads signed pricing, tax, currency, discount and refund terms from the offer
                service.
              </p>
              <div className="button-row">
                <Button tone="primary" onClick={acceptOffer}>
                  Accept offer
                  <Icon name="arrow-right" size={14} />
                </Button>
                <Button
                  onClick={() => {
                    setState(current => ({
                      ...current,
                      application: { ...current.application, status: "review" },
                      screen: "status",
                    }));
                    notify(
                      "Offer response recorded",
                      "The application returned to Admissions for discussion.",
                      "info",
                    );
                  }}
                >
                  Ask a question
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  function renderCheckout() {
    const fee = prototypeFee();
    const due =
      checkoutPlan === "Full payment" ? fee.amount : checkoutPlan === "Admission deposit" ? fee.deposit : 0;
    const completePayment = () => {
      if (!checkoutPlan || !checkoutMethod || !checkoutAgreement) {
        notify(
          "Complete the checkout choices",
          "Choose a payment plan and method, then accept the payment and enrolment terms.",
          "warning",
        );
        return;
      }
      setState(current => ({
        ...current,
        screen: "onboarding",
        application: { ...current.application, status: "enrolled" },
        onboarding: { ...current.onboarding, roleId: current.application.roleId },
      }));
      notify(
        "Prototype payment authorized",
        "No real money was charged. Role-specific onboarding is now open.",
        "success",
      );
    };
    return (
      <div className="page">
        <PageHead
          icon="credit-card"
          eyebrow="Secure checkout"
          title="Confirm payment and enrolment."
          description="The checkout shows the exact Program, cohort, payer, currency and schedule before authorization. This standalone prototype never sends a real charge."
        />
        <div className="checkout-layout">
          <div style={{ display: "grid", gap: 14 }}>
            <section className="panel">
              <div className="panel-head">
                <Icon name="wallet" size={16} />
                <strong>Payment plan</strong>
              </div>
              <div className="panel-body">
                <div className="checkbox-grid">
                  <CheckCard
                    selected={checkoutPlan === "Full payment"}
                    onClick={() => setCheckoutPlan("Full payment")}
                    title="Full payment"
                    detail="Pay the configured fee once."
                  />
                  <CheckCard
                    selected={checkoutPlan === "Admission deposit"}
                    onClick={() => setCheckoutPlan("Admission deposit")}
                    title="Admission deposit"
                    detail="Pay the configured 35% prototype deposit; remaining milestones appear in the account."
                  />
                </div>
              </div>
            </section>
            <section className="panel">
              <div className="panel-head">
                <Icon name="credit-card" size={16} />
                <strong>Payment method</strong>
              </div>
              <div className="panel-body">
                <div className="checkbox-grid">
                  {["Mobile money", "Card", "Bank transfer", "Sponsor code"].map(method => (
                    <CheckCard
                      key={method}
                      selected={checkoutMethod === method}
                      onClick={() => setCheckoutMethod(method)}
                      title={method}
                      detail={
                        method === "Mobile money"
                          ? "Use an approved local mobile-money provider."
                          : method === "Card"
                            ? "Use a PCI-compliant hosted payment page."
                            : method === "Bank transfer"
                              ? "Receive a unique reference and reconciliation instructions."
                              : "Apply a signed sponsor or scholarship authorization."
                      }
                    />
                  ))}
                </div>
                {checkoutMethod ? (
                  <div className="form-grid" style={{ marginTop: 12 }}>
                    <TextField
                      label={
                        checkoutMethod === "Mobile money"
                          ? "Mobile-money number"
                          : checkoutMethod === "Card"
                            ? "Cardholder email"
                            : checkoutMethod === "Bank transfer"
                              ? "Payer name"
                              : "Sponsor code"
                      }
                      value={
                        checkoutMethod === "Mobile money"
                          ? state.application.applicant.phone
                          : state.application.sponsorName
                      }
                      onChange={value =>
                        checkoutMethod === "Mobile money"
                          ? updateApplicant("phone", value)
                          : updateApplication("sponsorName", value)
                      }
                      placeholder={checkoutMethod === "Mobile money" ? "+256 …" : "Enter payer details"}
                      icon={
                        checkoutMethod === "Mobile money"
                          ? "phone"
                          : checkoutMethod === "Sponsor code"
                            ? "key"
                            : "wallet"
                      }
                    />
                  </div>
                ) : null}
              </div>
            </section>
            <section className="panel">
              <div className="panel-head">
                <Icon name="shield" size={16} />
                <strong>Authorization</strong>
              </div>
              <div className="panel-body">
                <ToggleRow
                  label="I accept the signed offer, enrolment and payment terms"
                  detail="I have reviewed the Program, cohort, payer, amount, schedule, refund terms and any guardian or sponsor responsibility."
                  value={checkoutAgreement}
                  required
                  onChange={setCheckoutAgreement}
                />
                <div style={{ marginTop: 10 }}>
                  <Callout icon="lock" title="Payment data boundary">
                    Production uses hosted, compliant payment providers. The Academy application must not
                    store full card details, mobile-money PINs or banking credentials.
                  </Callout>
                </div>
              </div>
            </section>
          </div>
          <aside className="panel order-summary">
            <div className="panel-head">
              <Icon name="clipboard" size={16} />
              <strong>Order summary</strong>
            </div>
            <div className="panel-body">
              {reviewRow(
                "Applicant",
                `${state.application.applicant.firstName} ${state.application.applicant.lastName}`,
              )}
              {reviewRow("Program", selectedProgram?.name)}
              {reviewRow("Track", selectedTrack?.label)}
              {reviewRow("Cohort", selectedCohort?.name)}
              {reviewRow("Payer", state.application.funding)}
              <div className="price-line">
                <span>Configured fee</span>
                <strong>{fee.label}</strong>
              </div>
              <div className="price-line">
                <span>Payment plan</span>
                <strong>{checkoutPlan || "Not selected"}</strong>
              </div>
              <div className="price-line">
                <span>Method</span>
                <strong>{checkoutMethod || "Not selected"}</strong>
              </div>
              <div className="price-line total">
                <span>Authorize now</span>
                <strong>
                  {checkoutPlan ? `${state.application.currency} ${due.toLocaleString()}` : "Select a plan"}
                </strong>
              </div>
              <Button
                tone="orange"
                disabled={!checkoutPlan || !checkoutMethod || !checkoutAgreement}
                onClick={completePayment}
              >
                Authorize prototype payment
                <Icon name="arrow-right" size={14} />
              </Button>
              <div className="demo-box">
                No real charge is made. The button completes the state transition so onboarding can be tested.
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  function policySetForRole(role: RoleRecord) {
    if (role.category === "assessment")
      return [
        {
          key: "facts",
          title: "Record observable facts",
          detail: "Use timestamps, actions and direct observations; separate facts from interpretation.",
          icon: "eye" as IconName,
        },
        {
          key: "no-grading",
          title: "No teaching or grading authority",
          detail:
            "Support readiness and session operations without coaching answers, grading responses or deciding outcomes.",
          icon: "x" as IconName,
        },
        {
          key: "signals",
          title: "Signals are not proof",
          detail:
            "Automated integrity, gaze, identity or behavior signals require human review and candidate context.",
          icon: "alert" as IconName,
        },
        {
          key: "escalation",
          title: "Escalate through due process",
          detail: "Protect the candidate, preserve evidence and use the assigned incident route.",
          icon: "shield" as IconName,
        },
      ];
    if (role.category === "family")
      return [
        {
          key: "no-impersonation",
          title: "Never impersonate the learner",
          detail:
            "Do not submit assessed work, operate private learner chats or enter raw Labs activity as the learner.",
          icon: "x" as IconName,
        },
        {
          key: "family-safe",
          title: "Use family-safe progress",
          detail:
            "See schedules, consent, billing, attendance summaries and age-appropriate actions rather than unrestricted private records.",
          icon: "heart" as IconName,
        },
        {
          key: "adult-transition",
          title: "Respect the adulthood transition",
          detail:
            "When the learner reaches legal adulthood, permissions are reviewed and transferred rather than silently continued.",
          icon: "refresh" as IconName,
        },
        {
          key: "safeguarding",
          title: "Use safeguarding routes",
          detail:
            "Report urgent safety concerns through protected channels; do not investigate privately in ordinary messages.",
          icon: "shield" as IconName,
        },
      ];
    if (role.category === "teaching")
      return [
        {
          key: "human-assessment",
          title: "Final assessment remains human",
          detail:
            "AI may help prepare feedback or organize evidence but cannot make the final grade or credential decision.",
          icon: "badge" as IconName,
        },
        {
          key: "group-privacy",
          title: "Protect learner and group privacy",
          detail: "Access only assigned cohorts, groups and minimum-necessary support information.",
          icon: "lock" as IconName,
        },
        {
          key: "ai-boundary",
          title: "Disclose and review AI assistance",
          detail: "Learners must understand where AI was used and what human judgment remains.",
          icon: "sparkles" as IconName,
        },
        {
          key: "safeguarding",
          title: "Safeguarding before convenience",
          detail: "Use age-appropriate communication, escalation and protected case handling.",
          icon: "shield" as IconName,
        },
      ];
    return [
      {
        key: "least-privilege",
        title: "Least-privilege access",
        detail: "Use only the workspace, tenant, cohort, academic unit, case or project assigned to you.",
        icon: "key" as IconName,
      },
      {
        key: "human-authority",
        title: "Named human authority",
        detail:
          "Admissions, academic, safeguarding, finance and governance decisions require the correct authorized person.",
        icon: "user" as IconName,
      },
      {
        key: "privacy",
        title: "Minimum-necessary information",
        detail:
          "Do not use sponsorship, administration or technical access to expose unrelated learner or staff data.",
        icon: "lock" as IconName,
      },
      {
        key: "audit",
        title: "Actions remain attributable",
        detail: "Role changes, approvals, exports, invitations, decisions and sensitive access are logged.",
        icon: "clipboard" as IconName,
      },
    ];
  }

  function renderOnboardingWelcome() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    const destination = onboardingDestination(role.id);
    return (
      <>
        <div className="welcome-banner">
          <span className="eyebrow">
            <Icon name="sparkles" size={13} />
            Welcome to Chatoner AI Academy
          </span>
          <h3>
            {state.authName ||
              `${state.application.applicant.firstName} ${state.application.applicant.lastName}`.trim() ||
              "Academy member"}
            , your {role.label} journey is ready.
          </h3>
          <p>
            Onboarding confirms security, language, accessibility, relationships, tools and boundaries before
            opening {destination.label}. It does not grant more authority than the verified server membership.
          </p>
        </div>
        <div className="review-grid" style={{ marginTop: 12 }}>
          <section className="review-section">
            <div className="review-head">
              <Icon name="badge" size={14} />
              <strong>Verified role</strong>
            </div>
            {reviewRow("Role", role.label)}
            {reviewRow("Category", ROLE_GROUPS.find(group => group.id === role.category)?.label)}
            {reviewRow("Workspace", role.workspace)}
            {reviewRow("MFA", role.mfa)}
          </section>
          <section className="review-section">
            <div className="review-head">
              <Icon name="book" size={14} />
              <strong>Program context</strong>
            </div>
            {reviewRow("Program", selectedProgram?.name || "Role-based access")}
            {reviewRow("Track", selectedTrack?.label)}
            {reviewRow("Cohort", selectedCohort?.name || "Assigned after invitation")}
            {reviewRow("Application", state.application.reference)}
          </section>
        </div>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="arrow-right" size={15} />
            What this onboarding will configure
          </h3>
          <div className="boundary-list">
            <div className="boundary-item">
              <span>
                <Icon name="lock" size={15} />
              </span>
              <div>
                <strong>Security and recovery</strong>
                <p>MFA where required, trusted devices, recovery codes and session expectations.</p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="language" size={15} />
              </span>
              <div>
                <strong>Language and accessibility</strong>
                <p>Personal interface, communication, captions, accommodations and low-bandwidth choices.</p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="users" size={15} />
              </span>
              <div>
                <strong>People and scope</strong>
                <p>
                  Only linked learners, assigned cohorts, teams, entities or academic units relevant to the
                  role.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="shield" size={15} />
              </span>
              <div>
                <strong>Role boundaries</strong>
                <p>What the role may do, must not do, and when a different human authority is required.</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderOnboardingSecurity() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    return (
      <>
        <Callout
          icon="lock"
          title={`${role.mfa === "required" ? "Multi-factor authentication is required" : "Multi-factor authentication is recommended"}`}
        >
          {role.mfa === "required"
            ? `The ${role.label} role includes privileged, academic, assessment, family, organization or institutional actions. Access remains blocked until MFA and recovery are confirmed.`
            : "MFA protects the account even where it is not mandatory. Passkeys or security keys should be offered before SMS where supported."}
        </Callout>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="key" size={15} />
            Security setup
          </h3>
          <div className="checkbox-grid">
            <ToggleRow
              label="MFA method enrolled"
              detail="Authenticator app, passkey/security key or approved organization identity provider."
              value={state.onboarding.mfaConfirmed}
              required={role.mfa === "required"}
              onChange={value => updateOnboarding("mfaConfirmed", value)}
            />
            <ToggleRow
              label="Recovery codes stored safely"
              detail="One-time codes are stored offline and never placed in shared learning or evidence spaces."
              value={state.onboarding.recoveryCodesSaved}
              required={role.mfa === "required"}
              onChange={value => updateOnboarding("recoveryCodesSaved", value)}
            />
          </div>
          {errors.security ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.security}
            </span>
          ) : null}
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="laptop" size={15} />
            Trusted access
          </h3>
          <div className="review-grid">
            <div className="review-section">
              <div className="review-head">
                <Icon name="laptop" size={14} />
                <strong>Current device</strong>
              </div>
              {reviewRow(
                "Browser",
                typeof navigator !== "undefined"
                  ? navigator.userAgent.split(" ").slice(-2).join(" ")
                  : "Current browser",
              )}
              {reviewRow("Remembered", rememberDevice ? "Yes, subject to policy" : "No")}
              {reviewRow("Session", "Revocable from account security")}
            </div>
            <div className="review-section">
              <div className="review-head">
                <Icon name="shield" size={14} />
                <strong>Privileged action protection</strong>
              </div>
              {reviewRow("Step-up checks", "Role, risk and action based")}
              {reviewRow("Audit", "Sensitive actions logged")}
              {reviewRow("Shared devices", "Sign out and do not remember")}
            </div>
          </div>
        </section>
        <Callout icon="shield" title="Authorization is never read from the browser" tone="blue">
          The interface can display role and scope, but every protected request must be authorized again by
          the backend using verified membership, tenant, learner relationship, assignment, expiry and policy
          context.
        </Callout>
      </>
    );
  }

  function renderOnboardingProfile() {
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="user" size={15} />
            Confirm profile
          </h3>
          <div className="review-grid">
            <section className="review-section">
              <div className="review-head">
                <Icon name="id" size={14} />
                <strong>Identity</strong>
              </div>
              {reviewRow(
                "Name",
                state.authName ||
                  `${state.application.applicant.firstName} ${state.application.applicant.lastName}`,
              )}
              {reviewRow("Email", state.authEmail || state.application.applicant.email)}
              {reviewRow("Country", state.application.applicant.country)}
              {reviewRow("Time zone", state.application.applicant.timezone)}
            </section>
            <section className="review-section">
              <div className="review-head">
                <Icon name="language" size={14} />
                <strong>Language</strong>
              </div>
              {reviewRow("Primary", state.application.applicant.language)}
              {reviewRow("Secondary", state.application.applicant.secondaryLanguage)}
              {reviewRow("Original content", "Retained with translations")}
              {reviewRow("Right-to-left", "Supported when selected")}
            </section>
          </div>
          <div className="checkbox-grid" style={{ marginTop: 11 }}>
            <ToggleRow
              label="Profile information is correct"
              detail="Update the account profile later if a verified identity detail changes."
              value={state.onboarding.profileConfirmed}
              required
              onChange={value => updateOnboarding("profileConfirmed", value)}
            />
            <ToggleRow
              label="Language preferences confirmed"
              detail="Interface and communication preferences can be changed without altering original evidence."
              value={state.onboarding.languageConfirmed}
              required
              onChange={value => updateOnboarding("languageConfirmed", value)}
            />
            <ToggleRow
              label="Accessibility preferences confirmed"
              detail="Selected accommodations and low-bandwidth support are correct or I know how to request changes."
              value={state.onboarding.accessibilityConfirmed}
              required
              onChange={value => updateOnboarding("accessibilityConfirmed", value)}
            />
          </div>
          {errors.profile ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.profile}
            </span>
          ) : null}
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="message" size={15} />
            Notifications
          </h3>
          <div className="checkbox-grid">
            {[
              "In-app",
              "Email",
              "WhatsApp / SMS",
              "Class reminders",
              "Review decisions",
              "Billing and sponsorship",
            ].map(item => (
              <CheckCard
                key={item}
                selected={state.onboarding.notifications.includes(item)}
                onClick={() =>
                  toggleArray(state.onboarding.notifications, item, next =>
                    updateOnboarding("notifications", next),
                  )
                }
                title={item}
              />
            ))}
          </div>
        </section>
        <Callout icon="accessibility" title="Accessibility follows the person, not one page">
          Approved preferences should carry into Academy, Chatoner Class, eligible AI Labs, assessments and
          generated documents while preserving each system’s safety and academic rules.
        </Callout>
      </>
    );
  }

  function renderOnboardingRelationships() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    if (role.category === "family") {
      const learner =
        `${state.application.learner.firstName || state.application.applicant.firstName} ${state.application.learner.lastName || state.application.applicant.lastName}`.trim() ||
        "Linked learner";
      const linked = state.onboarding.linkedLearners.includes(learner);
      return (
        <>
          <Callout icon="heart" title="Verify the learner relationship">
            Family access is created from a verified, scoped relationship. It can be suspended, transferred or
            reviewed as the learner’s age and legal context change.
          </Callout>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="child" size={15} />
              Linked learner
            </h3>
            <div className="workspace-grid">
              <button
                type="button"
                className={classNames("workspace-card", linked && "selected")}
                onClick={() => updateOnboarding("linkedLearners", linked ? [] : [learner])}
              >
                <div className="workspace-card-top">
                  <span className="workspace-card-icon">
                    <Icon name="child" size={20} />
                  </span>
                  <div>
                    <h3>{learner}</h3>
                    <p>{selectedTrack?.label || "Age-aware learner profile"}</p>
                  </div>
                  {linked ? (
                    <span className="selected-check">
                      <Icon name="check" size={13} />
                    </span>
                  ) : null}
                </div>
                <div className="workspace-meta">
                  <StatusChip tone="green">Guardian relationship</StatusChip>
                  <StatusChip>{selectedCohort?.name || "Cohort pending"}</StatusChip>
                </div>
              </button>
            </div>
          </section>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="users" size={15} />
              Family responsibilities
            </h3>
            <div className="boundary-list">
              <div className="boundary-item">
                <span>
                  <Icon name="calendar" size={15} />
                </span>
                <div>
                  <strong>Support participation</strong>
                  <p>
                    Review schedule, required adult activities, device readiness and absence or support
                    actions.
                  </p>
                </div>
              </div>
              <div className="boundary-item">
                <span>
                  <Icon name="wallet" size={15} />
                </span>
                <div>
                  <strong>Manage consent and billing</strong>
                  <p>
                    Complete only the consent and payment responsibilities assigned to this adult
                    relationship.
                  </p>
                </div>
              </div>
              <div className="boundary-item no">
                <span>
                  <Icon name="x" size={15} />
                </span>
                <div>
                  <strong>Do not complete learner work</strong>
                  <p>
                    Help the learner understand the task without taking over assessed work or pretending to be
                    the learner.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      );
    }
    const isInstitution = role.category === "institution";
    const entityName =
      state.application.entity.legalName || (isInstitution ? "Education institution" : "Organization");
    return (
      <>
        <Callout icon={isInstitution ? "school" : "building"} title={`Configure ${entityName}`}>
          Start with the verified owner and minimum viable structure. More administrators are invited by role
          and scope; nobody inherits every permission automatically.
        </Callout>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="users" size={15} />
            Initial people and scope
          </h3>
          <div className="checkbox-grid">
            {(isInstitution
              ? [
                  "Institution owner / principal administrator",
                  "Registrar / academic records",
                  "Dean / faculty leader",
                  "Department head / program leader",
                  "Quality & accreditation",
                  "Student success / accessibility",
                  "Finance / procurement",
                  "Information security / IT",
                ]
              : [
                  "Organization owner",
                  "Program manager",
                  "People administrator",
                  "Team manager",
                  "Governance lead",
                  "Finance administrator",
                  "Procurement officer",
                  "Technical administrator",
                ]
            ).map(item => (
              <CheckCard
                key={item}
                selected={state.onboarding.linkedLearners.includes(item)}
                onClick={() =>
                  toggleArray(state.onboarding.linkedLearners, item, next =>
                    updateOnboarding("linkedLearners", next),
                  )
                }
                title={item}
                detail="Invite after owner verification and scope approval."
              />
            ))}
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="building" size={15} />
            Workspace name
          </h3>
          <div className="form-grid">
            <div className="form-field full">
              <TextField
                label="Workspace display name"
                value={state.onboarding.workspaceName || entityName}
                onChange={value => updateOnboarding("workspaceName", value)}
                placeholder={entityName}
                icon={isInstitution ? "school" : "building"}
              />
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderOnboardingRoleSetup() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    const category = role.category;
    if (category === "learner")
      return (
        <>
          <Callout icon="graduation" title="Your learning journey is connected">
            Program learning, live Chatoner Class, eligible AI Labs, projects, Build Rooms and credential
            evidence will share the same learner and cohort context.
          </Callout>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="book" size={15} />
              Program setup
            </h3>
            <div className="review-grid">
              <section className="review-section">
                <div className="review-head">
                  <Icon name="book" size={14} />
                  <strong>Program</strong>
                </div>
                {reviewRow("Program", selectedProgram?.name)}
                {reviewRow("Track", selectedTrack?.label)}
                {reviewRow("Cohort", selectedCohort?.name)}
                {reviewRow("Commitment", selectedProgram?.commitment)}
              </section>
              <section className="review-section">
                <div className="review-head">
                  <Icon name="laptop" size={14} />
                  <strong>Practical outcome</strong>
                </div>
                {reviewRow("Project", selectedProgram?.project)}
                {reviewRow("Credential", selectedProgram?.credential)}
                {reviewRow("Language", selectedCohort?.language || state.application.applicant.language)}
                {reviewRow("Low bandwidth", state.application.lowBandwidth ? "Enabled" : "Standard")}
              </section>
            </div>
          </section>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="star" size={15} />
              Baseline diagnostic
            </h3>
            {Object.entries(state.onboarding.diagnostic).map(([key, value]) => (
              <div className="range-row" key={key}>
                <label>
                  {key.replace(/-/g, " ").replace(/\b\w/g, letter => letter.toUpperCase())}
                  <span>
                    This is used for support and personalization, not automatic admission or grading.
                  </span>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={value}
                    onChange={event =>
                      updateOnboarding("diagnostic", {
                        ...state.onboarding.diagnostic,
                        [key]: Number(event.target.value),
                      })
                    }
                  />
                </label>
                <output>{value}/5</output>
              </div>
            ))}
          </section>
        </>
      );
    if (category === "family")
      return (
        <>
          <Callout icon="heart" title="Family setup is action-oriented">
            The parent workspace highlights the next family action instead of exposing the learner’s full
            internal workspace.
          </Callout>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="calendar" size={15} />
              Family onboarding checklist
            </h3>
            <div className="boundary-list">
              <div className="boundary-item">
                <span>
                  <Icon name="video" size={15} />
                </span>
                <div>
                  <strong>Attend family orientation</strong>
                  <p>
                    Understand the selected age track, adult participation, communication and safeguarding.
                  </p>
                </div>
              </div>
              <div className="boundary-item">
                <span>
                  <Icon name="laptop" size={15} />
                </span>
                <div>
                  <strong>Check learner device and environment</strong>
                  <p>Use a family-safe setup, updated browser and age-appropriate account access.</p>
                </div>
              </div>
              <div className="boundary-item">
                <span>
                  <Icon name="message" size={15} />
                </span>
                <div>
                  <strong>Know where to ask for help</strong>
                  <p>
                    Use learning, technical, billing, accessibility or safeguarding routes according to the
                    issue.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      );
    if (category === "teaching")
      return (
        <>
          <Callout icon="badge" title="Instructional assignment is separate from account creation">
            The Academy assigns specific Programs, cohorts, groups and assessment authority after professional
            approval.
          </Callout>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="book" size={15} />
              Teaching assignments
            </h3>
            <div className="checkbox-grid">
              {PROGRAMS.map(program => (
                <CheckCard
                  key={program.id}
                  selected={state.onboarding.teachingPrograms.includes(program.id)}
                  onClick={() =>
                    toggleArray(state.onboarding.teachingPrograms, program.id, next =>
                      updateOnboarding("teachingPrograms", next),
                    )
                  }
                  title={program.name}
                  detail={
                    state.application.professional.programs.includes(program.id)
                      ? "Declared during application"
                      : "Requires separate assignment"
                  }
                />
              ))}
            </div>
          </section>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="clipboard" size={15} />
              Educator preparation
            </h3>
            <div className="boundary-list">
              <div className="boundary-item">
                <span>
                  <Icon name="shield" size={15} />
                </span>
                <div>
                  <strong>Safeguarding and accessibility orientation</strong>
                  <p>Complete required training before assigned learner contact.</p>
                </div>
              </div>
              <div className="boundary-item">
                <span>
                  <Icon name="badge" size={15} />
                </span>
                <div>
                  <strong>Assessment authority map</strong>
                  <p>
                    Know whether you facilitate, mentor, assess, moderate or lead; do not assume combined
                    authority.
                  </p>
                </div>
              </div>
              <div className="boundary-item">
                <span>
                  <Icon name="sparkles" size={15} />
                </span>
                <div>
                  <strong>AI-assisted teaching disclosure</strong>
                  <p>
                    Review generated materials, preserve original evidence and keep final academic decisions
                    human.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      );
    if (category === "assessment")
      return (
        <>
          <Callout icon="eye" title="Proctor assignment is session-specific">
            You receive only assigned assessments, candidates, windows, accommodations, incident routes and
            permitted controls.
          </Callout>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="clipboard" size={15} />
              Proctor readiness
            </h3>
            <div className="boundary-list">
              <div className="boundary-item">
                <span>
                  <Icon name="id" size={15} />
                </span>
                <div>
                  <strong>Identity and environment procedure</strong>
                  <p>Use the approved candidate-friendly steps and alternative verification route.</p>
                </div>
              </div>
              <div className="boundary-item">
                <span>
                  <Icon name="accessibility" size={15} />
                </span>
                <div>
                  <strong>Accommodation awareness</strong>
                  <p>
                    See only the operational adjustment needed for the assigned session, not unrelated
                    diagnoses.
                  </p>
                </div>
              </div>
              <div className="boundary-item">
                <span>
                  <Icon name="alert" size={15} />
                </span>
                <div>
                  <strong>Incident evidence</strong>
                  <p>Record facts, preserve timestamps and escalate; do not decide misconduct.</p>
                </div>
              </div>
            </div>
          </section>
        </>
      );
    if (category === "organization" || category === "institution")
      return (
        <>
          <Callout
            icon={category === "institution" ? "school" : "building"}
            title="Configure the first governed cohort"
          >
            The owner establishes structure, assigns administrators, confirms data boundaries and approves the
            first Program plan before inviting participants.
          </Callout>
          <section className="section-block">
            <h3 className="section-title">
              <Icon name="settings" size={15} />
              Initial configuration
            </h3>
            <div className="checkbox-grid">
              <ToggleRow
                label="Legal and commercial owner confirmed"
                detail="Named person can approve contract, plan, tenant and privileged role changes."
                value={Boolean(state.onboarding.policyChecks["owner-confirmed"])}
                onChange={value =>
                  updateOnboarding("policyChecks", {
                    ...state.onboarding.policyChecks,
                    "owner-confirmed": value,
                  })
                }
              />
              <ToggleRow
                label="Program and cohort owner named"
                detail="A responsible person coordinates participants, schedule, support and outcomes."
                value={Boolean(state.onboarding.policyChecks["program-owner"])}
                onChange={value =>
                  updateOnboarding("policyChecks", {
                    ...state.onboarding.policyChecks,
                    "program-owner": value,
                  })
                }
              />
              <ToggleRow
                label="Data and governance contact named"
                detail="Approves integrations, data scope, tools, retention and exceptions."
                value={Boolean(state.onboarding.policyChecks["governance-contact"])}
                onChange={value =>
                  updateOnboarding("policyChecks", {
                    ...state.onboarding.policyChecks,
                    "governance-contact": value,
                  })
                }
              />
              <ToggleRow
                label="Finance / procurement route named"
                detail="Invoices, purchase orders, contracts and renewals have responsible contacts."
                value={Boolean(state.onboarding.policyChecks["finance-contact"])}
                onChange={value =>
                  updateOnboarding("policyChecks", {
                    ...state.onboarding.policyChecks,
                    "finance-contact": value,
                  })
                }
              />
            </div>
          </section>
        </>
      );
    return (
      <>
        <Callout icon="shield" title="Academy operations setup">
          Access is restricted to the exact operational area, case, cohort or decision responsibility assigned
          by an authorized Academy administrator.
        </Callout>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="clipboard" size={15} />
            Operational preparation
          </h3>
          <div className="boundary-list">
            <div className="boundary-item">
              <span>
                <Icon name="users" size={15} />
              </span>
              <div>
                <strong>Segregation of duties</strong>
                <p>
                  No one person silently controls application review, payment, safeguarding, credential and
                  audit decisions.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="lock" size={15} />
              </span>
              <div>
                <strong>Protected cases</strong>
                <p>
                  Safeguarding, accommodations and integrity matters use separate minimum-necessary
                  workspaces.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="clipboard" size={15} />
              </span>
              <div>
                <strong>Decision record</strong>
                <p>
                  Record who decided, evidence considered, outcome, rationale, conditions and appeal path.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderOnboardingTools() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    const tools = [
      {
        id: "Academy",
        icon: "book" as IconName,
        title: "Chatoner AI Academy",
        detail: "Programs, progress, projects, evidence, credentials and role home.",
      },
      {
        id: "Class",
        icon: "video" as IconName,
        title: "Chatoner Class",
        detail: "Live classes, huddles, captions, interpretation, recordings and attendance.",
      },
      {
        id: "Labs",
        icon: "flask" as IconName,
        title: "Chatoner AI Labs",
        detail: "Eligible practical environments with controlled data, time, tools and evidence.",
      },
      {
        id: "Calendar",
        icon: "calendar" as IconName,
        title: "Calendar",
        detail: "Cohort sessions, reviews, office hours, deadlines and reminders.",
      },
      {
        id: "Messages",
        icon: "message" as IconName,
        title: "Academy Messages",
        detail: "Role-appropriate conversations and support without sharing personal contact details.",
      },
      {
        id: "Identity",
        icon: "key" as IconName,
        title: "Organization identity / SSO",
        detail: "Configured only for approved entities and permitted membership scopes.",
      },
    ];
    return (
      <>
        <Callout icon="link" title="Connected, but not merged into one unrestricted system">
          Academy carries signed identity, role, cohort, entitlement, language and return context into Class
          and Labs. Each destination still enforces its own permissions and safety rules.
        </Callout>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="link" size={15} />
            Connected tools and preferences
          </h3>
          <div className="workspace-grid">
            {tools.map(tool => {
              const disabled =
                tool.id === "Identity" && !["organization", "institution", "academy"].includes(role.category);
              const selected =
                state.onboarding.connectedApps.includes(tool.id) || ["Academy", "Class"].includes(tool.id);
              return (
                <button
                  type="button"
                  className={classNames("workspace-card", selected && "selected")}
                  key={tool.id}
                  disabled={disabled}
                  style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  onClick={() =>
                    !disabled &&
                    toggleArray(state.onboarding.connectedApps, tool.id, next =>
                      updateOnboarding("connectedApps", next),
                    )
                  }
                >
                  <div className="workspace-card-top">
                    <span className="workspace-card-icon">
                      <Icon name={tool.icon} size={20} />
                    </span>
                    <div>
                      <h3>{tool.title}</h3>
                      <p>{tool.detail}</p>
                    </div>
                    {selected ? (
                      <span className="selected-check">
                        <Icon name="check" size={13} />
                      </span>
                    ) : null}
                  </div>
                  <div className="workspace-meta">
                    <StatusChip tone={selected ? "green" : "default"}>
                      {selected ? "Enabled" : disabled ? "Not applicable" : "Optional"}
                    </StatusChip>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="shield" size={15} />
            Handoff rules
          </h3>
          <div className="boundary-list">
            <div className="boundary-item">
              <span>
                <Icon name="key" size={15} />
              </span>
              <div>
                <strong>Short-lived signed handoff</strong>
                <p>
                  Production sends a server-issued token, not a user-editable query string as the source of
                  authority.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="lock" size={15} />
              </span>
              <div>
                <strong>Minimum context</strong>
                <p>
                  Only learner, Program, cohort, role, language, accessibility and approved task context
                  needed by the destination.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="refresh" size={15} />
              </span>
              <div>
                <strong>Approved results return</strong>
                <p>
                  Attendance, selected Lab artifacts, evidence and status return through authorized APIs and
                  audit events.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderOnboardingPolicies() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    const policies = policySetForRole(role);
    return (
      <>
        <Callout icon="shield" title={`${role.label} boundaries must be understood`}>
          These acknowledgements are part of onboarding, not a substitute for policy enforcement. The backend
          must still prevent prohibited actions even when a user has clicked every box.
        </Callout>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="shield" size={15} />
            Required role boundaries
          </h3>
          <div className="checkbox-grid">
            {policies.map(policy => (
              <ToggleRow
                key={policy.key}
                label={policy.title}
                detail={policy.detail}
                value={Boolean(state.onboarding.policyChecks[policy.key])}
                required
                onChange={value =>
                  updateOnboarding("policyChecks", { ...state.onboarding.policyChecks, [policy.key]: value })
                }
              />
            ))}
          </div>
          {errors.policies ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.policies}
            </span>
          ) : null}
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="alert" size={15} />
            When to stop and escalate
          </h3>
          <div className="boundary-list">
            <div className="boundary-item">
              <span>
                <Icon name="heart" size={15} />
              </span>
              <div>
                <strong>Immediate safety concern</strong>
                <p>
                  Use the protected safeguarding route and emergency services where appropriate; do not
                  investigate through ordinary chat.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="badge" size={15} />
              </span>
              <div>
                <strong>Academic or integrity decision</strong>
                <p>
                  Preserve evidence and send it to the authorized reviewer, assessor, board or external
                  examiner.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="lock" size={15} />
              </span>
              <div>
                <strong>Access or privacy mismatch</strong>
                <p>
                  Stop using the information, record the issue and contact support, privacy, governance or
                  security.
                </p>
              </div>
            </div>
            <div className="boundary-item">
              <span>
                <Icon name="wallet" size={15} />
              </span>
              <div>
                <strong>Payment or authority dispute</strong>
                <p>
                  Do not change ownership, billing or sponsorship informally; route it to the responsible
                  human team.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderOnboardingReadiness() {
    return (
      <>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="laptop" size={15} />
            Device and connection check
          </h3>
          <div className="review-grid">
            <section className="review-section">
              <div className="review-head">
                <Icon name="laptop" size={14} />
                <strong>Browser and device</strong>
              </div>
              {reviewRow(
                "Secure context",
                typeof window !== "undefined" && window.isSecureContext ? "Available" : "Prototype / local",
              )}
              {reviewRow("Local storage", "Available for prototype")}
              {reviewRow("Keyboard access", "Required throughout")}
              {reviewRow("Screen reader", "Supported by semantic controls")}
            </section>
            <section className="review-section">
              <div className="review-head">
                <Icon name="globe" size={14} />
                <strong>Connection and media</strong>
              </div>
              {reviewRow(
                "Low bandwidth",
                state.application.lowBandwidth ? "Alternative route enabled" : "Standard route",
              )}
              {reviewRow(
                "Captions",
                state.application.accommodations.includes("Live captions")
                  ? "Requested"
                  : "Available in Class",
              )}
              {reviewRow("Time zone", state.application.applicant.timezone)}
              {reviewRow("Language", state.application.applicant.language)}
            </section>
          </div>
          <div className="checkbox-grid" style={{ marginTop: 11 }}>
            <ToggleRow
              label="Device and connection check complete"
              detail="I can open Academy, use keyboard navigation and know the low-bandwidth or support route if media fails."
              value={state.onboarding.deviceChecked}
              required
              onChange={value => updateOnboarding("deviceChecked", value)}
            />
          </div>
          {errors.readiness ? (
            <span className="field-error" style={{ marginTop: 9 }}>
              <Icon name="alert" size={12} />
              {errors.readiness}
            </span>
          ) : null}
        </section>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="message" size={15} />
            Support directory
          </h3>
          <div className="destination-grid">
            <div className="destination-card">
              <span>
                <Icon name="book" size={16} />
              </span>
              <strong>Learning support</strong>
              <p>Program content, assignments, feedback and mentor guidance.</p>
            </div>
            <div className="destination-card">
              <span>
                <Icon name="laptop" size={16} />
              </span>
              <strong>Technical support</strong>
              <p>Account, device, Class, Labs, browser and integration problems.</p>
            </div>
            <div className="destination-card">
              <span>
                <Icon name="accessibility" size={16} />
              </span>
              <strong>Accessibility</strong>
              <p>Accommodations, alternative formats and participation support.</p>
            </div>
            <div className="destination-card">
              <span>
                <Icon name="heart" size={16} />
              </span>
              <strong>Safeguarding</strong>
              <p>Protected reporting and urgent learner-safety concerns.</p>
            </div>
            <div className="destination-card">
              <span>
                <Icon name="wallet" size={16} />
              </span>
              <strong>Billing / sponsorship</strong>
              <p>Invoices, payments, sponsor confirmation and refunds.</p>
            </div>
            <div className="destination-card">
              <span>
                <Icon name="shield" size={16} />
              </span>
              <strong>Privacy / governance</strong>
              <p>Data rights, access mismatch, approved tools and policy questions.</p>
            </div>
          </div>
        </section>
        <Callout icon="info" title="Readiness can be revisited" tone="blue">
          Failing a device or media check should not lock a learner out. Production should offer
          troubleshooting, alternative participation and a named support route.
        </Callout>
      </>
    );
  }

  function renderOnboardingReady() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    const destination = onboardingDestination(role.id);
    const completed = onboardingSteps.filter(step =>
      state.onboarding.completedSteps.includes(step.id),
    ).length;
    return (
      <>
        <div className="welcome-banner">
          <span className="eyebrow">
            <Icon name="check" size={13} />
            Activation review
          </span>
          <h3>{destination.label} is ready to activate.</h3>
          <p>
            Confirm the role, destination and next actions. Activation creates the workspace membership
            defined by the server; it does not copy every permission from another role or entity.
          </p>
        </div>
        <div className="review-grid" style={{ marginTop: 12 }}>
          <section className="review-section">
            <div className="review-head">
              <Icon name="badge" size={14} />
              <strong>Access</strong>
            </div>
            {reviewRow("Role", role.label)}
            {reviewRow("Destination", destination.label)}
            {reviewRow("Route", destination.path)}
            {reviewRow("Security", role.mfa === "required" ? "MFA required" : "MFA available")}
          </section>
          <section className="review-section">
            <div className="review-head">
              <Icon name="clipboard" size={14} />
              <strong>Onboarding</strong>
            </div>
            {reviewRow("Completed", `${completed}/${onboardingSteps.length} steps`)}
            {reviewRow("Profile", state.onboarding.profileConfirmed ? "Confirmed" : "Pending")}
            {reviewRow("Device", state.onboarding.deviceChecked ? "Checked" : "Pending")}
            {reviewRow(
              "Policies",
              policySetForRole(role).every(item => state.onboarding.policyChecks[item.key])
                ? "Acknowledged"
                : "Pending",
            )}
          </section>
        </div>
        <section className="section-block">
          <h3 className="section-title">
            <Icon name="arrow-right" size={15} />
            First actions after activation
          </h3>
          <div className="boundary-list">
            <div className="boundary-item">
              <span>
                <Icon name={destination.icon} size={15} />
              </span>
              <div>
                <strong>Open {destination.label}</strong>
                <p>
                  See the role-specific home, next action, assignments, deadlines, support and accessible
                  navigation.
                </p>
              </div>
            </div>
            {selectedCohort ? (
              <div className="boundary-item">
                <span>
                  <Icon name="calendar" size={15} />
                </span>
                <div>
                  <strong>Confirm {selectedCohort.name}</strong>
                  <p>
                    {selectedCohort.schedule} · {selectedCohort.timezone} · starts{" "}
                    {formatDate(selectedCohort.startDate)}.
                  </p>
                </div>
              </div>
            ) : null}
            <div className="boundary-item">
              <span>
                <Icon name="message" size={15} />
              </span>
              <div>
                <strong>Know your help route</strong>
                <p>
                  Use the role-appropriate learning, technical, accessibility, safeguarding, billing or
                  governance channel.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  function renderOnboardingStepBody(step: OnboardingStepId) {
    if (step === "welcome") return renderOnboardingWelcome();
    if (step === "security") return renderOnboardingSecurity();
    if (step === "profile") return renderOnboardingProfile();
    if (step === "relationships") return renderOnboardingRelationships();
    if (step === "role-setup") return renderOnboardingRoleSetup();
    if (step === "tools") return renderOnboardingTools();
    if (step === "policies") return renderOnboardingPolicies();
    if (step === "readiness") return renderOnboardingReadiness();
    return renderOnboardingReady();
  }

  function renderOnboarding() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    const progress = Math.round(((state.onboardingStep + 1) / Math.max(1, onboardingSteps.length)) * 100);
    const activeStepIsValid = Object.keys(validateOnboardingStep(activeOnboardingStep.id)).length === 0;
    const onboardingIsReady = onboardingSteps.every(
      step => Object.keys(validateOnboardingStep(step.id)).length === 0,
    );
    return (
      <div className="page">
        <PageHead
          icon="sparkles"
          eyebrow={`${role.label} onboarding`}
          title={`Prepare ${role.workspace}.`}
          description="Security, preferences, people, tools and boundaries are configured around the verified role before activation."
          action={
            <StatusChip tone="green">
              <Icon name="shield" size={11} />
              {role.mfa === "required" ? "MFA role" : "Verified role"}
            </StatusChip>
          }
        />
        <div className="onboarding-layout">
          <aside className={classNames("step-sidebar", mobileStepsOpen && "mobile-open")}>
            <div className="step-summary">
              <div>
                <span>Onboarding progress</span>
                <strong>
                  {progress}% · {state.onboardingStep + 1} of {onboardingSteps.length}
                </strong>
              </div>
              <button
                type="button"
                className="btn ghost small mobile-step-toggle mobile-only"
                onClick={() => setMobileStepsOpen(current => !current)}
              >
                <Icon name="menu" size={13} />
                {mobileStepsOpen ? "Hide" : "Show"} steps
              </button>
              <div className="step-progress">
                <i style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="step-list">
              {onboardingSteps.map((step, index) => {
                const done = state.onboarding.completedSteps.includes(step.id);
                const unlocked =
                  index <= state.onboardingStep ||
                  onboardingSteps
                    .slice(0, index)
                    .every(previous => state.onboarding.completedSteps.includes(previous.id));
                return (
                  <button
                    type="button"
                    disabled={!unlocked}
                    className={classNames(
                      "step-link",
                      index === state.onboardingStep && "active",
                      done && "done",
                      !unlocked && "locked",
                    )}
                    key={step.id}
                    onClick={() => {
                      if (!unlocked) return;
                      setState(current => ({ ...current, onboardingStep: index }));
                      setMobileStepsOpen(false);
                    }}
                  >
                    <span className="step-index">{done ? <Icon name="check" size={12} /> : index + 1}</span>
                    <span>
                      <strong>{step.label}</strong>
                      <small>{step.description}</small>
                    </span>
                    <Icon name="chevron" size={13} />
                  </button>
                );
              })}
            </div>
          </aside>
          <main className="application-main">
            <section className="step-card">
              <div className="step-card-head">
                <span className="step-card-icon">
                  <Icon name={activeOnboardingStep.icon} size={20} />
                </span>
                <div>
                  <h3>{activeOnboardingStep.label}</h3>
                  <p>{activeOnboardingStep.description}</p>
                </div>
                <span className="autosave">
                  <Icon name="save" size={12} />
                  Progress saved
                </span>
              </div>
              <div className="step-card-body">{renderOnboardingStepBody(activeOnboardingStep.id)}</div>
              <div className="step-card-actions">
                <Button disabled={state.onboardingStep === 0} onClick={previousOnboardingStep}>
                  <Icon name="arrow-left" size={15} />
                  Back
                </Button>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {activeOnboardingStep.id === "ready" ? (
                    <Button tone="orange" disabled={!onboardingIsReady} onClick={completeOnboarding}>
                      Activate workspace
                      <Icon name="arrow-right" size={15} />
                    </Button>
                  ) : (
                    <Button tone="primary" disabled={!activeStepIsValid} onClick={nextOnboardingStep}>
                      Save and continue
                      <Icon name="arrow-right" size={15} />
                    </Button>
                  )}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    );
  }

  function renderComplete() {
    const role = roleById(state.onboarding.roleId || state.application.roleId);
    const destination = onboardingDestination(role.id);
    return (
      <div className="page">
        <div className="completion-card">
          <span className="completion-icon">
            <Icon name="check" size={34} />
          </span>
          <span className="eyebrow">
            <Icon name="sparkles" size={13} />
            Onboarding complete
          </span>
          <h2>{destination.label} is activated.</h2>
          <p>
            {state.authName || state.application.applicant.firstName || "Your account"} can now continue with
            the verified {role.label} membership. The workspace remains bounded by tenant, cohort, learner
            relationship, assignment, expiry and policy.
          </p>
          <div className="destination-grid">
            <div className="destination-card">
              <span>
                <Icon name={destination.icon} size={17} />
              </span>
              <strong>{destination.label}</strong>
              <p>Role home and next action · {destination.path}</p>
            </div>
            <div className="destination-card">
              <span>
                <Icon name="book" size={17} />
              </span>
              <strong>{selectedProgram?.name || "Role-based Academy access"}</strong>
              <p>{selectedCohort?.name || "Assignments appear when authorized"}</p>
            </div>
            <div className="destination-card">
              <span>
                <Icon name="shield" size={17} />
              </span>
              <strong>Security and boundaries</strong>
              <p>
                {role.mfa === "required" ? "MFA enforced" : "MFA available"} · activity attributable · human
                authority retained
              </p>
            </div>
          </div>
          <div className="button-row" style={{ justifyContent: "center", marginTop: 20 }}>
            <Button
              tone="orange"
              onClick={() => openAssignedWorkspace(role, { name: state.authName, email: state.authEmail })}
            >
              <Icon name="arrow-right" size={15} />
              Open {destination.label}
            </Button>
            <Button onClick={() => setScreen("workspaces")}>
              <Icon name="building" size={15} />
              Switch workspace
            </Button>
            <Button onClick={exportApplication}>
              <Icon name="download" size={15} />
              Export application
            </Button>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <Callout icon="shield" title="Ready for your workspace">
            Open the assigned workspace to continue. Use Switch workspace only when another verified role is
            already available on this account.
          </Callout>
        </div>
      </div>
    );
  }

  function renderModalLayer() {
    if (!modal) return null;
    if (modal === "role-guide")
      return (
        <Modal
          title={
            authorizationContext ? `${authorizationContext.label} role guide` : "Complete Academy role guide"
          }
          subtitle={
            authorizationContext
              ? "Only application and invitation roles valid for this requesting app are shown."
              : "Roles are assigned independently and can coexist on one verified account."
          }
          icon="users"
          onClose={() => setModal(null)}
          actions={
            <Button tone="primary" onClick={() => setModal(null)}>
              Close guide
            </Button>
          }
        >
          <div className="role-tabs">
            {(authorizationContext
              ? ROLE_GROUPS.filter(group => group.id === authorizationContext.roleCategory)
              : ROLE_GROUPS
            ).map(group => (
              <button
                type="button"
                className={classNames("role-tab", roleFilter === group.id && "active")}
                key={group.id}
                onClick={() => setRoleFilter(group.id)}
              >
                {group.label}
              </button>
            ))}
          </div>
          <p className="section-copy">{ROLE_GROUPS.find(group => group.id === roleFilter)?.description}</p>
          <div className="role-list">
            {ROLES.filter(
              role =>
                role.category === roleFilter &&
                (!authorizationContext ||
                  [
                    ...authorizationContext.applicationRoleIds,
                    ...authorizationContext.invitationRoleIds,
                  ].includes(role.id)),
            ).map(role => (
              <div
                className={classNames("role-card", state.application.roleId === role.id && "selected")}
                key={role.id}
              >
                <span className="role-card-icon">
                  <Icon
                    name={
                      role.category === "family"
                        ? "heart"
                        : role.category === "teaching"
                          ? "badge"
                          : role.category === "assessment"
                            ? "eye"
                            : role.category === "organization"
                              ? "building"
                              : role.category === "institution"
                                ? "school"
                                : role.category === "academy"
                                  ? "shield"
                                  : "graduation"
                    }
                    size={16}
                  />
                </span>
                <span>
                  <strong>{role.label}</strong>
                  <p>{role.description}</p>
                </span>
                <span className={classNames("access-tag", role.access)}>{role.access}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Callout icon="key" title="Invitation-only means scoped assignment">
              Invitation roles must come from an authorized Academy, organization, institution or verified
              family relationship. The invitation carries tenant, learner, unit, cohort, expiry and permission
              scope.
            </Callout>
          </div>
        </Modal>
      );
    if (modal === "application-export")
      return (
        <Modal
          title="Export application record"
          subtitle="Download a portable prototype copy without authentication secrets."
          icon="download"
          onClose={() => setModal(null)}
          actions={
            <>
              <Button onClick={() => setModal(null)}>Cancel</Button>
              <Button
                tone="primary"
                onClick={() => {
                  exportApplication();
                  setModal(null);
                }}
              >
                Download JSON
              </Button>
            </>
          }
        >
          <Callout icon="shield" title="Safe export">
            The export contains the application and configured role context. It excludes passwords, email
            verification codes, MFA secrets, payment credentials and uploaded document bytes.
          </Callout>
          <div className="review-grid" style={{ marginTop: 12 }}>
            <section className="review-section">
              <div className="review-head">
                <Icon name="clipboard" size={14} />
                <strong>Record</strong>
              </div>
              {reviewRow("Reference", state.application.reference)}
              {reviewRow("Version", APPLICATION_VERSION)}
              {reviewRow("Status", state.application.status)}
              {reviewRow("Last saved", new Date(state.application.lastSavedAt).toLocaleString())}
            </section>
            <section className="review-section">
              <div className="review-head">
                <Icon name="lock" size={14} />
                <strong>Excluded secrets</strong>
              </div>
              {reviewRow("Password", "Never exported")}
              {reviewRow("MFA secret", "Never exported")}
              {reviewRow("Payment credentials", "Never exported")}
              {reviewRow("Document bytes", "Never exported")}
            </section>
          </div>
        </Modal>
      );
    return (
      <Modal
        title="Resume saved progress"
        subtitle="A locally saved Academy entry journey is available."
        icon="save"
        onClose={() => setModal(null)}
        actions={
          <>
            <Button onClick={resetDemo}>Start over</Button>
            <Button
              tone="primary"
              onClick={() => {
                setModal(null);
                beginAccountJourney();
              }}
            >
              Resume
            </Button>
          </>
        }
      >
        <Callout icon="clipboard" title={state.application.reference}>
          Status: {state.application.status}. Completed application sections:{" "}
          {state.application.completedSteps.length}. Onboarding sections:{" "}
          {state.onboarding.completedSteps.length}.
        </Callout>
      </Modal>
    );
  }

  const visibleScreen = visibleAccessScreen(state);
  const lastScreen = useRef<Screen | null>(null);
  const historyNavigation = useRef(false);
  const [historyRevision, setHistoryRevision] = useState(0);

  // Every screen transition (including form submissions) updates the URL here.
  // Initial entries and Back/Forward canonicalize without adding history entries.
  useLayoutEffect(() => {
    const mode = lastScreen.current === null || historyNavigation.current ? "replace" : "push";
    updateAccessPath(visibleScreen, mode);
    lastScreen.current = visibleScreen;
    historyNavigation.current = false;
  }, [visibleScreen, historyRevision]);

  useEffect(() => {
    const onPopState = () => {
      historyNavigation.current = true;
      setModal(null);
      setState(current => stateForAuthorizationContext(stateForAccessEntry(current), authorizationContext));
      setHistoryRevision(current => current + 1);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [authorizationContext]);

  const useAcademyDesign =
    visibleScreen === "welcome" ||
    visibleScreen === "signup" ||
    ((!authorizationContext || ["learner-self", "parent-child"].includes(authorizationContext.route)) &&
      ["", "learner-self", "parent-child"].includes(state.application.route));

  let content: React.ReactNode;
  if (visibleScreen === "welcome") content = renderWelcome();
  else if (visibleScreen === "signin") content = renderSignIn();
  else if (visibleScreen === "signup") content = renderSignup();
  else if (visibleScreen === "verify-email") content = renderVerifyEmail();
  else if (visibleScreen === "mfa") content = renderMfa();
  else if (visibleScreen === "forgot-password") content = renderForgotPassword();
  else if (visibleScreen === "reset-password") content = renderResetPassword();
  else if (visibleScreen === "invite") content = renderInvite();
  else if (visibleScreen === "workspaces") content = renderWorkspaces();
  else if (visibleScreen === "application") content = renderApplication();
  else if (visibleScreen === "status") content = renderStatus();
  else if (visibleScreen === "offer") content = renderOffer();
  else if (visibleScreen === "checkout") content = renderCheckout();
  else if (visibleScreen === "onboarding") content = renderOnboarding();
  else content = renderComplete();

  return (
    <>
      <div
        className={classNames(
          "access-shell",
          useAcademyDesign && "academy-redesign",
          visibleScreen === "welcome" && "gateway-mode",
          visibleScreen === "signup" && "signup-mode",
          authorizationContext && "has-request-context",
        )}
      >
        <style>{redesignStyles + gatewayStyles}</style>
        <AccessHeader
          context={authorizationContext}
          onHome={() => setScreen(authorizationContext ? "signin" : "welcome")}
          showSignIn={visibleScreen !== "signin" && visibleScreen !== "welcome"}
          onSignIn={() => setScreen("signin")}
          authenticated={state.authenticated}
          onSignOut={signOut}
          minimal={visibleScreen === "signup"}
          onHelp={() => openWorkspaceHelp()}
        />
        <main className="main-scroll access-main" ref={scrollRef}>
          {authorizationContext ? (
            <section className="request-context" aria-label={`${authorizationContext.label} access request`}>
              <span className="request-context-icon">
                <Icon name={authorizationContext.icon} size={20} />
              </span>
              <span className="request-context-copy">
                <small>Access request</small>
                <strong>{authorizationContext.label}</strong>
                <span>{authorizationContext.description}</span>
              </span>
              <span className="request-context-status">
                <Icon name="lock" size={13} />
                {authorizationRequest ? "Verified return" : "App-specific entry"}
              </span>
            </section>
          ) : null}
          {content}
        </main>
      </div>
      {renderModalLayer()}
      <ToastStack messages={toasts} dismiss={dismissToast} />
    </>
  );
}
